import './styles.css';
import './menu.css';
import './mobile.css';
import './performance-test.css';
import { CARS, CAR_BY_ID, cloneTuning } from '../shared/cars.js';
import { roadSectionAtProgress, roadVisualProfile } from '../shared/map-identity.js';
import { LOCAL_CHECKPOINT_INTERVAL_MS } from './local-race-protocol.js';
import { SOCKET_CLOSE_BANNED, SOCKET_CLOSE_KICKED } from '../shared/protocol.js';
import { getTrackGeometry, sampleBakedRoad, trackPoseAtIndex } from '../shared/track.js';
import type { CreateRoomRequest, ResumeState, ServerMessage } from '../shared/protocol.js';
import type {
  MapDefinition,
  NpcDifficulty,
  PlayerIdentity,
  PublicLobbySummary,
  PublicRoom,
  RaceEventMode,
  RaceSettings,
  RacePhase,
  RaceSnapshot,
  RacerSnapshot,
  VisualTuning,
} from '../shared/types.js';
import {
  canQuickRestart,
  eventModeLabel,
  eventResultLabel,
  raceEventMode,
  raceLapCount,
} from '../shared/race-events.js';
import { vehicleSignatureFor } from '../shared/vehicle-signatures.js';
import {
  raceWagerDefinition,
  type DailyChallenge,
} from '../shared/gameplay-fun.js';
import { CREW_MODULES } from '../shared/crew-modules.js';
import {
  ARCADE_PRESETS,
  arcadeCareerEligible,
  normaliseArcadePreset,
} from '../shared/arcade-presets.js';
import { liveChallengeDefinition } from '../shared/live-challenges.js';
import {
  ApiError,
  isUnavailableRaceError,
  ApiTimeoutError,
  ProtocolVersionError,
  createRoom,
  getDailyChallenge,
  getMaps,
  getPublicLobbies,
  getRoom,
  joinRoom,
  loadCachedDailyChallenge,
  loadCachedMaps,
} from './api.js';
import { DailyChallengeRefreshScheduler } from './daily-refresh.js';
import { detectDeviceCapabilities, detectMotionRuntime, motionIndicatorOffsetPx } from './device.js';
import { GarageController, type GarageAction } from './garage-controller.js';
import { ResultsCareerController } from './results-career-controller.js';
import { ResultsPresentation } from './ui/results-presentation.js';
import { formatEventClock, formatGap, formatLapTime, formatSectorTime, heatRunDisplayScore, medalLabel, ordinal, standingsWindow } from './ui/race-format.js';
import { SpectatorController } from './spectator-controller.js';
import { drawMapOutline } from './map-outline.js';
import { browserDeviceId } from './device-id.js';
import { applyLobbySetup, clearAllLocalData, loadLobbySetup, saveLobbySetup } from './lobby-preferences.js';
import { bindDriverStep, driverStepHtml, preferredSteeringDevice, type DriverStepModel, type SteeringDevice } from './menu/driver-step.js';
import { garageNamePlateHtml, garageStepHtml } from './menu/garage-step.js';
import { renderStartLights } from './ui/start-lights.js';
import { LobbyPanel, lobbyLaunchState, type LobbyMember, type LobbyRival, type LobbyViewModel } from './menu/lobby-panel.js';
import { menuFrameHtml, wizardFooterHtml, wizardRailHtml, type MenuStep } from './menu/menu-frame.js';
import { bindLanguageStep, languageStepCopy, languageStepHtml } from './menu/language-step.js';
import { relocaliseTree } from './i18n/relocalise.js';
import { lobbyBrowserHtml, parseInviteInput, playStepHtml, type PlayStepModel } from './menu/play-step.js';
import { applyLobbyPatch, dominantNpcDifficulty, MAX_LOBBY_NPCS, npcRosterOf, type LobbySettingsPatch } from '../shared/lobby.js';
import { npcGridIdentity } from '../shared/npc-grid.js';
import { localRaceSettings } from './local-race-protocol.js';
import { escapeHtml, safeColour } from './ui/html.js';
import { connectionQuality, displayPing, signalBarsHtml } from './ui/connection-quality.js';
import { raceShell, spectatorShell } from './ui/shells.js';
import { LIVERY_PRESETS, applyLivery, shuffledTuning, stanceText, tuningPanelHtml } from './ui/tuning-panel.js';
import type { GaragePreview } from './game/garage-preview.js';
import { defaultGamepadMapping, type GamepadStatus } from './game/gamepad.js';
import { ControlsSettingsPanel } from './controls-settings.js';
import { GamepadUi } from './gamepad-ui.js';
import { formatNumber, hasChosenLocale, locale, setLocale, t, tc, tn, type Locale } from './i18n/index.js';
import { renderInputGlyphs } from './input-glyphs.js';
import { onKeyboardBindingsChange } from './keyboard-bindings.js';
import type { GhostStatus } from './game/lap-ghost-store.js';
import {
  ghostMatchesRaceSetup,
  lapGhostRecordingEligible,
  loadLapGhost,
  type LapGhostSetup,
} from './game/lap-ghost-store.js';
import { rememberPhysicsTelemetryQuery } from './game/physics-telemetry.js';
import { RaceDirector, type RaceDirectorCue } from './game/race-director.js';
import type {
  CameraModeLabel,
  MotionSensorReading,
  MotionSteeringStatus,
  RaceGame,
  RendererRecoveryStatus,
} from './game/race-game.js';
import {
  RaceConnection,
  SpectatorConnection,
  type ConnectionStatus,
  type RaceConnectionOptions,
  type RaceTransport,
} from './network.js';
import type { PerformanceStatus } from './performance.js';
import type { LocalRaceSession } from './local-race.js';
import {
  STREET_TOURS,
  currentNightTourEvent,
  loadNightTour,
  nightTourDefinition,
  nightTourCompletions,
  nightTourEvents,
  nightTourStateForStart,
  selectNightTour,
  type NightTourState,
} from './night-tour.js';
import { isStreetTourId, type StreetTourId } from '../shared/night-tour-rivals.js';
import { quickEventFor } from './event-playlist.js';
import {
  clearStoredGhostChallenge,
  createGhostChallengeUrl,
  decodeGhostChallenge,
  GHOST_CHALLENGE_RACE_RULES,
  loadStoredGhostChallenge,
  reconcileGhostChallengeCatalog,
  storeGhostChallenge,
  type GhostChallenge,
} from './ghost-challenge.js';
import { TrailingUiScheduler } from './ui-update-scheduler.js';
import { UiSounds } from './ui-sounds.js';
import { audioHub } from './audio/audio-hub.js';
import { installDevTouchEmulation } from './dev-touch-emulation.js';
import { motionAccess, motionAccessGranted, requestMotionAccess } from './motion-preview.js';
import { COMPACT_CUE_DURATION_MS, CompactCueGate, compactStyleCalloutAllowed, toastDecision, ToastRepeatGate, type ToastOptions } from './mobile-hud-policy.js';
import { AudioVuMeter, audioChannelSettingKey, syncAudioMixers, type AudioMixChannel } from './ui/audio-mixer.js';
import {
  deleteOfflineRaceCheckpoint,
  loadOfflineRaceCheckpoint,
  type StoredOfflineRace,
} from './offline-race-store.js';
import { OfflineCheckpointPublisher } from './offline-checkpoint-publisher.js';
import {
  benchmarkHeapBytes,
  MobileBenchmarkRecorder,
  parseMobileBenchmarkRequest,
  type MobileBenchmarkRequest,
  type MobileBenchmarkResult,
} from './mobile-benchmark.js';
import {
  CAREER_REWARDS,
  CHALLENGES,
  activeRotatingContracts,
  adaptiveNpcDifficulty,
  createRaceRunStats,
  currentNemesis,
  loadProgression,
  nemesisPaintUnlocked,
  nextCareerReward,
  observeRaceRun,
  progressionLevel,
  progressionMeter,
  rankTitle,
  unlockedReward,
  type ProgressionState,
  type ProgressionUpdate,
  type RaceRunStats,
} from './progression.js';
import {
  loadVehicleMastery,
  masteryPaintForCar,
  masteryPaintUnlocked,
  vehicleMasteryGoals,
  vehicleMasteryRecord,
  type VehicleMasteryState,
  type VehicleMasteryUpdate,
} from './vehicle-mastery.js';
import {
  dailyChallengeRecord,
  loadDailyChallengeState,
  type DailyChallengeState,
} from './daily-challenge.js';
import {
  commitLocalCareerResultWithRetry,
  mutateLocalNightTour,
} from './local-career-commit.js';
import {
  createDefaultDriverProfile,
  forgetRaceSession,
  loadDriverProfile,
  loadRaceSession,
  loadRecentRaceSession,
  replaceRaceSession,
  resumeWindowFromSessionTtl,
  saveDriverProfile,
  saveRaceSession,
  updateRaceSession,
  type DriverProfile,
  type StoredRaceSession,
} from './profile.js';
import { PwaController, racePhaseHoldsUpdateLease } from './pwa.js';
import { resolveRaceAttempt } from './race-attempt.js';
import { RaceRuntimeController } from './race-runtime-controller.js';
import { RaceDiagnostics } from './race-diagnostics.js';
import { createDiagnosticReport, downloadDiagnosticReport } from './diagnostic-download.js';
import { RouteController, type RaceRoute } from './route-controller.js';
import { resolveRuntimeRaceMap } from './runtime-race-map.js';
import {
  defaultGameSettings,
  graphicsDefaults,
  loadGameSettings,
  saveGameSettings,
  applyCalibrationToSettings,
  resolveGraphicsProfile,
  shouldStartWithConservativeFrameRate,
  type GameSettings,
  type GraphicsQuality,
} from './settings.js';
import { cachedCalibration, loadCalibration, saveCalibration } from './graphics-calibration.js';
import { openPerformanceTest, performanceTestOpen } from './ui/performance-test.js';
import { graphicsQuickHtml, graphicsTestCardHtml } from './ui/graphics-panel.js';

const RACING_DEBUG_SESSION_KEY = 'nightshift:racing-line-debug';
const OFFLINE_AUTO_LAUNCH_MS = 700;
rememberRacingDebugQuery();
rememberPhysicsTelemetryQuery();

function rememberRacingDebugQuery(): void {
  const params = new URLSearchParams(window.location.search);
  const enabled = params.get('aiDebug') === '1' || params.get('racingLine') === '1';
  const disabled = params.get('aiDebug') === '0' || params.get('racingLine') === '0';
  try {
    if (enabled) window.sessionStorage.setItem(RACING_DEBUG_SESSION_KEY, '1');
    else if (disabled) window.sessionStorage.removeItem(RACING_DEBUG_SESSION_KEY);
  } catch {
    // Debug state is optional; storage restrictions must never block startup.
  }
}

interface RaceUiElements {
  raceStage: HTMLElement;
  speedValue: HTMLElement;
  powertrain: HTMLElement;
  gearValue: HTMLElement;
  rpmValue: HTMLElement;
  rpmFill: HTMLElement;
  cluster: HTMLElement | null;
  tachoFill: SVGElement | null;
  tachoNitro: SVGElement | null;
  integrity: HTMLElement;
  integrityValue: HTMLElement;
  integrityFill: HTMLElement;
  integrityLoss: HTMLElement;
  integrityHit: HTMLElement;
  damageMap: SVGSVGElement;
  damageVignette: HTMLElement;
  nitroFill: HTMLElement;
  nitroValue: HTMLElement;
  draftHud: HTMLElement;
  draftFill: HTMLElement;
  draftValue: HTMLElement;
  launchHud: HTMLElement;
  launchNeedle: HTMLElement;
  launchValue: HTMLElement;
  nitroButton: HTMLElement | null;
  driftButton: HTMLElement | null;
  styleHud: HTMLElement;
  styleScore: HTMLElement;
  styleAction: HTMLElement;
  styleMultiplier: HTMLElement;
  styleComboFill: HTMLElement;
  styleCallout: HTMLElement;
  directorCallout: HTMLElement;
  gripHud: HTMLElement;
  gripFill: HTMLElement;
  gripSteer: HTMLElement;
  gripValue: HTMLElement;
  lapValue: HTMLElement;
  placeValue: HTMLElement;
  positionTotal: HTMLElement;
  eventMode: HTMLElement;
  leaderboard: HTMLOListElement;
  minimap: HTMLCanvasElement;
  lobby: HTMLElement;
  hud: HTMLElement;
  countdown: HTMLElement;
  results: HTMLElement;
  ghostHud: HTMLElement;
  ghostBest: HTMLElement;
  ghostDelta: HTMLElement;
  ghostSectors: HTMLElement;
  ghostMedal: HTMLElement;
  liveChallengeHud: HTMLElement;
  liveChallengeTitle: HTMLElement;
  liveChallengeDetail: HTMLElement;
  liveChallengeFill: HTMLElement;
  liveChallengeTime: HTMLElement;
  sectorHud: HTMLElement;
  sectorDelta: HTMLElement;
  sectorCells: [HTMLElement, HTMLElement, HTMLElement];
  comebackHud: HTMLElement;
  comebackFill: HTMLElement;
  comebackValue: HTMLElement;
  heatHud: HTMLElement;
  heatLevel: HTMLElement;
  heatUnits: HTMLElement;
}

interface OfflineLobbyState {
  settings: RaceSettings;
  banner: { kicker: string; title: string } | null;
  /** Daily challenges race one published car. */
  forcedCarId: string | null;
  /** NIGHT TOUR progress that becomes current once the stage launches. */
  pendingTour: NightTourState | null;
  /** Ghost and daily setups are fixed so their records stay comparable. */
  locked: boolean;
  notice: string;
}

/** Primary actions get a brighter two-tone confirm instead of a plain click. */
const UI_CONFIRM_BUTTON_IDS = new Set([
  'results-lobby',
  'results-restart',
  'results-next',
  'motion-permission-enable',
]);

class NightshiftApp {
  private maps: MapDefinition[] = [];
  private driverProfile: DriverProfile = loadDriverProfile();
  private recentRace: StoredRaceSession | null = null;
  private offlineRace: StoredOfflineRace | null = null;
  private offlineRaceLoadGeneration = 0;
  private readonly offlineCheckpointPublisher: OfflineCheckpointPublisher;
  private readonly garageController: GarageController;
  private get selectedMapId(): string {
    return this.garageController.state.selectedMapId;
  }
  private set selectedMapId(mapId: string) {
    this.garageController.dispatch({ type: 'select-map', mapId });
  }
  private get selectedCarId(): string {
    return this.garageController.state.selectedCarId;
  }
  private set selectedCarId(carId: string) {
    this.garageController.dispatch({ type: 'select-car', carId });
  }
  private tuning = new Map(CARS.map((car) => [car.id, cloneTuning(car.defaultTuning)]));
  private garage: GaragePreview | null = null;
  private readonly diagnostics = new RaceDiagnostics();
  private readonly diagnosticsEnabled = new URLSearchParams(window.location.search).get('diagnostics') === '1';
  private readonly raceRuntime = new RaceRuntimeController<RaceTransport, RaceGame>(this.diagnostics);
  private readonly routes = new RouteController(window);
  private get garageActions() {
    return this.garageController.actions;
  }
  /** Route authenticated by the current transport; it may be a predecessor
   * while an HTTP lookup has already resolved this.room to its successor. */
  private connectionRoomId = '';
  private room: PublicRoom | null = null;
  private player: PlayerIdentity | null = null;
  private inviteToken = '';
  private isHost = false;
  private latestSnapshot: RaceSnapshot | null = null;
  private readonly compactCueGate = new CompactCueGate();
  private readonly toastRepeatGate = new ToastRepeatGate();
  private inputTimer: number | undefined;
  private inputSequence = 0;
  private lastPhase = '';
  private lastLocalFinished = false;
  private goTimer: number | undefined;
  private readonly resultsCareer: ResultsCareerController;
  private readonly resultsPresentation: ResultsPresentation;
  private directorUiTimer: number | undefined;
  private connectionState: ConnectionStatus = 'connecting';
  private latencyMs: number | null = null;
  /** Other drivers' self-reported round trips, from the room's latency fan-out. */
  private readonly memberPings = new Map<string, number>();
  private readonly deviceCapabilities = detectDeviceCapabilities();
  private gameSettings: GameSettings = loadGameSettings(this.deviceCapabilities);
  private settingsOpen = false;
  private inviteShareOpen = false;
  private inviteShareRenderId = 0;
  private inviteShareReturnFocus: HTMLElement | null = null;
  private inviteQrAbort: AbortController | null = null;
  private motionStatus: MotionSteeringStatus = 'off';
  private lastRaceSaveAt = 0;
  private lastPersistedRacePhase: RacePhase | null = null;
  private racePersistenceRetryAt = 0;
  private pendingRaceSessionPredecessorId = '';
  private lastStyleEventId = -1;
  /** The grip row lingers after a slide so it does not flicker in and out. */
  private gripHudVisibleUntil = 0;
  private progression: ProgressionState = loadProgression();
  private vehicleMastery: VehicleMasteryState = loadVehicleMastery();
  private vehicleMasteryUpdate: VehicleMasteryUpdate | null = null;
  private dailyChallenges: DailyChallengeState = loadDailyChallengeState();
  private publishedDailyChallenge: DailyChallenge | null = null;
  private readonly dailyRefreshScheduler: DailyChallengeRefreshScheduler;
  private dailyRefreshPromise: Promise<boolean> | null = null;
  private nightTour: NightTourState = loadNightTour();
  private importedGhostChallenge: GhostChallenge | null = null;
  private startupNotice: { message: string; type: 'info' | 'error' } | null = null;
  private shareLinkOverride: { link: string; title: string; text: string; label: string } | null = null;
  private raceRun: RaceRunStats = createRaceRunStats();
  private progressionUpdate: ProgressionUpdate | null = null;
  private careerBankPromise: Promise<void> | null = null;
  private careerCommitGeneration = 0;
  private readonly pwa: PwaController;
  private runtimeGeneration = 0;
  private historyNavigationGeneration = 0;
  private garageMetadataReady = false;
  private mountSequence = 0;
  private garageGeneration = 0;
  private quickEventSequence = 0;
  private raceUiBound = false;
  private gamepadStatus: GamepadStatus = { connected: false, index: null, name: '', standardMapping: false };
  private lastGamepadMappingSignature = '';
  private lastLocalRacer: RacerSnapshot | null = null;
  private currentGhostStatus: GhostStatus | null = null;
  private raceUiElements: RaceUiElements | null = null;
  private raceDirector: RaceDirector | null = null;
  private lastTelemetryUiAt = 0;
  private hudIntegrityHealth: number | null = null;
  private hudIntegrityRacerId = '';
  private integrityHitAt = 0;
  private integrityHitTotal = 0;
  private integrityLossFrom = 100;
  private lastMinimapUiAt = 0;
  private lastLeaderboardSignature = '';
  private lastHudPlace = 0;
  private lastGhostUiAt = 0;
  private lastGhostSectorsSignature = '';
  private lastGhostPersistence: GhostStatus['persistence'] = 'idle';
  private benchmarkRequest: MobileBenchmarkRequest | null = parseMobileBenchmarkRequest(window.location.search);
  private benchmarkRecorder: MobileBenchmarkRecorder | null = null;
  private benchmarkOriginalSettings: GameSettings | null = null;
  private benchmarkAutoStarted = false;
  private offlineAutoLaunchTimer: number | undefined;
  private raceGameModulePromise: Promise<typeof import('./game/race-game.js')> | null = null;
  private localRaceModulePromise: Promise<typeof import('./local-race.js')> | null = null;
  private invitePollTimer: number | undefined;
  private invitePollGeneration = 0;
  private inviteUnavailableMessage = '';
  private raceEngineReady = false;
  private preparationWatchGame: RaceGame | null = null;
  private playerReady = false;
  private activeRaceMap: MapDefinition | null = null;
  private readonly spectator = new SpectatorController();
  private notificationFeed: { text: string; type: 'info' | 'error'; at: number }[] = [];
  private readonly spectatorUiScheduler = new TrailingUiScheduler(() => this.renderSpectatorUi(), 125);
  private readonly uiSounds = new UiSounds();
  private readonly audioMeter = new AudioVuMeter(document, () => (audioHub.isMuted ? null : audioHub.meter()));
  private readonly gamepadUi = new GamepadUi({
    onNavigate: () => this.uiSounds.tick(),
    vibration: () => this.gameSettings.gamepadVibration,
    suspended: () => document.documentElement.dataset.padCapture === 'true',
  });
  private controlsPanel: ControlsSettingsPanel | null = null;
  private lastSpectatorLeaderboardSignature = '';
  private tiltSetupTimer: number | undefined;
  private tiltSetupStartedAt = 0;
  private tiltSetupLeft = 0;
  private tiltSetupRight = 0;
  private menuStep: MenuStep = 'driver';
  /** Step to return to after changing language from later in the flow. */
  private languageReturn: MenuStep | null = null;
  /** Re-rendered after an in-race language switch. */
  private lastPerformanceStatus: PerformanceStatus | null = null;
  /** Listeners owned by the current menu screen; run on every navigation. */
  private menuDisposers: (() => void)[] = [];
  private steeringDevice: SteeringDevice = 'keyboard';
  private steeringDeviceChosen = false;
  /** Invitation from a race link or the paste field, shown on step 3. */
  private pendingInvite: { roomId: string; inviteToken: string; room: PublicRoom | null; error: string } | null = null;
  private publicLobbies: PublicLobbySummary[] | null = null;
  private publicLobbiesError = '';
  private lobbyBrowserTimer: number | undefined;
  private lobbyBrowserAbort: AbortController | null = null;
  /** Client-side lobby for offline sessions; survives races for "back to lobby". */
  private offlineLobby: OfflineLobbyState | null = null;
  private offlineLaunching = false;
  /** Offline room whose late worker checkpoints must not become a continue card. */
  private discardedOfflineRoomId = '';
  private garageReturn: 'play' | 'offline-lobby' = 'play';
  private lobbyPanel: LobbyPanel | null = null;
  /** Latest racer list seen in the lobby phase (names, cars, rival styles). */
  private lobbyRacers: RacerSnapshot[] = [];
  private lobbyGarageOpen = false;
  private lobbyGarageBefore = '';
  private returningToLobby = false;
  private savedOnlineSetupSignature = '';
  /** Track/atmosphere/car identity of the mounted engine; a change remounts. */
  private engineSignature = '';
  private engineRemountTimer: number | undefined;

  private get connection(): RaceTransport | null {
    return this.raceRuntime.transport;
  }

  private set connection(connection: RaceTransport | null) {
    this.raceRuntime.setTransport(connection);
  }

  private get game(): RaceGame | null {
    return this.raceRuntime.presentation;
  }

  private set game(game: RaceGame | null) {
    this.raceRuntime.setPresentation(game);
  }

  private get localRace(): boolean {
    return this.raceRuntime.lifecycle.state.mode === 'offline';
  }

  private get raceAttempt(): number {
    return this.raceRuntime.lifecycle.state.attempt;
  }

  private get restartPending(): boolean {
    return this.raceRuntime.lifecycle.state.operation === 'restarting';
  }

  constructor(private readonly root: HTMLElement) {
    // Touch devices get their own menu and HUD layout (mobile.css), keyed off
    // the same capability probe the input layer uses rather than media queries.
    document.documentElement.dataset.touch = String(this.deviceCapabilities.touchDevice);
    this.syncHudEffects();
    document.documentElement.dataset.form = this.deviceCapabilities.phone ? 'phone' : this.deviceCapabilities.tablet ? 'tablet' : 'desktop';
    this.garageController = new GarageController({
      selectedMapId: '',
      selectedCarId: CARS[0]?.id ?? '',
    }, (locked) => {
      this.setGarageActionButtonsDisabled(locked);
    });
    this.resultsPresentation = new ResultsPresentation(this.root);
    this.resultsCareer = new ResultsCareerController({
      schedule: (callback, delayMs) => this.raceRuntime.scope?.timeout(callback, delayMs),
      cancel: (timer) => this.raceRuntime.scope?.cancelTimeout(timer),
    }, (visible) => {
      if (!visible) this.resultsPresentation.reset();
      this.root.querySelector('#results-overlay')?.classList.toggle('hidden', !visible);
      this.root.querySelector('#race-stage')?.classList.toggle('results-visible', visible);
    });
    if (this.diagnosticsEnabled) {
      Object.defineProperty(window, '__NIGHTSHIFT_DIAGNOSTICS__', {
        configurable: true,
        value: Object.freeze({
          export: () => this.diagnosticReport(),
        }),
      });
      this.diagnostics.record('runtime', 'diagnostics-enabled');
    }
    this.pwa = new PwaController({
      onInstallAvailability: () => this.syncInstallButton(),
      onInstalled: () => this.toast(t('NIGHTSHIFT installed'), 'info', { quiet: true }),
      onUpdateReady: () => this.toast(t('A new build is ready for the next launch'), 'info'),
    });
    this.dailyRefreshScheduler = new DailyChallengeRefreshScheduler(
      () => this.refreshPublishedDailyChallenge(),
    );
    this.offlineCheckpointPublisher = new OfflineCheckpointPublisher({
      intervalMs: LOCAL_CHECKPOINT_INTERVAL_MS,
      onDurable: (record) => {
        this.offlineRaceLoadGeneration += 1;
        this.offlineRace = record;
        this.refreshGarageContinuation();
      },
      onUnavailable: () => {
        const message = t('Offline checkpoint storage is temporarily unavailable; retrying in the background.');
        const output = this.root.querySelector<HTMLElement>('#form-error');
        if (output && !output.textContent) output.textContent = message;
        if (this.root.querySelector('#race-stage')) this.toast(message, 'error');
      },
    });
    audioHub.install();
    this.applyAudioMix();
    audioHub.onMuteChange(() => this.syncAudioControls());
    this.bindAudioControls();
    this.bindGraphicsControls();
    this.root.addEventListener('click', (event) => {
      const target = event.target as HTMLElement | null;
      const button = target?.closest?.('button');
      if (!button || button.disabled || button.hasAttribute('data-drive')) return;
      if (button.matches('[data-audio-mute],[data-audio-test],[data-audio-dynamics]')) return;
      const confirm = UI_CONFIRM_BUTTON_IDS.has(button.id) || button.matches('.wizard-next,[data-play-action],[data-focus-key="ready"]');
      // Any confirming tap before a race doubles as the motion-permission
      // gesture, so tilt drivers never meet a permission dialog mid-grid.
      if (confirm && this.deviceCapabilities.motionSteering && this.gameSettings.motionSteeringEnabled
        && motionAccess() === 'unknown' && motionPermissionRequiresGesture()) {
        void requestMotionAccess();
      }
      if (confirm) this.uiSounds.confirm();
      else if (button.matches('[data-pad-back],.wizard-back')) this.uiSounds.back();
      else this.uiSounds.click();
    });
    this.root.addEventListener('change', (event) => {
      const input = event.target as HTMLInputElement | null;
      if (input?.type === 'checkbox') this.uiSounds.toggle(input.checked);
    });
    this.root.addEventListener('pointerover', (event) => {
      if ((event as PointerEvent).pointerType !== 'mouse') return;
      const button = (event.target as HTMLElement | null)?.closest?.('button:not([disabled]),summary,.swatch,[role="tab"]');
      const from = (event as PointerEvent).relatedTarget as Node | null;
      if (button && !(from && button.contains(from))) this.uiSounds.hover();
    });
    this.gamepadUi.start();
    if (import.meta.env.DEV) (window as unknown as { __nightshiftGamepadUi?: GamepadUi }).__nightshiftGamepadUi = this.gamepadUi;
    if (import.meta.env.DEV) (window as unknown as { __nightshiftAudio?: typeof audioHub }).__nightshiftAudio = audioHub;
    this.gamepadUi.onChange(() => this.refreshInputGlyphs());
    onKeyboardBindingsChange(() => this.refreshInputGlyphs());
  }

  /** Re-renders every key/button glyph for the live bindings and pad family. */
  private refreshInputGlyphs(): void {
    const mapping = this.controlsPanel?.currentMapping() ?? this.gamepadStatus.mapping ?? defaultGamepadMapping();
    renderInputGlyphs(this.root, mapping, this.gamepadStatus.family ?? this.gamepadUi.family);
    const hints = this.root.querySelector<HTMLElement>('#control-hints');
    if (hints) {
      // Replay the reveal so hints reappear briefly whenever the device changes.
      hints.classList.remove('revealed');
      void hints.offsetWidth;
      hints.classList.add('revealed');
    }
  }

  async start(): Promise<void> {
    this.routes.start(this.onHistoryNavigation);
    this.pwa.start();
    this.dailyRefreshScheduler.start();
    this.renderBoot();
    let routedNavigationGeneration: number | null = null;
    try {
      const offlineRaceLoadGeneration = ++this.offlineRaceLoadGeneration;
      const offlineRaceRequest = settlePromise(loadOfflineRaceCheckpoint());
      void this.applyStartupOfflineRace(
        offlineRaceRequest,
        offlineRaceLoadGeneration,
      );
      const cachedMaps = loadCachedMaps();
      this.publishedDailyChallenge = loadCachedDailyChallenge();
      const mapsRequest = settlePromise(getMaps());
      const dailyRequest = settlePromise(getDailyChallenge());
      if (cachedMaps) {
        this.maps = cachedMaps;
        // Both endpoints revalidate in the background. A half-open mobile
        // radio must never delay a garage that already has validated metadata.
        void this.applyStartupMetadataRefresh(mapsRequest, dailyRequest);
      } else {
        const mapsResult = await mapsRequest;
        if (mapsResult.status === 'fulfilled') {
          this.maps = mapsResult.value;
        } else {
          const offlineRaceResult = await offlineRaceRequest;
          const offlineRace = offlineRaceResult.status === 'fulfilled'
            ? offlineRaceResult.value
            : null;
          if (offlineRace && isConnectionError(mapsResult.reason)) {
            this.offlineRace = offlineRace;
            this.maps = [offlineRace.setup.map];
          } else {
            throw mapsResult.reason;
          }
        }
        // The Daily is optional and must not extend cold-start latency.
        void this.applyStartupMetadataRefresh(null, dailyRequest);
      }
      this.restoreDriverProfile();
      this.applyGhostChallengeRoute();
      this.garageMetadataReady = true;
      if (this.benchmarkRequest) {
        await this.startMobileBenchmark();
        return;
      }
      const route = this.routes.read();
      // A finished predecessor credential is not a "continue race" card, but
      // it is still required when its URL redirects to a durable successor.
      // Resolve the routed identity before loadRecentRaceSession prunes
      // completed standalone sessions from the garage.
      const routedSession = route ? loadRaceSession(route.roomId) : null;
      this.recentRace = routedSession ?? loadRecentRaceSession();
      if (!route) {
        this.renderMenu(this.entryStep());
        return;
      }
      const routeNavigationGeneration = this.historyNavigationGeneration;
      routedNavigationGeneration = routeNavigationGeneration;

      const stored = routedSession;
      if (stored) {
        this.inviteToken = stored.inviteToken || route.inviteToken;
        try {
          const room = await getRoom(route.roomId, this.inviteToken);
          if (routeNavigationGeneration !== this.historyNavigationGeneration) return;
          this.room = room;
          this.selectedMapId = room.settings.mapId;
          updateRaceSession(route.roomId, {
            mapId: room.settings.mapId,
            mapName: this.maps.find((map) => map.id === room.settings.mapId)?.name ?? stored.mapName,
            phase: room.phase,
            resumeWindowMs: resumeWindowFromSessionTtl(room.sessionTtlMs) ?? stored.resumeWindowMs,
          });
          this.connect(route.roomId, stored.player);
        } catch (error) {
          if (routeNavigationGeneration !== this.historyNavigationGeneration) return;
          if (isUnavailableRaceError(error)) forgetRaceSession(route.roomId);
          throw error;
        }
        return;
      }
      const room = await getRoom(route.roomId, route.inviteToken);
      if (routeNavigationGeneration !== this.historyNavigationGeneration) return;
      // An invitation leads through the wizard: new drivers set up name and
      // car first, returning drivers land on PLAY with the invite on top.
      this.pendingInvite = { roomId: route.roomId, inviteToken: route.inviteToken, room, error: '' };
      this.renderMenu(this.entryStep());
    } catch (error) {
      if (
        routedNavigationGeneration !== null
        && routedNavigationGeneration !== this.historyNavigationGeneration
      ) return;
      if (error instanceof ProtocolVersionError) {
        this.renderUpdating();
        void this.pwa.recoverProtocolMismatch();
        return;
      }
      if (isConnectionError(error)) {
        this.renderOffline();
        return;
      }
      if (error instanceof ApiError && ['ROOM_NOT_FOUND', 'INVALID_INVITE'].includes(error.code)) {
        this.renderInviteUnavailable(t(error.message));
        return;
      }
      this.renderFatal(error instanceof Error ? t(error.message) : t('Unable to load the race.'));
    }
  }

  private renderBoot(): void {
    this.root.innerHTML = `
      <main class="boot-screen">
        <div class="brand-mark"><span>N</span></div>
        <p class="eyebrow">${t('INITIALISING RACE CONTROL')}</p>
        <div class="loader"><i></i></div>
      </main>`;
  }

  // ──────────────────────────── Wizard flow ────────────────────────────
  // 1 DRIVER → 2 GARAGE → 3 PLAY → 4 LOBBY → 5 RACE → results → lobby | play

  /** First screen on launch: language until chosen, then driver or play. */
  private entryStep(): MenuStep {
    if (!hasChosenLocale()) return 'language';
    return this.hasDriverName() ? 'play' : 'driver';
  }

  private hasDriverName(): boolean {
    return this.driverProfile.name.trim().length >= 2;
  }

  private disposeMenuListeners(): void {
    const disposers = this.menuDisposers;
    this.menuDisposers = [];
    for (const dispose of disposers) dispose();
  }

  private menuReachable(): Set<MenuStep> {
    const steps = new Set<MenuStep>(['language', 'driver']);
    if (this.hasDriverName()) {
      steps.add('garage');
      steps.add('play');
    }
    if (this.offlineLobby) steps.add('lobby');
    return steps;
  }

  private menuDriverChip(): { name: string; carName: string; paint: string; level: number } | null {
    if (!this.hasDriverName()) return null;
    const car = CAR_BY_ID.get(this.selectedCarId) ?? CARS[0]!;
    return {
      name: this.driverProfile.name,
      carName: car.name,
      paint: this.tuning.get(car.id)?.paint ?? car.defaultTuning.paint,
      level: progressionLevel(this.progression.xp),
    };
  }

  /** Renders one wizard step inside the shared frame. */
  private renderMenu(step: MenuStep): void {
    if (step !== 'language' && step !== 'driver' && !this.hasDriverName()) step = 'driver';
    // Coming back to the language step from later in the flow returns there.
    if (step === 'language' && this.menuStep !== 'language') this.languageReturn = hasChosenLocale() ? this.menuStep : null;
    if (step === 'lobby') {
      if (this.offlineLobby) this.renderOfflineLobby();
      else this.renderMenu('play');
      return;
    }
    this.destroyRuntime();
    this.reloadLocalCareerState();
    this.recentRace = loadRecentRaceSession();
    this.menuStep = step;
    const online = navigator.onLine !== false;
    let body = '';
    let footer = '';
    if (step === 'language') {
      const copy = languageStepCopy(locale());
      body = languageStepHtml(locale());
      footer = wizardFooterHtml({
        back: null,
        next: { label: copy.cta, action: 'language-next' },
        hint: copy.hint,
      });
    } else if (step === 'driver') {
      const name = this.driverProfile.name || suggestedCallsign();
      body = driverStepHtml(this.driverStepModel(name));
      footer = wizardFooterHtml({
        back: { label: t('LANGUAGE'), step: 'language' },
        next: { label: this.pendingInvite ? t('CHOOSE CAR') : t('TO THE GARAGE'), action: 'driver-next', disabled: name.trim().length < 2 },
        hint: t('Enter ↵ continues'),
      });
    } else if (step === 'garage') {
      const car = CAR_BY_ID.get(this.selectedCarId) ?? CARS[0]!;
      const returnToLobby = this.garageReturn === 'offline-lobby' && this.offlineLobby;
      body = garageStepHtml({
        car,
        paint: this.currentTuning().paint,
        carInfoHtml: this.carInfo(car.id),
        tuningHtml: this.tuningControls(car.id),
        locked: this.importedGhostChallenge !== null,
        eyebrow: returnToLobby ? t('GARAGE · YOUR LOBBY IS WAITING') : t('STEP 3 OF 6 · YOUR MACHINE'),
      });
      footer = wizardFooterHtml({
        back: returnToLobby ? { label: t('LOBBY'), step: 'lobby' } : { label: t('DRIVER'), step: 'driver' },
        next: {
          label: returnToLobby ? t('BACK TO LOBBY') : this.pendingInvite?.room ? t('JOIN THE LOBBY') : t('CHOOSE HOW TO PLAY'),
          action: 'garage-next',
        },
        hint: t('← → browse cars · changes save automatically'),
      });
    } else {
      body = playStepHtml(this.playStepModel());
      footer = wizardFooterHtml({
        back: { label: t('GARAGE'), step: 'garage' },
        next: null,
        hint: t('Press G any time to open the garage'),
      });
    }
    this.root.innerHTML = menuFrameHtml({
      step,
      reachable: this.menuReachable(),
      driver: step === 'driver' || step === 'language' ? null : this.menuDriverChip(),
      body,
      footer,
      online,
      installAvailable: this.pwa.installAvailable,
      variant: step === 'language' ? 'wizard-language' : undefined,
      graphicsQuick: step === 'language' ? '' : graphicsQuickHtml(cachedCalibration(), this.gameSettings.graphicsQuality),
    });
    this.bindMenuFrame();
    if (step === 'language') this.bindLanguageStepEvents();
    else if (step === 'driver') this.bindDriverStepEvents();
    else if (step === 'garage') this.bindGarageStepEvents(mustElement('#wizard-stage'));
    else this.bindPlayStepEvents();
    this.syncInstallButton();
    this.refreshInputGlyphs();
    this.renderNotificationFeed();
    this.renderMapOutlines();
    if (this.startupNotice) {
      const notice = this.startupNotice;
      this.startupNotice = null;
      this.toast(notice.message, notice.type);
    }
    const focusTarget = this.root.querySelector<HTMLElement>(step === 'driver' ? '#driver-name' : '[data-pad-default]:not([disabled])');
    focusTarget?.focus({ preventScroll: true });
    if (step !== 'language') this.offerFirstPerformanceTest();
  }

  // ─────────────────────────── Performance test ───────────────────────────

  /** LOW/MEDIUM drop the HUD's per-frame CSS filters (see performance-test.css). */
  private syncHudEffects(): void {
    const quality = resolveGraphicsProfile(this.gameSettings.graphicsQuality, this.deviceCapabilities).quality;
    document.documentElement.dataset.hudFx = quality === 'high' || quality === 'ultra' ? 'full' : 'lite';
  }

  /** First launch (after the language): run the device test once, skippable. */
  private offerFirstPerformanceTest(): void {
    if (this.benchmarkRequest || performanceTestOpen() || loadCalibration() !== null) return;
    this.startPerformanceTest();
  }

  /** Runs the device test, or with `adjust` reopens the stored result to edit it. */
  private startPerformanceTest(adjust = false): void {
    if (performanceTestOpen()) return;
    this.root.querySelector<HTMLDetailsElement>('#graphics-quick')?.removeAttribute('open');
    const stored = cachedCalibration();
    openPerformanceTest({
      touchDevice: this.deviceCapabilities.touchDevice,
      settings: () => this.gameSettings,
      update: (next) => {
        this.gameSettings = next;
        this.applySettings();
        this.syncSettingsForm();
        this.refreshGraphicsPanels();
      },
      skipped: () => {
        if (loadCalibration() === null) saveCalibration({ version: 1, status: 'skipped', measuredAt: Date.now() });
        this.refreshGraphicsPanels();
      },
      closed: () => this.refreshGraphicsPanels(),
      sound: (kind) => {
        if (kind === 'confirm') this.uiSounds.confirm();
        else if (kind === 'back') this.uiSounds.back();
        else if (kind === 'toggle') this.uiSounds.click();
        else this.uiSounds.tick();
      },
    }, adjust && stored?.status === 'measured' ? { review: stored } : {});
  }

  /** Repaints the menu GRAPHICS popover and the Settings › Video test card. */
  private refreshGraphicsPanels(): void {
    const quick = this.root.querySelector<HTMLDetailsElement>('#graphics-quick');
    if (quick) {
      const open = quick.open;
      quick.outerHTML = graphicsQuickHtml(cachedCalibration(), this.gameSettings.graphicsQuality);
      if (open) this.root.querySelector<HTMLDetailsElement>('#graphics-quick')?.setAttribute('open', '');
    }
    const slot = this.root.querySelector<HTMLElement>('#graphics-test-slot');
    if (slot) {
      // A live online race cannot pause; the test would steal its frames.
      const live = (this.lastPhase === 'countdown' || this.lastPhase === 'racing') && !this.localRace;
      slot.innerHTML = graphicsTestCardHtml(cachedCalibration(), {
        disabledReason: live ? t('Available in the lobby, the menu or after the race.') : undefined,
        quality: this.gameSettings.graphicsQuality,
      });
    }
  }

  private setGraphicsQuality(graphicsQuality: GraphicsQuality): void {
    const calibration = cachedCalibration();
    this.gameSettings = graphicsQuality === 'auto' && calibration?.status === 'measured'
      ? applyCalibrationToSettings(this.gameSettings, calibration)
      : { ...this.gameSettings, graphicsQuality, ...graphicsDefaults(graphicsQuality, this.deviceCapabilities) };
    this.syncSettingsForm();
    this.applySettings();
    this.refreshGraphicsPanels();
  }

  /** Delegated: GRAPHICS popover and Settings › Video share these buttons. */
  private bindGraphicsControls(): void {
    this.root.addEventListener('click', (event) => {
      const target = (event.target as HTMLElement | null)?.closest?.<HTMLButtonElement>('[data-graphics-test],[data-graphics-adjust],[data-graphics-preset]');
      if (!target || target.disabled) return;
      if (target.hasAttribute('data-graphics-test') || target.hasAttribute('data-graphics-adjust')) {
        this.uiSounds.click();
        this.startPerformanceTest(target.hasAttribute('data-graphics-adjust'));
        return;
      }
      this.uiSounds.click();
      this.setGraphicsQuality(target.dataset.graphicsPreset as GraphicsQuality);
    });
    document.addEventListener('pointerdown', (event) => {
      const panel = this.root.querySelector<HTMLDetailsElement>('#graphics-quick');
      if (panel?.open && !panel.contains(event.target as Node)) panel.open = false;
    });
    document.addEventListener('keydown', (event) => {
      const panel = this.root.querySelector<HTMLDetailsElement>('#graphics-quick');
      if (event.key !== 'Escape' || !panel?.open) return;
      panel.open = false;
      panel.querySelector('summary')?.focus();
    });
  }

  private bindLanguageStepEvents(): void {
    const dispose = bindLanguageStep(this.root, locale(), {
      // Highlighting previews the language across the frame without saving it.
      select: (next) => {
        setLocale(next, { persist: false });
        const rail = this.root.querySelector('.wizard-rail');
        if (rail) rail.outerHTML = wizardRailHtml('language', this.menuReachable());
        this.uiSounds.tick();
      },
      confirm: (next) => this.completeLanguageStep(next),
    });
    this.menuDisposers.push(dispose);
  }

  private completeLanguageStep(next: Locale): void {
    setLocale(next);
    const target = this.languageReturn;
    this.languageReturn = null;
    this.renderMenu(target ?? (this.hasDriverName() ? 'play' : 'driver'));
  }

  /** Top-bar DE | EN switch: re-render the current screen in place. */
  private switchLanguage(next: Locale): void {
    if (next === locale()) return;
    setLocale(next);
    this.renderMenu(this.menuStep);
  }

  /**
   * Pause-menu language switch: re-translates the mounted race screen in
   * place (the race, canvas and listeners stay), then lets every dynamic
   * label re-render in the new language.
   */
  private switchRaceLanguage(next: Locale): void {
    if (next === locale()) return;
    const before = raceShell({ diagnostics: this.diagnosticsEnabled });
    setLocale(next);
    relocaliseTree(this.root, before, raceShell({ diagnostics: this.diagnosticsEnabled }));
    for (const button of this.root.querySelectorAll<HTMLButtonElement>('[data-set-language]')) {
      button.setAttribute('aria-pressed', String(button.dataset.setLanguage === next));
    }
    const live = this.lastPhase === 'countdown' || this.lastPhase === 'racing';
    const eyebrow = this.root.querySelector<HTMLElement>('#settings-eyebrow');
    if (eyebrow) eyebrow.textContent = this.localRace && live ? t('RACE PAUSED // OFFLINE') : t('RACE SYSTEMS');
    this.lastLeaderboardSignature = '';
    this.lastGhostSectorsSignature = '';
    const map = this.currentRaceMap();
    if (map) this.syncRaceUi(map, this.lobbyRacers);
    if (this.resultsCareer.visible && this.latestSnapshot) this.showResults(this.latestSnapshot, false, false);
    this.syncSettingsForm();
    this.controlsPanel?.syncForm();
    this.controlsPanel?.updateStatus(this.gamepadStatus);
    this.syncAudioControls();
    this.applySettings();
    this.renderConnectionStatus();
    this.renderNotificationFeed();
    if (this.lastPerformanceStatus) this.updatePerformanceStatus(this.lastPerformanceStatus);
    if (this.game) this.syncCameraMode(this.game.cameraModeLabel);
    this.refreshInputGlyphs();
    this.gamepadUi.refresh();
    // The strip mirrors HUD text, which re-localises on the next HUD tick.
    if (this.settingsOpen) window.setTimeout(() => { if (this.settingsOpen) this.renderPauseStatus(live); }, 150);
  }

  private bindMenuFrame(): void {
    audioHub.setPaused(false);
    audioHub.setScoreMode('menu');
    this.audioMeter.stop();
    this.syncAudioControls();
    this.root.querySelector('[data-home]')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.goHome();
    });
    this.root.addEventListener('click', this.onMenuNavigate);
    this.menuDisposers.push(() => this.root.removeEventListener('click', this.onMenuNavigate));
    this.root.querySelector<HTMLButtonElement>('#install-app')?.addEventListener('click', () => void this.installApp());
    window.addEventListener('keydown', this.onMenuShortcut);
    this.menuDisposers.push(() => window.removeEventListener('keydown', this.onMenuShortcut));
    window.addEventListener('online', this.onConnectivityChange);
    window.addEventListener('offline', this.onConnectivityChange);
  }

  private readonly onMenuNavigate = (event: Event): void => {
    const language = (event.target as HTMLElement).closest<HTMLElement>('[data-set-language]');
    if (language) {
      this.switchLanguage(language.dataset.setLanguage as Locale);
      return;
    }
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-wizard-step],[data-wizard-action]');
    if (!target || (target as HTMLButtonElement).disabled) return;
    const step = target.dataset.wizardStep as MenuStep | undefined;
    if (step) {
      if (step === 'garage' && this.menuStep === 'lobby') this.garageReturn = 'offline-lobby';
      else if (step === 'garage') this.garageReturn = 'play';
      this.renderMenu(step);
      return;
    }
    switch (target.dataset.wizardAction) {
      case 'language-next':
        // Handled by the language step's own binding (it knows the highlight).
        break;
      case 'driver-next':
        this.completeDriverStep();
        break;
      case 'garage-next':
        this.completeGarageStep();
        break;
    }
  };

  /** G opens the garage from the menus; Enter/Escape stay with the focused UI. */
  private readonly onMenuShortcut = (event: KeyboardEvent): void => {
    if (event.code !== 'KeyG' || event.ctrlKey || event.metaKey || event.altKey) return;
    const target = event.target as HTMLElement | null;
    if (target && (target.matches('input, textarea, select') || target.isContentEditable)) return;
    if (this.menuStep === 'garage' || !this.hasDriverName()) return;
    event.preventDefault();
    this.garageReturn = this.menuStep === 'lobby' && this.offlineLobby ? 'offline-lobby' : 'play';
    this.renderMenu('garage');
  };

  private driverStepModel(name: string): DriverStepModel {
    const pads = typeof navigator.getGamepads === 'function' ? [...navigator.getGamepads()].filter(Boolean) : [];
    const pad = pads[0] ?? null;
    if (!this.steeringDeviceChosen) {
      this.steeringDevice = preferredSteeringDevice(this.deviceCapabilities, this.gameSettings, pad !== null);
    }
    const inviteRoom = this.pendingInvite?.room ?? null;
    return {
      name,
      settings: this.gameSettings,
      capabilities: this.deviceCapabilities,
      device: this.steeringDevice,
      gamepad: { connected: pad !== null, name: pad?.id.replace(/\s*\(.*$/, '').slice(0, 40) ?? '' },
      invite: inviteRoom
        ? {
          hostName: inviteRoom.members?.find((member) => member.host)?.name ?? t('A friend'),
          trackName: this.maps.find((map) => map.id === inviteRoom.settings.mapId)?.name ?? t('Private race'),
        }
        : null,
    };
  }

  private bindDriverStepEvents(): void {
    const stage = mustElement<HTMLElement>('#wizard-stage');
    const model = this.driverStepModel(mustElement<HTMLInputElement>('#driver-name').value);
    const dispose = bindDriverStep(stage, model, {
      settings: () => this.gameSettings,
      saveSettings: (settings) => {
        this.gameSettings = settings;
        saveGameSettings(settings);
      },
      setName: (name) => this.persistDriverProfile(name),
      suggestName: () => suggestedCallsign(),
      setDevice: (device) => {
        this.steeringDevice = device;
        this.steeringDeviceChosen = true;
        if (device === 'tilt' || device === 'touch') {
          this.gameSettings.motionSteeringEnabled = device === 'tilt';
          saveGameSettings(this.gameSettings);
        }
      },
      next: () => this.completeDriverStep(),
      toast: (message) => this.toast(message, 'info'),
    });
    this.menuDisposers.push(dispose);
  }

  private completeDriverStep(): void {
    const input = this.root.querySelector<HTMLInputElement>('#driver-name');
    const name = input?.value.trim() ?? this.driverProfile.name;
    if (name.length < 2) {
      input?.focus();
      return;
    }
    this.persistDriverProfile(name);
    this.garageReturn = 'play';
    this.renderMenu('garage');
  }

  private completeGarageStep(): void {
    this.persistDriverProfile();
    if (this.garageReturn === 'offline-lobby' && this.offlineLobby) {
      this.garageReturn = 'play';
      this.renderOfflineLobby();
      return;
    }
    const invite = this.pendingInvite;
    if (invite?.room && invite.room.phase === 'lobby' && !invite.error) {
      void this.joinOnlineLobby(invite.roomId, invite.inviteToken);
      return;
    }
    this.renderMenu('play');
  }

  /** Car tabs, browsing keys, mastery paint and the livery panel. */
  private bindGarageStepEvents(scope: HTMLElement): void {
    const preview = scope.querySelector<HTMLElement>('#garage-preview');
    if (preview) {
      const generation = ++this.garageGeneration;
      preview.classList.add('loading');
      if (shouldStartWithConservativeFrameRate(this.gameSettings, this.deviceCapabilities)) {
        preview.classList.remove('loading');
        preview.classList.add('low-power-preview');
      } else {
        void this.mountGaragePreview(preview, generation);
      }
    }
    const selectCar = (carId: string): void => {
      if (this.importedGhostChallenge || !CAR_BY_ID.has(carId)) return;
      this.selectedCarId = carId;
      const car = CAR_BY_ID.get(carId)!;
      for (const tab of scope.querySelectorAll<HTMLButtonElement>('[data-car]')) {
        const active = tab.dataset.car === carId;
        tab.classList.toggle('active', active);
        tab.setAttribute('aria-selected', String(active));
        if (active) tab.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      }
      const counter = scope.querySelector<HTMLElement>('.garage-counter b');
      if (counter) counter.textContent = String(CARS.indexOf(car) + 1).padStart(2, '0');
      const label = scope.querySelector('.preview-label');
      if (label) label.innerHTML = garageNamePlateHtml(car);
      mustElementIn(scope, '#car-info').innerHTML = this.carInfo(carId);
      mustElementIn(scope, '#tuning-grid').innerHTML = this.tuningControls(carId);
      this.refreshPreview();
      this.persistDriverProfile();
    };
    scope.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const tab = target.closest<HTMLButtonElement>('[data-car]');
      if (tab?.dataset.car && !tab.disabled) {
        selectCar(tab.dataset.car);
        return;
      }
      const cycle = target.closest<HTMLButtonElement>('[data-car-cycle]');
      if (cycle && !cycle.disabled) {
        const index = CARS.findIndex((car) => car.id === this.selectedCarId);
        const next = CARS[(index + Number(cycle.dataset.carCycle) + CARS.length) % CARS.length];
        if (next) selectCar(next.id);
        return;
      }
      const mastery = target.closest<HTMLButtonElement>('[data-apply-mastery-paint]');
      const carId = mastery?.dataset.applyMasteryPaint;
      if (mastery && carId && masteryPaintUnlocked(this.vehicleMastery, carId)) {
        const paint = masteryPaintForCar(carId);
        const tuning = this.tuning.get(carId);
        if (!paint || !tuning) return;
        tuning.paint = paint;
        mustElementIn(scope, '#car-info').innerHTML = this.carInfo(carId);
        mustElementIn(scope, '#tuning-grid').innerHTML = this.tuningControls(carId);
        this.refreshPreview();
        this.persistDriverProfile();
        this.toast(t('Vehicle mastery paint equipped'), 'info', { quiet: true });
      }
    });
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      const target = event.target as HTMLElement | null;
      if (target && (target.matches('input, select, textarea') || target.closest('#tuning-grid'))) return;
      event.preventDefault();
      const index = CARS.findIndex((car) => car.id === this.selectedCarId);
      const next = CARS[(index + (event.key === 'ArrowRight' ? 1 : -1) + CARS.length) % CARS.length];
      if (next) selectCar(next.id);
    };
    window.addEventListener('keydown', onKey);
    this.menuDisposers.push(() => window.removeEventListener('keydown', onKey));
    this.bindTuningEvents(scope);
  }

  // ───────────────────────────── Step 3: Play ─────────────────────────────

  private playStepModel(): PlayStepModel {
    return {
      online: navigator.onLine !== false,
      maps: this.maps,
      lobbies: this.publicLobbies,
      lobbiesError: this.publicLobbiesError,
      invite: this.pendingInvite ? { room: this.pendingInvite.room, error: this.pendingInvite.error } : null,
      continueHtml: this.continueRaceCard(),
      eventsHtml: this.playEventsHtml(),
      careerHtml: this.progressionCard(),
      careerSummary: this.garageCareerStatus(),
    };
  }

  private playEventsHtml(): string {
    return `${this.dailyChallengeCard()}${this.nightTourCard()}${this.nemesisCard()}`;
  }

  private bindPlayStepEvents(): void {
    const stage = mustElement<HTMLElement>('#wizard-stage');
    stage.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const watch = target.closest<HTMLButtonElement>('[data-watch-lobby]');
      if (watch?.dataset.watchLobby && !watch.disabled) {
        void this.watchOnlineLobby(watch.dataset.watchLobby, watch);
        return;
      }
      const lobby = target.closest<HTMLButtonElement>('[data-join-lobby]');
      if (lobby?.dataset.joinLobby && !lobby.disabled) {
        void this.joinOnlineLobby(lobby.dataset.joinLobby, '', lobby);
        return;
      }
      const action = target.closest<HTMLButtonElement>('[data-play-action]');
      if (!action || action.disabled) return;
      switch (action.dataset.playAction) {
        case 'host':
          void this.hostOnlineLobby(action);
          break;
        case 'offline':
          this.openOfflineLobby({});
          break;
        case 'join-invite':
          if (this.pendingInvite) void this.joinOnlineLobby(this.pendingInvite.roomId, this.pendingInvite.inviteToken, action);
          break;
        case 'watch':
          if (this.pendingInvite?.room && !this.pendingInvite.error) {
            this.startSpectating(this.pendingInvite.room, this.pendingInvite.inviteToken);
          }
          break;
        case 'reset':
          this.confirmLocalReset(action);
          break;
        case 'dismiss-invite':
          this.pendingInvite = null;
          this.routes.replace('/');
          this.renderMenu('play');
          break;
      }
    });
    this.root.querySelector<HTMLFormElement>('#invite-entry')?.addEventListener('submit', (event) => {
      event.preventDefault();
      const input = mustElement<HTMLInputElement>('#invite-input');
      const parsed = parseInviteInput(input.value);
      if (!parsed) {
        this.showMenuError(t('That does not look like a NIGHTSHIFT invite link.'));
        input.focus();
        return;
      }
      this.pendingInvite = { ...parsed, room: null, error: '' };
      this.routes.replace(this.routes.raceUrl(parsed.roomId, parsed.inviteToken));
      void this.refreshPendingInvite().then(() => {
        const invite = this.pendingInvite;
        if (invite?.room && invite.room.phase === 'lobby' && !invite.error) {
          void this.joinOnlineLobby(invite.roomId, invite.inviteToken);
        } else if (this.menuStep === 'play') {
          this.renderMenu('play');
        }
      });
    });
    this.bindContinuationEvents();
    this.bindCareerEvents();
    this.startLobbyBrowser();
    if (this.pendingInvite) this.startInvitePolling();
  }

  /** Two clicks within four seconds wipe local data and restart the app. */
  private confirmLocalReset(button: HTMLButtonElement): void {
    if (button.dataset.confirming !== 'true') {
      button.dataset.confirming = 'true';
      button.textContent = t('CLICK AGAIN TO DELETE EVERYTHING');
      window.setTimeout(() => {
        if (!button.isConnected || button.dataset.confirming !== 'true') return;
        delete button.dataset.confirming;
        button.textContent = t('RESET LOCAL DATA');
      }, 4_000);
      return;
    }
    button.disabled = true;
    button.textContent = t('RESETTING…');
    this.destroyRuntime();
    void clearAllLocalData().finally(() => window.location.replace('/'));
  }

  private showMenuError(message: string): void {
    const output = this.root.querySelector<HTMLElement>('#form-error');
    if (output) output.textContent = message;
    this.toast(message, 'error');
  }

  private startLobbyBrowser(): void {
    this.stopLobbyBrowser();
    if (navigator.onLine === false) return;
    const poll = async (): Promise<void> => {
      const abort = new AbortController();
      this.lobbyBrowserAbort = abort;
      try {
        this.publicLobbies = await getPublicLobbies(abort.signal);
        this.publicLobbiesError = '';
      } catch (error) {
        if (abort.signal.aborted) return;
        if (error instanceof ProtocolVersionError) {
          this.handleMetadataFailure(error);
          return;
        }
        this.publicLobbies = this.publicLobbies ?? [];
        this.publicLobbiesError = t('Lobby list unavailable — race control is not reachable.');
      }
      const list = this.root.querySelector<HTMLElement>('#lobby-browser');
      if (!list || this.menuStep !== 'play') return;
      const focused = document.activeElement as HTMLElement | null;
      const focusedLobby = focused?.dataset.joinLobby ?? focused?.dataset.watchLobby;
      list.innerHTML = lobbyBrowserHtml({
        lobbies: this.publicLobbies,
        lobbiesError: this.publicLobbiesError,
        maps: this.maps,
        online: navigator.onLine !== false,
      });
      if (this.garageActions.pending) this.setGarageActionButtonsDisabled(true);
      if (focusedLobby) list.querySelector<HTMLElement>(`[data-join-lobby="${CSS.escape(focusedLobby)}"], [data-watch-lobby="${CSS.escape(focusedLobby)}"]`)?.focus({ preventScroll: true });
    };
    void poll();
    this.lobbyBrowserTimer = window.setInterval(() => void poll(), 5_000);
  }

  private stopLobbyBrowser(): void {
    if (this.lobbyBrowserTimer !== undefined) window.clearInterval(this.lobbyBrowserTimer);
    this.lobbyBrowserTimer = undefined;
    this.lobbyBrowserAbort?.abort();
    this.lobbyBrowserAbort = null;
  }

  private startInvitePolling(): void {
    this.stopInvitePolling();
    void this.refreshPendingInvite().then(() => this.refreshInviteCard());
    this.invitePollTimer = window.setInterval(() => {
      void this.refreshPendingInvite().then(() => this.refreshInviteCard());
    }, 4_000);
  }

  private stopInvitePolling(): void {
    if (this.invitePollTimer !== undefined) window.clearInterval(this.invitePollTimer);
    this.invitePollTimer = undefined;
    this.invitePollGeneration += 1;
  }

  private async refreshPendingInvite(): Promise<void> {
    const invite = this.pendingInvite;
    if (!invite) return;
    const generation = ++this.invitePollGeneration;
    try {
      const room = await getRoom(invite.roomId, invite.inviteToken);
      if (generation !== this.invitePollGeneration || this.pendingInvite !== invite) return;
      invite.room = room;
      invite.error = '';
    } catch (error) {
      if (generation !== this.invitePollGeneration || this.pendingInvite !== invite) return;
      invite.error = error instanceof ApiError && ['ROOM_NOT_FOUND', 'INVALID_INVITE'].includes(error.code)
        ? t('This invitation has expired or was revoked.')
        : isConnectionError(error) ? t('Race control is not reachable right now.') : error instanceof Error ? t(error.message) : t('Invitation unavailable.');
    }
  }

  private refreshInviteCard(): void {
    if (this.menuStep !== 'play' || !this.pendingInvite) return;
    const current = this.root.querySelector<HTMLElement>('.play-invite');
    const template = document.createElement('template');
    template.innerHTML = playStepHtml(this.playStepModel());
    const replacement = template.content.querySelector('.play-invite');
    if (current && replacement) current.replaceWith(replacement);
  }

  // ───────────────────────────── Online lobby ─────────────────────────────

  private resolvedNpcDifficulty(): NpcDifficulty {
    const preference = this.driverProfile.npcDifficulty;
    return preference === 'adaptive' ? adaptiveNpcDifficulty(this.progression) : preference;
  }

  private preferredLobbyMap(): MapDefinition {
    return this.maps.find((map) => map.id === this.selectedMapId) ?? this.maps[0]!;
  }

  private onlineCreatePayload(): CreateRoomRequest {
    const preferredMap = this.preferredLobbyMap();
    const profile = this.driverProfile;
    const profileMode: RaceEventMode = profile.eventMode === 'time-trial' ? 'circuit' : profile.eventMode;
    const difficulty = this.resolvedNpcDifficulty();
    const profileRoster = Array.from({ length: Math.min(MAX_LOBBY_NPCS, Math.max(0, profile.npcCount)) }, () => difficulty);
    // The last lobby this driver hosted wins over the generic profile defaults.
    const saved = applyLobbySetup({
      mapId: preferredMap.id,
      npcCount: profileRoster.length,
      npcDifficulty: difficulty,
      npcRoster: profileRoster,
      maxPlayers: 6,
      antiCheat: profile.antiCheat,
      collisions: profile.collisions,
      traffic: profile.traffic,
      timeOfDay: profile.timeOfDay === 'map' ? preferredMap.timeOfDay : profile.timeOfDay,
      weather: profile.weather === 'map' ? preferredMap.weather : profile.weather,
      eventMode: profileMode,
      visibility: 'private',
    }, loadLobbySetup('online'), this.maps, true);
    const map = this.maps.find((candidate) => candidate.id === saved.mapId) ?? preferredMap;
    const eventMode: RaceEventMode = raceEventMode(saved) === 'time-trial' ? 'circuit' : raceEventMode(saved);
    const roster = npcRosterOf(saved);
    const carId = this.selectedCarId;
    return {
      name: profile.name,
      deviceId: browserDeviceId(),
      mapId: map.id,
      npcCount: roster.length,
      npcDifficulty: difficulty,
      npcRoster: roster,
      visibility: saved.visibility === 'public' ? 'public' : 'private',
      maxPlayers: saved.maxPlayers,
      ...(saved.laps !== undefined ? { laps: saved.laps } : {}),
      antiCheat: saved.antiCheat === true,
      collisions: eventMode === 'road-rage' ? true : saved.collisions !== false,
      traffic: saved.traffic === true,
      timeOfDay: saved.timeOfDay ?? map.timeOfDay,
      weather: saved.weather ?? map.weather,
      timeTrial: false,
      eventMode,
      arcadePreset: 'standard',
      crewModule: profile.crewModule,
      signatureActive: masteryPaintUnlocked(this.vehicleMastery, carId),
      carId,
      tuning: cloneTuning(this.currentTuning()),
    };
  }

  private async hostOnlineLobby(button?: HTMLButtonElement): Promise<void> {
    if (this.maps.length === 0) return;
    const action = this.beginGarageAction('submit');
    if (button) button.innerHTML = `<span class="button-spinner" aria-hidden="true"></span>${t('OPENING LOBBY…')}`;
    void this.preloadRaceRuntime(false);
    try {
      const created = await createRoom(this.onlineCreatePayload());
      if (!this.isGarageActionCurrent(action)) return;
      this.pendingInvite = null;
      this.room = created.room;
      this.inviteToken = created.inviteToken;
      this.routes.pushRace(created.room.id, created.inviteToken);
      this.rememberRace(created.room, created.player, created.inviteToken);
      this.connect(created.room.id, created.player);
    } catch (error) {
      if (!this.isGarageActionCurrent(action)) return;
      this.finishGarageAction(action);
      if (error instanceof ProtocolVersionError) {
        this.renderUpdating();
        void this.pwa.recoverProtocolMismatch();
        return;
      }
      if (button?.isConnected) button.innerHTML = `${t('HOST A LOBBY')} <b>→</b>`;
      this.showMenuError(isConnectionError(error)
        ? t('Race control is not reachable. Try again, or race offline.')
        : error instanceof Error ? t(error.message) : t('Unable to open a lobby.'));
    }
  }

  private async watchOnlineLobby(roomId: string, button: HTMLButtonElement): Promise<void> {
    const action = this.beginGarageAction('submit');
    const label = button.innerHTML;
    button.disabled = true;
    button.textContent = t('CONNECTING…');
    try {
      const room = await getRoom(roomId, '');
      if (!this.isGarageActionCurrent(action)) return;
      this.finishGarageAction(action);
      this.routes.pushRace(room.id, '');
      this.startSpectating(room, '');
    } catch (error) {
      if (!this.isGarageActionCurrent(action)) return;
      this.finishGarageAction(action);
      if (button.isConnected) {
        button.disabled = false;
        button.innerHTML = label;
      }
      if (error instanceof ProtocolVersionError) {
        this.renderUpdating();
        void this.pwa.recoverProtocolMismatch();
        return;
      }
      this.showMenuError(error instanceof Error ? t(error.message) : t('This live session is no longer available.'));
    }
  }

  private async joinOnlineLobby(roomId: string, inviteToken: string, button?: HTMLButtonElement): Promise<void> {
    if (!this.hasDriverName()) {
      this.renderMenu('driver');
      return;
    }
    const action = this.beginGarageAction('submit');
    const label = button?.innerHTML;
    if (button) button.innerHTML = `<span class="button-spinner" aria-hidden="true"></span>${t('JOINING…')}`;
    void this.preloadRaceRuntime(false);
    try {
      const joined = await joinRoom(roomId, {
        inviteToken,
        deviceId: browserDeviceId(),
        name: this.driverProfile.name,
        carId: this.selectedCarId,
        tuning: cloneTuning(this.currentTuning()),
        crewModule: this.driverProfile.crewModule,
        signatureActive: masteryPaintUnlocked(this.vehicleMastery, this.selectedCarId),
      });
      if (!this.isGarageActionCurrent(action)) return;
      const memberInvite = joined.inviteToken ?? inviteToken;
      this.pendingInvite = null;
      this.room = joined.room;
      this.inviteToken = memberInvite;
      if (memberInvite) this.routes.pushRace(joined.room.id, memberInvite);
      this.rememberRace(joined.room, joined.player, memberInvite);
      this.connect(joined.room.id, joined.player);
    } catch (error) {
      if (!this.isGarageActionCurrent(action)) return;
      this.finishGarageAction(action);
      if (error instanceof ProtocolVersionError) {
        this.renderUpdating();
        void this.pwa.recoverProtocolMismatch();
        return;
      }
      if (button?.isConnected && label !== undefined) button.innerHTML = label;
      this.showMenuError(error instanceof ApiError && error.code === 'BANNED'
        ? t('The host has banned you from this lobby.')
        : error instanceof ApiError && error.code === 'RACE_STARTED'
          ? t('That race has already launched. Watch it live or pick another lobby.')
          : error instanceof ApiError && error.code === 'ROOM_FULL'
            ? t('That lobby is full.')
            : isConnectionError(error) ? t('Race control is not reachable right now.')
              : error instanceof Error ? t(error.message) : t('Unable to join that lobby.'));
      if (this.menuStep === 'play') void this.refreshPendingInvite().then(() => this.refreshInviteCard());
    }
  }

  /** Lobby view model for an online room, built from authoritative state. */
  private onlineLobbyModel(): LobbyViewModel | null {
    const room = this.room;
    const player = this.player;
    if (!room || !player) return null;
    const map = this.maps.find((candidate) => candidate.id === room.settings.mapId) ?? this.activeRaceMap;
    if (!map) return null;
    const racers = this.lobbyRacers;
    const racerById = new Map(racers.map((racer) => [racer.id, racer]));
    const members: LobbyMember[] = (room.members ?? []).map((member) => ({
      id: member.id,
      name: member.name,
      carId: member.carId,
      tuning: racerById.get(member.id)?.tuning,
      host: member.host,
      ready: member.ready,
      connected: member.connected,
      ping: displayPing(member.id === player.id ? this.latencyMs : this.memberPings.get(member.id)),
    }));
    const roster = npcRosterOf(room.settings);
    const rivals: LobbyRival[] = roster.map((difficulty, index) => {
      const racer = racerById.get(`npc-${index + 1}`);
      const identity = npcGridIdentity(index, room.settings);
      return {
        index,
        name: racer?.name ?? identity.name,
        carId: racer?.carId ?? null,
        paint: racer?.tuning.paint ?? identity.paint,
        style: racer?.rivalStyle ?? identity.style,
        difficulty,
        featured: identity.featured,
      };
    });
    return {
      kind: 'online',
      isHost: this.isHost,
      localPlayerId: player.id,
      settings: room.settings,
      map,
      maps: this.maps,
      members,
      rivals,
      inviteLink: this.inviteLink(),
      trackReady: this.raceEngineReady,
      localReady: this.playerReady,
      eventBanner: null,
      launching: false,
      notice: this.connectionState === 'reconnecting' ? t('Reconnecting to race control…') : '',
    };
  }

  /** Mounts or refreshes the lobby overlay inside the race stage. */
  private syncLobbyUi(): void {
    const container = this.root.querySelector<HTMLElement>('#lobby-panel-root');
    if (!container) return;
    if (this.localRace) {
      this.lobbyPanel?.destroy();
      this.lobbyPanel = null;
      const map = this.currentRaceMap();
      const mode = this.room ? raceEventMode(this.room.settings) : 'circuit';
      const html = `<div class="lobby-launch-card" style="--map-accent:${safeColour(map?.palette.accent ?? '#57e9ff')}"><p class="eyebrow">${escapeHtml(map ? t(map.location) : t('OFFLINE'))}</p><h1 id="lobby-title">${escapeHtml(map?.name ?? t('Offline race'))}</h1><span>${escapeHtml(t(eventModeLabel(mode)))}</span>${map?.gravity !== undefined && map.gravity < 0.95 ? `<p class="lobby-launch-gravity">${t('LOW GRAVITY · {percent}% · LONGER JUMPS — STEER AND PITCH IN THE AIR', { percent: Math.round(map.gravity * 100) })}</p>` : ''}<p class="lobby-launch-status">${this.raceEngineReady ? `<b>${t('GRID RELEASED')}</b>` : `<span class="button-spinner" aria-hidden="true"></span>${t('BUILDING THE CIRCUIT…')}`}</p><div class="lobby-launch-progress" data-ready="${this.raceEngineReady}" aria-hidden="true"><i></i></div><p class="lobby-launch-tip"><b>${t('TIP')}</b>${escapeHtml(t(launchTip(map?.id ?? 'offline')))}</p><button type="button" class="lobby-leave" data-lobby-action="leave-local" data-pad-back data-pad-back-label="${t('BACK')}">${t('BACK TO LOBBY')}</button></div>`;
      if (container.dataset.launchHtml !== html) {
        container.dataset.launchHtml = html;
        container.innerHTML = html;
        container.querySelector<HTMLButtonElement>('[data-lobby-action="leave-local"]')?.addEventListener('click', () => this.returnToOfflineLobby());
      }
      return;
    }
    const model = this.onlineLobbyModel();
    if (!model) return;
    if (this.isHost && this.room && (this.latestSnapshot?.phase ?? this.room.phase) === 'lobby') {
      const signature = JSON.stringify(this.room.settings);
      if (signature !== this.savedOnlineSetupSignature) {
        this.savedOnlineSetupSignature = signature;
        saveLobbySetup('online', this.room.settings);
      }
    }
    if (!this.lobbyPanel) {
      container.replaceChildren();
      delete container.dataset.launchHtml;
      this.lobbyPanel = new LobbyPanel(container, {
        patch: (patch) => this.connection?.send({ type: 'configure', settings: patch }),
        setReady: (ready) => this.setLobbyReady(ready),
        forceStart: () => this.connection?.send({ type: 'start' }),
        leave: () => this.leaveSession(),
        openGarage: () => this.openLobbyGarage(),
        share: () => this.openInviteShare(),
        copyInvite: () => void this.copyInvite(),
        kick: (playerId, ban) => this.connection?.send({ type: 'kick', playerId, ban }),
      });
    }
    this.lobbyPanel.update(model);
  }

  private setLobbyReady(ready: boolean): void {
    if (!this.raceEngineReady || !this.room || !this.player) return;
    this.playerReady = ready;
    if (this.room.members) {
      this.room = {
        ...this.room,
        members: this.room.members.map((member) => member.id === this.player?.id ? { ...member, ready } : member),
      };
    }
    this.connection?.send({ type: 'ready', ready });
    this.syncLobbyUi();
  }

  /** Exits a session. Lobby and result screens leave for good; a running
   * race keeps its reconnect pass so the continue card can resume it. */
  private leaveSession(): void {
    const roomId = this.room?.id;
    const phase = this.latestSnapshot?.phase ?? this.room?.phase;
    if (this.localRace) {
      if (phase === 'lobby' || phase === 'finished') {
        this.returnToOfflineLobby(true);
        return;
      }
      this.goHome();
      return;
    }
    if (roomId && (phase === 'lobby' || phase === 'finished')) {
      this.connection?.send({ type: 'leave' });
      forgetRaceSession(roomId);
      this.recentRace = null;
    }
    this.goHome();
  }

  private openLobbyGarage(): void {
    const stage = this.root.querySelector<HTMLElement>('#race-stage');
    if (!stage || this.lobbyGarageOpen || (this.latestSnapshot?.phase ?? this.room?.phase) !== 'lobby') return;
    this.lobbyGarageOpen = true;
    this.lobbyGarageBefore = JSON.stringify([this.selectedCarId, this.currentTuning(), this.driverProfile.crewModule]);
    const car = CAR_BY_ID.get(this.selectedCarId) ?? CARS[0]!;
    const overlay = document.createElement('section');
    overlay.className = 'lobby-garage-overlay';
    overlay.id = 'lobby-garage';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', t('Garage'));
    overlay.innerHTML = `
      <div class="lobby-garage-card">
        ${garageStepHtml({
          car,
          paint: this.currentTuning().paint,
          carInfoHtml: this.carInfo(car.id),
          tuningHtml: this.tuningControls(car.id),
          locked: this.importedGhostChallenge !== null,
          eyebrow: t('IN-LOBBY GARAGE'),
        })}
        <footer class="lobby-garage-foot"><p>${t('Your rivals see the new car as soon as you apply it. Applying clears your ready state.')}</p><button type="button" data-garage-close="cancel" data-pad-back data-pad-back-label="${t('CANCEL')}">${t('CANCEL')}</button><button type="button" class="wizard-next" data-garage-close="apply" data-pad-default>${t('APPLY')} <b>→</b></button></footer>
      </div>`;
    stage.append(overlay);
    this.bindGarageStepEvents(overlay);
    overlay.addEventListener('click', (event) => {
      const close = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-garage-close]');
      if (close) this.closeLobbyGarage(close.dataset.garageClose === 'apply');
    });
    overlay.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.closeLobbyGarage(false);
      }
    });
    overlay.querySelector<HTMLElement>('[data-garage-close="apply"]')?.focus({ preventScroll: true });
  }

  private closeLobbyGarage(apply: boolean): void {
    if (!this.lobbyGarageOpen) return;
    this.lobbyGarageOpen = false;
    this.garageGeneration += 1;
    this.garage?.destroy();
    this.garage = null;
    this.disposeMenuListeners();
    this.root.querySelector('#lobby-garage')?.remove();
    const changed = JSON.stringify([this.selectedCarId, this.currentTuning(), this.driverProfile.crewModule]) !== this.lobbyGarageBefore;
    if (!apply && changed) {
      // Cancel restores the saved build that was active before the garage opened.
      const [carId, tuning, crewModule] = JSON.parse(this.lobbyGarageBefore) as [string, VisualTuning, DriverProfile['crewModule']];
      this.selectedCarId = carId;
      this.tuning.set(carId, tuning);
      this.driverProfile = { ...this.driverProfile, crewModule };
      this.persistDriverProfile();
    } else if (apply && changed) {
      this.persistDriverProfile();
      this.connection?.send({
        type: 'loadout',
        carId: this.selectedCarId,
        tuning: cloneTuning(this.currentTuning()),
        crewModule: this.driverProfile.crewModule,
      });
      if (this.player) {
        this.player = { ...this.player, carId: this.selectedCarId, tuning: cloneTuning(this.currentTuning()), crewModule: this.driverProfile.crewModule };
      }
    }
    this.root.querySelector<HTMLElement>('[data-focus-key="garage"]')?.focus({ preventScroll: true });
  }

  /** Results → lobby. Online rooms reopen the shared lobby; an offline race
   * returns to the local lobby with the same settings. */
  private backToLobbyFromResults(): void {
    if (this.localRace) {
      this.returnToOfflineLobby();
      return;
    }
    this.resultsCareer.reset();
    if ((this.latestSnapshot?.phase ?? this.room?.phase) === 'finished') {
      this.returningToLobby = true;
      this.connection?.send({ type: 'return-to-lobby' });
    }
    this.syncLobbyUi();
  }

  // ───────────────────────────── Offline lobby ─────────────────────────────

  private defaultOfflineSettings(): RaceSettings {
    return applyLobbySetup(this.profileOfflineSettings(), loadLobbySetup('offline'), this.maps, false);
  }

  private profileOfflineSettings(): RaceSettings {
    const profile = this.driverProfile;
    const map = this.preferredLobbyMap();
    const mode = profile.eventMode;
    const difficulty = this.resolvedNpcDifficulty();
    const roster = mode === 'time-trial' ? [] : Array.from({ length: Math.max(1, Math.min(MAX_LOBBY_NPCS, profile.npcCount || 5)) }, () => difficulty);
    return {
      mapId: map.id,
      npcCount: roster.length,
      npcDifficulty: difficulty,
      npcRoster: roster,
      maxPlayers: 1,
      antiCheat: false,
      collisions: mode === 'road-rage' ? true : profile.collisions,
      traffic: profile.traffic,
      timeOfDay: profile.timeOfDay === 'map' ? map.timeOfDay : profile.timeOfDay,
      weather: profile.weather === 'map' ? map.weather : profile.weather,
      timeTrial: mode === 'time-trial',
      eventMode: mode,
      arcadePreset: profile.arcadePreset,
      visibility: 'private',
    };
  }

  private openOfflineLobby(options: {
    settings?: RaceSettings;
    banner?: { kicker: string; title: string } | null;
    forcedCarId?: string | null;
    pendingTour?: NightTourState | null;
    locked?: boolean;
  }): void {
    if (this.maps.length === 0) return;
    this.offlineLobby = {
      settings: options.settings ?? this.defaultOfflineSettings(),
      banner: options.banner ?? null,
      forcedCarId: options.forcedCarId ?? null,
      pendingTour: options.pendingTour ?? null,
      locked: options.locked === true,
      notice: '',
    };
    this.renderOfflineLobby();
  }

  /** Converts a curated event payload into a lobby the player can review. */
  private openOfflineEventLobby(
    payload: CreateRoomRequest,
    banner: { kicker: string; title: string },
    options: { pendingTour?: NightTourState | null; forcedCarId?: string | null; locked?: boolean } = {},
  ): void {
    const map = this.maps.find((candidate) => candidate.id === payload.mapId);
    if (!map) {
      this.showMenuError(t('That event track is not available in this build.'));
      return;
    }
    this.openOfflineLobby({
      settings: { ...localRaceSettings(payload, map), visibility: 'private' },
      banner,
      forcedCarId: options.forcedCarId ?? null,
      pendingTour: options.pendingTour ?? null,
      locked: options.locked,
    });
  }

  private renderOfflineLobby(): void {
    const lobby = this.offlineLobby;
    if (!lobby) {
      this.renderMenu('play');
      return;
    }
    this.destroyRuntime();
    this.menuStep = 'lobby';
    this.offlineLaunching = false;
    this.root.innerHTML = menuFrameHtml({
      step: 'lobby',
      reachable: this.menuReachable(),
      driver: this.menuDriverChip(),
      body: '<div class="lobby-panel-root offline" id="lobby-panel-root"></div>',
      footer: '',
      online: navigator.onLine !== false,
      installAvailable: this.pwa.installAvailable,
      variant: 'wizard-lobby',
    });
    this.bindMenuFrame();
    this.lobbyPanel = new LobbyPanel(mustElement('#lobby-panel-root'), {
      patch: (patch) => this.patchOfflineLobby(patch),
      setReady: () => void this.launchOfflineLobby(),
      forceStart: () => void this.launchOfflineLobby(),
      leave: () => {
        this.offlineLobby = null;
        this.renderMenu('play');
      },
      openGarage: () => {
        this.garageReturn = 'offline-lobby';
        this.renderMenu('garage');
      },
      share: () => undefined,
      copyInvite: () => undefined,
      kick: () => undefined,
    });
    this.lobbyPanel.update(this.offlineLobbyModel());
    this.renderNotificationFeed();
    this.root.querySelector<HTMLElement>('[data-focus-key="ready"]')?.focus({ preventScroll: true });
  }

  private offlineLobbyModel(): LobbyViewModel {
    const lobby = this.offlineLobby!;
    const map = this.maps.find((candidate) => candidate.id === lobby.settings.mapId) ?? this.maps[0]!;
    const carId = lobby.forcedCarId ?? this.selectedCarId;
    const car = CAR_BY_ID.get(carId) ?? CARS[0]!;
    return {
      kind: 'offline',
      isHost: !lobby.locked,
      localPlayerId: 'local',
      settings: lobby.settings,
      map,
      maps: this.maps,
      members: [{
        id: 'local',
        name: this.driverProfile.name,
        carId: car.id,
        tuning: this.tuning.get(car.id) ?? car.defaultTuning,
        host: true,
        ready: false,
        connected: true,
      }],
      rivals: npcRosterOf(lobby.settings).map((difficulty, index) => {
        const identity = npcGridIdentity(index, lobby.settings);
        return { index, name: identity.name, carId: null, paint: identity.paint, style: identity.style, difficulty, featured: identity.featured };
      }),
      inviteLink: null,
      trackReady: true,
      localReady: false,
      eventBanner: lobby.banner,
      launching: this.offlineLaunching,
      notice: lobby.notice || (lobby.locked ? t('This event uses a fixed setup — press start when ready.') : ''),
    };
  }

  private patchOfflineLobby(patch: LobbySettingsPatch): void {
    const lobby = this.offlineLobby;
    if (!lobby || lobby.locked) return;
    const map = this.maps.find((candidate) => candidate.id === (patch.mapId ?? lobby.settings.mapId));
    if (!map) return;
    const next = applyLobbyPatch(lobby.settings, patch, map, { online: false });
    if (!next.dailyChallengeId) lobby.forcedCarId = null;
    if (!next.tourId) lobby.pendingTour = null;
    if (!next.tourId && !next.dailyChallengeId && !next.featuredRival && lobby.banner) lobby.banner = null;
    lobby.settings = next;
    lobby.notice = '';
    this.rememberLobbyPreferences(next);
    if (!next.tourId && !next.dailyChallengeId && !next.featuredRival) saveLobbySetup('offline', next);
    this.lobbyPanel?.update(this.offlineLobbyModel());
  }

  /** Lobby edits become the defaults for the next lobby this driver opens. */
  private rememberLobbyPreferences(settings: RaceSettings): void {
    const map = this.maps.find((candidate) => candidate.id === settings.mapId);
    this.selectedMapId = settings.mapId;
    const roster = npcRosterOf(settings);
    const mode = raceEventMode(settings);
    this.driverProfile = {
      ...this.driverProfile,
      selectedMapId: settings.mapId,
      eventMode: mode,
      timeTrial: mode === 'time-trial',
      npcCount: mode === 'time-trial' ? this.driverProfile.npcCount : roster.length,
      npcDifficulty: roster.length > 0 ? dominantNpcDifficulty(roster) : this.driverProfile.npcDifficulty,
      traffic: settings.traffic === true,
      collisions: settings.collisions !== false,
      antiCheat: settings.antiCheat === true,
      arcadePreset: normaliseArcadePreset(settings.arcadePreset),
      timeOfDay: !map || settings.timeOfDay === map.timeOfDay ? 'map' : settings.timeOfDay ?? 'map',
      weather: !map || settings.weather === map.weather ? 'map' : settings.weather ?? 'map',
      updatedAt: Date.now(),
    };
    saveDriverProfile(this.driverProfile);
  }

  private offlineLobbyPayload(lobby: OfflineLobbyState): CreateRoomRequest {
    const settings = lobby.settings;
    const mode = raceEventMode(settings);
    const carId = lobby.forcedCarId ?? this.selectedCarId;
    const car = CAR_BY_ID.get(carId) ?? CARS[0]!;
    return {
      name: this.driverProfile.name,
      mapId: settings.mapId,
      npcCount: settings.npcCount,
      npcDifficulty: settings.npcDifficulty,
      npcRoster: npcRosterOf(settings),
      ...(settings.laps !== undefined ? { laps: settings.laps } : {}),
      antiCheat: false,
      collisions: settings.collisions !== false,
      traffic: settings.traffic === true,
      timeOfDay: settings.timeOfDay,
      weather: settings.weather,
      timeTrial: mode === 'time-trial',
      eventMode: mode,
      arcadePreset: normaliseArcadePreset(settings.arcadePreset),
      crewModule: this.driverProfile.crewModule,
      tourId: settings.tourId,
      tourStage: settings.tourStage,
      featuredRival: settings.featuredRival,
      wager: settings.wager,
      dailyChallengeId: settings.dailyChallengeId,
      signatureActive: mode !== 'time-trial' && masteryPaintUnlocked(this.vehicleMastery, car.id),
      carId: car.id,
      tuning: cloneTuning(this.tuning.get(car.id) ?? car.defaultTuning),
    };
  }

  private async launchOfflineLobby(): Promise<void> {
    const lobby = this.offlineLobby;
    if (!lobby || this.offlineLaunching) return;
    const model = this.offlineLobbyModel();
    const launch = lobbyLaunchState(model);
    if (launch.blocked) {
      lobby.notice = launch.blocked;
      this.lobbyPanel?.update(this.offlineLobbyModel());
      return;
    }
    this.offlineLaunching = true;
    this.lobbyPanel?.update(this.offlineLobbyModel());
    const action = this.beginGarageAction('event');
    try {
      const started = await this.startOfflineRace(this.offlineLobbyPayload(lobby), () => this.isGarageActionCurrent(action));
      if (!started) return;
      if (lobby.pendingTour) this.nightTour = lobby.pendingTour;
    } catch (error) {
      if (!this.isGarageActionCurrent(action)) return;
      this.finishGarageAction(action);
      this.offlineLaunching = false;
      lobby.notice = error instanceof Error ? t(error.message) : t('The offline race could not start.');
      if (this.lobbyPanel) this.lobbyPanel.update(this.offlineLobbyModel());
      else this.renderOfflineLobby();
    }
  }

  /** Closes the running offline session and reopens its lobby. */
  private returnToOfflineLobby(abandon = false): void {
    const roomId = this.room?.id;
    if (!this.offlineLobby && this.room && this.maps.some((map) => map.id === this.room!.settings.mapId)) {
      this.offlineLobby = {
        settings: { ...this.room.settings, visibility: 'private' },
        banner: null,
        forcedCarId: null,
        pendingTour: null,
        locked: false,
        notice: '',
      };
    }
    if (roomId && (abandon || this.localRace)) {
      this.discardedOfflineRoomId = roomId;
      void this.clearOfflineCheckpoint(false, roomId);
    }
    this.room = null;
    this.player = null;
    this.inviteToken = '';
    this.routes.replace('/');
    if (this.offlineLobby) this.renderOfflineLobby();
    else this.renderMenu('play');
  }

  private async mountGaragePreview(preview: HTMLElement, generation: number): Promise<void> {
    let candidate: GaragePreview | null = null;
    try {
      const { GaragePreview: GaragePreviewClass } = await import('./game/garage-preview.js');
      await waitForGaragePreviewOpportunity();
      if (generation !== this.garageGeneration || !preview.isConnected) return;
      candidate = new GaragePreviewClass(preview);
      candidate.show(this.selectedCarId, this.currentTuning());
      if (generation !== this.garageGeneration || !preview.isConnected) {
        candidate.destroy();
        return;
      }
      this.garage?.destroy();
      this.garage = candidate;
      candidate = null;
      preview.classList.remove('loading');
    } catch (error) {
      candidate?.destroy();
      if (generation !== this.garageGeneration || !preview.isConnected) return;
      preview.classList.remove('loading');
      preview.classList.add(
        error instanceof Error && error.name === 'GaragePreviewSoftwareRendererError'
          ? 'low-power-preview'
          : 'unavailable',
      );
    }
  }

  private continueRaceCard(): string {
    const continuation = this.preferredContinuation();
    if (!continuation) return '';
    const mapId = continuation.kind === 'offline' ? continuation.race.setup.payload.mapId : continuation.race.mapId;
    const mapName = this.maps.find((map) => map.id === mapId)?.name
      || (continuation.kind === 'offline' ? continuation.race.setup.map.name : continuation.race.mapName)
      || t('Private race');
    const player = continuation.kind === 'offline' ? continuation.race.setup.player : continuation.race.player;
    const racePhase = continuation.kind === 'offline' ? continuation.race.checkpoint.phase : continuation.race.phase;
    const carName = CAR_BY_ID.get(player.carId)?.name ?? t('Saved vehicle');
    const phase = racePhase === 'lobby' ? t('GRID OPEN') : racePhase === 'countdown' ? t('STARTING') : t('RACE IN PROGRESS');
    const signal = continuation.kind === 'offline' ? t('OFFLINE CHECKPOINT') : t('LOCAL SAVE');
    // A compact opener sits on the title line; the full save card lives in
    // a modal so the race modes and events stay above the fold.
    return `
      <span class="continue-race-pill">
        <button type="button" class="continue-race-opener" id="continue-race-opener">
          <i aria-hidden="true"></i><span><small>${signal}</small><b>${escapeHtml(mapName)}</b></span><em>${t('RESUME')} <b aria-hidden="true">↗</b></em>
        </button>
        <button type="button" class="continue-race-manage" id="continue-race-manage" aria-haspopup="dialog" aria-controls="continue-race-dialog" aria-label="${t('Forget saved race')}" title="${t('Forget saved race')}">×</button>
      </span>
      <dialog class="continue-race-dialog" id="continue-race-dialog" aria-labelledby="continue-race-dialog-title">
        <header><h2 id="continue-race-dialog-title">${t('SAVED RACE')}</h2><button type="button" class="continue-race-close" id="continue-race-close" aria-label="${t('Close')}">×</button></header>
        <aside class="continue-race-card" id="continue-race-card">
          <span class="continue-race-signal"><i></i>${signal}</span>
          <div><small>${t('CONTINUE WITHOUT AN ACCOUNT')}</small><b>${escapeHtml(mapName)}</b><span>${escapeHtml(player.name)} · ${escapeHtml(carName)} · ${phase}</span><em>${continuation.kind === 'offline' ? t('Full race state stored on this device') : t('Reconnect pass stored only in this browser')}</em></div>
          <div class="continue-race-actions"><button type="button" id="continue-race" autofocus>${t('CONTINUE')} <b>↗</b></button><button type="button" id="forget-race" aria-label="${t('Forget saved race')}">×</button></div>
        </aside>
      </dialog>`;
  }

  /** Drops the opener and the save modal once the saved race is gone. */
  private removeContinuationUi(): void {
    const dialog = this.root.querySelector<HTMLDialogElement>('#continue-race-dialog');
    if (dialog?.open) dialog.close();
    const slot = this.root.querySelector<HTMLElement>('#continue-race-slot');
    if (slot) slot.innerHTML = '';
    else this.root.querySelector('#continue-race-card')?.remove();
  }

  private refreshGarageContinuation(): void {
    const slot = this.root.querySelector<HTMLElement>('#continue-race-slot');
    if (!slot) return;
    slot.innerHTML = this.continueRaceCard();
    this.bindContinuationEvents();
  }

  private dailyChallengeCard(): string {
    const challenge = this.currentDailyChallenge();
    if (!challenge) return '';
    const map = this.maps.find((candidate) => candidate.id === challenge.mapId);
    const car = CAR_BY_ID.get(challenge.carId);
    const record = dailyChallengeRecord(this.dailyChallenges, challenge);
    return `
      <aside class="daily-challenge-card">
        <i>24</i>
        <span><small>${t('DAILY // {id}', { id: challenge.id })}</small><b>${escapeHtml(map?.name ?? challenge.mapId)} · ${escapeHtml(car?.name ?? challenge.carId)}</b><em>${t(challenge.timeOfDay.toUpperCase())} · ${t(challenge.weather.toUpperCase())} · ${record.bestLapMs ? t('BEST {time}', { time: formatLapTime(record.bestLapMs) }) : t('FIRST RUN OPEN')} · ${tn(record.attempts, '{n} ATTEMPT', '{n} ATTEMPTS')}</em></span>
        <button type="button" id="start-daily-challenge" ${this.importedGhostChallenge ? `disabled title="${t('Leave the current ghost challenge first')}"` : ''}>${t('RUN DAILY →')}</button>
      </aside>`;
  }

  private currentDailyChallenge(): DailyChallenge | null {
    const challenge = this.publishedDailyChallenge;
    if (
      !challenge
      || challenge.id !== new Date().toISOString().slice(0, 10)
      || !this.maps.some((map) => map.id === challenge.mapId)
      || !CAR_BY_ID.has(challenge.carId)
    ) return null;
    return challenge;
  }

  private reconcileImportedGhostChallenge(): boolean {
    const resolution = reconcileGhostChallengeCatalog(
      this.importedGhostChallenge,
      this.selectedMapId,
      this.maps.map((map) => map.id),
    );
    this.selectedMapId = resolution.selectedMapId;
    this.importedGhostChallenge = resolution.challenge;
    if (!resolution.releasedMapId) return false;
    clearStoredGhostChallenge(resolution.releasedMapId);
    this.driverProfile = {
      ...this.driverProfile,
      selectedMapId: resolution.selectedMapId,
      updatedAt: Date.now(),
    };
    saveDriverProfile(this.driverProfile);
    this.startupNotice = {
      message: t('Ghost challenge released // Its track is no longer available'),
      type: 'info',
    };
    return true;
  }

  private async applyStartupMetadataRefresh(
    mapsRequest: Promise<PromiseSettledResult<MapDefinition[]>> | null,
    dailyRequest: Promise<PromiseSettledResult<DailyChallenge | null>>,
  ): Promise<void> {
    const applyMaps = async (): Promise<void> => {
      if (!mapsRequest) return;
      const mapsResult = await mapsRequest;
      if (mapsResult.status === 'fulfilled') {
        this.maps = mapsResult.value;
        const releasedGhost = !this.room && this.reconcileImportedGhostChallenge();
        const onMenu = Boolean(this.root.querySelector('.wizard-shell')) && !this.room;
        if (onMenu && (releasedGhost || this.menuStep === 'play')) {
          // Re-render atomically so cards, outlines and locks match the
          // refreshed catalogue instead of mixing old and new tracks.
          this.renderMenu(this.menuStep);
          return;
        }
        this.refreshGarageCareerContent();
      } else {
        this.handleMetadataFailure(mapsResult.reason);
      }
    };
    const applyDaily = async (): Promise<void> => {
      const dailyResult = await dailyRequest;
      if (dailyResult.status === 'fulfilled') {
        this.publishedDailyChallenge = dailyResult.value;
      } else {
        this.handleMetadataFailure(dailyResult.reason);
        if (!this.currentDailyChallenge()) this.publishedDailyChallenge = null;
      }
      this.refreshGarageCareerContent();
    };
    await Promise.all([applyMaps(), applyDaily()]);
  }

  private async applyStartupOfflineRace(
    request: Promise<PromiseSettledResult<StoredOfflineRace | null>>,
    generation: number,
  ): Promise<void> {
    const result = await request;
    if (generation !== this.offlineRaceLoadGeneration || result.status !== 'fulfilled') return;
    this.offlineRace = result.value;
    this.refreshGarageContinuation();
  }

  private refreshPublishedDailyChallenge(): Promise<boolean> {
    if (this.dailyRefreshPromise) return this.dailyRefreshPromise;
    if (!this.currentDailyChallenge()) {
      this.publishedDailyChallenge = null;
      this.refreshGarageCareerContent();
    }
    const refresh = (async (): Promise<boolean> => {
      try {
        this.publishedDailyChallenge = await getDailyChallenge();
        this.refreshGarageCareerContent();
        return true;
      } catch (error) {
        this.handleMetadataFailure(error);
        if (!this.currentDailyChallenge()) this.publishedDailyChallenge = null;
        this.refreshGarageCareerContent();
        // A protocol mismatch is handled by the update flow and cannot be
        // repaired by repeatedly calling the same incompatible endpoint.
        return error instanceof ProtocolVersionError;
      }
    })();
    const tracked = refresh.finally(() => {
      if (this.dailyRefreshPromise === tracked) this.dailyRefreshPromise = null;
    });
    this.dailyRefreshPromise = tracked;
    return tracked;
  }

  private refreshGarageCareerContent(): void {
    if (this.menuStep !== 'play' || this.room || !this.root.querySelector('.wizard-shell')) return;
    const events = this.root.querySelector<HTMLElement>('#play-events');
    const career = this.root.querySelector<HTMLElement>('#garage-career-content');
    if (events) events.innerHTML = this.playEventsHtml();
    if (career) career.innerHTML = this.progressionCard();
    this.bindCareerEvents();
    if (this.garageActions.pending !== null) this.setGarageActionButtonsDisabled(true);
  }

  private reloadLocalCareerState(): void {
    this.progression = loadProgression();
    this.vehicleMastery = loadVehicleMastery();
    this.dailyChallenges = loadDailyChallengeState();
    this.nightTour = loadNightTour();
  }

  private garageCareerStatus(): string {
    const tour = nightTourDefinition(this.nightTour.tourId);
    return this.importedGhostChallenge
      ? t('GHOST CHALLENGE // {name}', { name: this.importedGhostChallenge.challenger.toUpperCase() })
      : this.nightTour.completed
        ? t('{tour} CLEARED', { tour: tour.name })
        : t('{tour} // STAGE {stage}', { tour: tour.name, stage: this.nightTour.eventIndex + 1 });
  }

  private syncGarageCareerState(): void {
    const level = this.root.querySelector<HTMLElement>('#garage-career-level');
    const status = this.root.querySelector<HTMLElement>('#garage-career-status');
    if (level) setText(level, t('LV {level}', { level: String(progressionLevel(this.progression.xp)).padStart(2, '0') }));
    if (status) setText(status, this.garageCareerStatus());
    this.refreshGarageCareerContent();
  }

  private handleMetadataFailure(error: unknown): void {
    if (!(error instanceof ProtocolVersionError) || this.room || this.player) return;
    this.renderUpdating();
    void this.pwa.recoverProtocolMismatch();
  }

  private nightTourCard(): string {
    const event = currentNightTourEvent(this.nightTour);
    const definition = nightTourDefinition(this.nightTour.tourId);
    const events = nightTourEvents(this.nightTour);
    const stage = Math.min(this.nightTour.eventIndex + 1, events.length);
    const completed = this.nightTour.completed;
    const imported = this.importedGhostChallenge;
    const challengeCar = imported ? CAR_BY_ID.get(imported.ghost.carId) : null;
    const challengeModule = imported
      ? CREW_MODULES.find((module) => module.id === imported.ghost.crewModule)
      : null;
    const challenge = imported
      ? `<aside class="ghost-challenge-card"><i>◎</i><span><small>${t('LOCKED GHOST CHALLENGE')}</small><b>${t('BEAT {name}', { name: escapeHtml(imported.challenger.toUpperCase()) })}</b><em>${escapeHtml(challengeCar?.name ?? imported.ghost.carId)} · ${escapeHtml(t(challengeModule?.name ?? 'STREET SETUP'))} · ${escapeHtml(this.maps.find((map) => map.id === imported.ghost.mapId)?.name ?? imported.ghost.mapId)}</em></span><span class="ghost-challenge-actions"><button type="button" id="race-ghost-challenge">${t('RACE GHOST →')}</button><button type="button" id="leave-ghost-challenge" title="${t('Return to an unrestricted race setup')}">${t('LEAVE')}</button></span></aside>`
      : '';
    return `${challenge}
      <section class="night-tour-card" data-complete="${completed}" style="--tour-accent:${definition.accent}">
        <nav class="night-tour-tabs" aria-label="${t('Offline championships')}">${STREET_TOURS.map((tour) => {
          const archive = tour.id === this.nightTour.tourId ? this.nightTour : this.nightTour.archives[tour.id];
          return `<button type="button" data-tour-id="${tour.id}" class="${tour.id === this.nightTour.tourId ? 'active' : ''}" aria-pressed="${tour.id === this.nightTour.tourId}"><small>${escapeHtml(t(tour.kicker))}</small><b>${escapeHtml(tour.name)}</b><em>${archive?.completed ? t('CLEARED') : archive?.eventIndex ? `${archive.eventIndex}/${tour.events.length}` : t('NEW')}</em></button>`;
        }).join('')}</nav>
        <header><span><small>${t('OFFLINE-SAFE CHAMPIONSHIP')}</small><b>${t('NIGHT TOUR // {tour}', { tour: escapeHtml(definition.name) })}</b></span><em>${completed ? t('TOUR COMPLETE') : t('STAGE {stage} / {total}', { stage, total: events.length })}</em></header>
        <div class="night-tour-route">${events.map((tourEvent, index) => `<span class="${index < this.nightTour.eventIndex ? 'complete' : index === this.nightTour.eventIndex && !completed ? 'active' : ''}"><i>${index < this.nightTour.eventIndex ? '✓' : index + 1}</i><b>${escapeHtml(tourEvent.mapId.split('-')[0]!.toUpperCase())}</b></span>`).join('')}</div>
        <div class="night-tour-next"><span><small>${completed ? tn(this.nightTour.completions, '{n} TOUR CLEARED · BEST {points} PTS', '{n} TOURS CLEARED · BEST {points} PTS', { points: this.nightTour.bestPoints }) : event ? t(event.title) : definition.name}</small><b>${completed ? t('{reward} UNLOCKED.', { reward: t(definition.reward) }) : escapeHtml(event?.subtitle ? t(event.subtitle) : '')}</b><em>${t('YOU {player} · RIVAL BOSSES {rivals}', { player: this.nightTour.playerPoints, rivals: this.nightTour.rivalPoints })}</em></span><button type="button" id="start-night-tour" ${imported ? `disabled title="${t('Leave the ghost challenge before starting NIGHT TOUR')}"` : ''}>${completed ? t('NEW TOUR ↻') : this.nightTour.eventIndex > 0 ? t('CONTINUE TOUR →') : t('START TOUR →')}</button></div>
      </section>
      <button type="button" class="surprise-event" id="surprise-me" ${imported ? `disabled title="${t('Leave the ghost challenge before starting a surprise event')}"` : ''}><span><small>${t('ONE-TAP QUICK EVENT')}</small><b>${t('SURPRISE ME')}</b></span><em>${t('RANDOM DISTRICT · MODE · CONDITIONS')}</em><i>↗</i></button>`;
  }

  private preferredContinuation(): { kind: 'offline'; race: StoredOfflineRace } | { kind: 'online'; race: StoredRaceSession } | null {
    if (this.offlineRace && (!this.recentRace || this.offlineRace.savedAt >= this.recentRace.savedAt)) {
      return { kind: 'offline', race: this.offlineRace };
    }
    return this.recentRace ? { kind: 'online', race: this.recentRace } : null;
  }

  private progressionCard(): string {
    const level = progressionLevel(this.progression.xp);
    const meter = progressionMeter(this.progression.xp);
    const liveContracts = activeRotatingContracts(this.progression);
    const nextReward = nextCareerReward(level);
    const paintRewards = CAREER_REWARDS.filter((reward) => reward.kind === 'paint' && reward.colour);
    return `
      <section class="progression-card" aria-labelledby="career-title">
        <header>
          <span class="career-level">${t('LV {level}', { level: String(level).padStart(2, '0') })}</span>
          <div><small>${t('LOCAL STREET CAREER')}</small><h3 id="career-title">${t(rankTitle(level))}</h3></div>
          <b>${t('{races} RACES · {wins} WINS', { races: formatNumber(this.progression.races), wins: formatNumber(this.progression.wins) })}</b>
        </header>
        <div class="career-meter"><span><i style="width:${meter.percent.toFixed(1)}%"></i></span><small>${meter.maxed ? t('MAX RANK') : `${formatNumber(meter.current)} / ${formatNumber(meter.required)} XP`}</small></div>
        <div class="career-summary"><span>${t('{done} / {total} STREET CONTRACTS', { done: this.progression.completed.length, total: CHALLENGES.length })}</span><b>${nextReward ? t('NEXT // {reward} · LV{level}', { reward: t(nextReward.name), level: nextReward.level }) : t('ALL RANK REWARDS UNLOCKED')}</b></div>
        <div class="challenge-strip">
          ${CHALLENGES.map((challenge) => {
            const complete = this.progression.completed.includes(challenge.id);
            return `<article class="${complete ? 'complete' : ''}"><i>${complete ? '✓' : '+' + formatNumber(challenge.reward)}</i><div><b>${t(challenge.name)}</b><small>${t(challenge.description)}</small></div></article>`;
          }).join('')}
        </div>
        <section class="live-contracts" aria-label="${t('Rotating career contracts')}">
          <header><span>${t('LIVE CONTRACTS')}</span><small>${t('OFFLINE-SAFE · AUTO ROTATION')}</small></header>
          <div>${liveContracts.map((contract) => {
            const percent = Math.min(100, (contract.progress / contract.target) * 100);
            const progress = contract.target >= 1_000
              ? `${formatNumber(Math.round(contract.progress))} / ${formatNumber(contract.target)}`
              : `${contract.progress} / ${contract.target}`;
            return `<article class="${contract.claimed ? 'complete' : ''}" data-cadence="${contract.cadence}"><i>${contract.claimed ? '✓' : contract.cadence === 'daily' ? 'D' : 'W'}</i><div><b>${t(contract.name)}</b><small>${t(contract.description)}</small><span><em style="width:${percent.toFixed(1)}%"></em></span></div><strong>${contract.claimed ? t('CLAIMED') : progress}<small>+${formatNumber(contract.reward)} XP</small></strong></article>`;
          }).join('')}</div>
        </section>
        <div class="career-rewards">
          <span>${t('REWARD PAINT')}</span>
          ${paintRewards.map((reward) => `<button type="button" data-reward-paint="${reward.colour}" ${unlockedReward(level, reward.id) ? '' : 'disabled'} style="--reward:${reward.colour}" aria-label="${unlockedReward(level, reward.id) ? t('Apply {paint} paint', { paint: reward.name }) : t('{paint} paint unlocks at level {level}', { paint: reward.name, level: reward.level })}"></button>`).join('')}
          <button type="button" data-reward-paint="#ff4fa3" ${nightTourCompletions(this.nightTour, 'city-lights') > 0 ? '' : 'disabled'} style="--reward:#ff4fa3" aria-label="${nightTourCompletions(this.nightTour, 'city-lights') > 0 ? t('Apply City Lights tour paint') : t('City Lights paint unlocks by completing its tour')}"></button>
          <button type="button" data-reward-paint="#57e9ff" ${nightTourCompletions(this.nightTour, 'mountain-pulse') > 0 ? '' : 'disabled'} style="--reward:#57e9ff" aria-label="${nightTourCompletions(this.nightTour, 'mountain-pulse') > 0 ? t('Apply Summit Ice tour paint') : t('Summit Ice paint unlocks by completing Mountain Pulse')}"></button>
          <button type="button" data-reward-paint="#ff7a3d" ${nightTourCompletions(this.nightTour, 'outlaw-frequency') > 0 ? '' : 'disabled'} style="--reward:#ff7a3d" aria-label="${nightTourCompletions(this.nightTour, 'outlaw-frequency') > 0 ? t('Apply Outlaw Frequency tour paint') : t('Outlaw Frequency paint unlocks by completing its tour')}"></button>
          <button type="button" data-reward-paint="#d946ef" ${nemesisPaintUnlocked(this.progression) ? '' : 'disabled'} style="--reward:#d946ef" aria-label="${nemesisPaintUnlocked(this.progression) ? t('Apply Nemesis Violet paint') : t('Nemesis Violet unlocks after beating one rival three times')}"></button>
          <small>${t('RANK PAINTS · THREE TOUR PAINTS · NEMESIS VIOLET AT 3 WINS')}</small>
        </div>
      </section>`;
  }

  private nemesisCard(): string {
    const nemesis = currentNemesis(this.progression);
    if (!nemesis) return '';
    const record = nemesis.record;
    const advantage = record.winsAgainst - record.lossesAgainst;
    return `
      <aside class="nemesis-card">
        <i>⚡</i>
        <span><small>${t('CURRENT NEMESIS')}</small><b>${escapeHtml(record.name.toUpperCase())}</b><em>${record.winsAgainst}-${record.lossesAgainst} · ${advantage >= 0 ? t('YOU LEAD') : t('{n} DOWN', { n: Math.abs(advantage) })} · ${record.streak < 0 ? t('{n} LOSS STREAK', { n: Math.abs(record.streak) }) : t('{n} WIN STREAK', { n: Math.abs(record.streak) })}</em></span>
        <button type="button" id="start-nemesis-race" ${this.importedGhostChallenge ? `disabled title="${t('Leave the ghost challenge before starting a revenge race')}"` : ''}>${t('REVENGE →')}</button>
      </aside>`;
  }

  private carInfo(carId: string): string {
    const car = CAR_BY_ID.get(carId) ?? CARS[0]!;
    const signature = vehicleSignatureFor(car);
    const masteryRecord = vehicleMasteryRecord(this.vehicleMastery, car.id);
    const masteryGoals = vehicleMasteryGoals(car.id, masteryRecord);
    const masteryComplete = masteryGoals.every((goal) => goal.complete);
    const masteryPaint = masteryPaintForCar(car.id);
    const equippedPaint = this.tuning.get(car.id)?.paint ?? car.defaultTuning.paint;
    const stat = (label: string, value: number, max: number) => `<div><span>${label}</span><i><b style="width:${Math.round((value / max) * 100)}%"></b></i></div>`;
    return `
      <div><p class="car-class">${escapeHtml(t(car.className))}</p><h3>${escapeHtml(car.name)}</h3><p>${escapeHtml(t(car.description))}</p><aside class="car-signature" data-active="${masteryComplete}"><small>${masteryComplete ? t('ACTIVE MASTERY SIGNATURE') : t('SIGNATURE PREVIEW')}</small><b>${escapeHtml(t(signature.name))}</b><span>${escapeHtml(t(signature.description))} ${masteryComplete ? t('Its event-triggered sidegrade is equipped automatically.') : t('Complete all mastery goals to activate its gameplay sidegrade.')}</span></aside><aside class="vehicle-mastery" data-complete="${masteryComplete}"><header><small>${t('VEHICLE MASTERY')}</small><b>${masteryGoals.filter((goal) => goal.complete).length} / ${masteryGoals.length}</b></header>${masteryGoals.map((goal) => `<span class="${goal.complete ? 'complete' : ''}" title="${escapeHtml(t(goal.description))}"><i>${goal.complete ? '✓' : '◇'}</i><em>${escapeHtml(t(goal.name))}</em><small>${Math.min(goal.target, goal.progress)} / ${goal.target}</small></span>`).join('')}${masteryPaint ? `<button type="button" data-apply-mastery-paint="${escapeHtml(car.id)}" ${masteryComplete ? '' : 'disabled'} style="--mastery-paint:${safeColour(masteryPaint)}">${masteryComplete ? equippedPaint.toLowerCase() === masteryPaint.toLowerCase() ? t('MASTERY PAINT EQUIPPED') : t('EQUIP MASTERY PAINT') : t('PAINT LOCKED')}</button>` : ''}</aside></div>
      <div class="stat-bars">
        ${stat(t('TOP SPEED'), car.topSpeed, 80)}${stat(t('ACCEL'), car.acceleration, 14)}${stat(t('HANDLING'), car.handling, 3)}${stat(t('TERRAIN'), car.offroadGrip, 1)}
      </div>`;
  }

  private tuningControls(carId: string): string {
    const car = CAR_BY_ID.get(carId) ?? CARS[0]!;
    const tuning = this.tuning.get(carId) ?? car.defaultTuning;
    const level = progressionLevel(this.progression.xp);
    return `
      ${tuningPanelHtml({
        tuning,
        factory: car.defaultTuning,
        bike: car.body === 'bike',
        decalUnlocked: (reward) => unlockedReward(level, reward),
      })}
      <fieldset class="crew-module-control"><legend>${t('CREW MODULE // ONE TRADE-OFF')}</legend>${CREW_MODULES.map((module) => `<label title="${escapeHtml(this.importedGhostChallenge ? t('Leave the ghost challenge to change its performance setup.') : t(module.description))}"><input type="radio" name="crew-module" value="${module.id}" ${this.driverProfile.crewModule === module.id ? 'checked' : ''} ${this.importedGhostChallenge ? 'disabled' : ''}><span><b>${t(module.name)}</b><small>${t(module.tradeoff)}</small></span></label>`).join('')}</fieldset>`;
  }

  /** Redraws the styling panel after a whole-build change, keeping focus on
   * the control the player used so keyboard and screen-reader users stay put. */
  private rerenderTuning(focusSelector?: string): void {
    const grid = this.root.querySelector<HTMLElement>('#tuning-grid');
    if (!grid) return;
    grid.innerHTML = this.tuningControls(this.selectedCarId);
    if (focusSelector) grid.querySelector<HTMLElement>(focusSelector)?.focus({ preventScroll: true });
  }

  private renderMapOutlines(): void {
    for (const canvas of this.root.querySelectorAll<HTMLCanvasElement>('canvas[data-map-outline]')) {
      const map = this.maps.find((candidate) => candidate.id === canvas.dataset.mapOutline);
      if (map) drawMapOutline(canvas, map, { sections: canvas.dataset.outlineSections === 'true' });
    }
  }

  private bindContinuationEvents(): void {
    const dialog = this.root.querySelector<HTMLDialogElement>('#continue-race-dialog');
    // One tap straight back onto the track; the modal only guards discarding.
    this.root.querySelector<HTMLButtonElement>('#continue-race-opener')?.addEventListener('click', (event) => {
      const opener = event.currentTarget as HTMLButtonElement;
      if (opener.disabled) return;
      opener.disabled = true;
      void this.resumeRecentRace().finally(() => {
        if (opener.isConnected) opener.disabled = false;
      });
    });
    this.root.querySelector<HTMLButtonElement>('#continue-race-manage')?.addEventListener('click', () => {
      if (dialog && !dialog.open) dialog.showModal();
    });
    this.root.querySelector<HTMLButtonElement>('#continue-race-close')?.addEventListener('click', () => dialog?.close());
    // A click on the backdrop lands on the dialog element itself.
    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    this.root.querySelector<HTMLButtonElement>('#continue-race')?.addEventListener('click', () => {
      void this.resumeRecentRace();
    });
    this.root.querySelector<HTMLButtonElement>('#forget-race')?.addEventListener('click', () => {
      if (this.garageActions.pending === 'resume') this.cancelGarageAction();
      const continuation = this.preferredContinuation();
      if (continuation?.kind === 'offline') {
        void this.clearOfflineCheckpoint(false, continuation.race.setup.roomId);
      } else if (continuation?.kind === 'online') {
        forgetRaceSession(continuation.race.roomId);
        this.recentRace = null;
      }
      if (continuation?.kind === 'online') this.refreshGarageContinuation();
      else this.removeContinuationUi();
      const status = this.root.querySelector<HTMLElement>('#continue-race-status') ?? mustElement('#form-error');
      status.textContent = t('Local race removed. Your driver setup is still saved.');
    });
  }

  private bindCareerEvents(): void {
    this.root.querySelector<HTMLButtonElement>('#start-night-tour')?.addEventListener('click', () => {
      void this.startNightTourFromGarage();
    });
    for (const button of this.root.querySelectorAll<HTMLButtonElement>('[data-tour-id]')) {
      button.addEventListener('click', () => {
        const tourId = button.dataset.tourId as StreetTourId;
        if (!STREET_TOURS.some((tour) => tour.id === tourId) || tourId === this.nightTour.tourId) return;
        const action = this.beginGarageAction('tour');
        void mutateLocalNightTour((current) => selectNightTour(current, tourId)).then((nightTour) => {
          if (!this.isGarageActionCurrent(action)) return;
          this.nightTour = nightTour;
          this.renderMenu('play');
        }).catch((error) => {
          if (!this.isGarageActionCurrent(action)) return;
          this.finishGarageAction(action);
          const output = this.root.querySelector<HTMLElement>('#form-error');
          if (output) {
            output.textContent = error instanceof Error
              ? t(error.message)
              : t('Unable to save the selected NIGHT TOUR.');
          }
        });
      });
    }
    this.root.querySelector<HTMLButtonElement>('#surprise-me')?.addEventListener('click', () => {
      void this.startSurpriseEventFromGarage();
    });
    this.root.querySelector<HTMLButtonElement>('#start-daily-challenge')?.addEventListener('click', () => {
      void this.startDailyChallenge();
    });
    this.root.querySelector<HTMLButtonElement>('#start-nemesis-race')?.addEventListener('click', () => {
      void this.startNemesisRaceFromGarage();
    });
    this.root.querySelector<HTMLButtonElement>('#leave-ghost-challenge')?.addEventListener('click', () => {
      const challenge = this.importedGhostChallenge;
      if (!challenge) return;
      clearStoredGhostChallenge(challenge.ghost.mapId);
      this.importedGhostChallenge = null;
      this.startupNotice = { message: t('Ghost challenge released // Race setup unlocked'), type: 'info' };
      this.renderMenu('play');
    });
    this.root.querySelector<HTMLButtonElement>('#race-ghost-challenge')?.addEventListener('click', () => {
      this.startGhostChallenge();
    });
    for (const button of this.root.querySelectorAll<HTMLButtonElement>('[data-reward-paint]')) {
      button.addEventListener('click', () => {
        if (button.disabled || !button.dataset.rewardPaint) return;
        this.currentTuning().paint = button.dataset.rewardPaint;
        this.rerenderTuning();
        this.refreshPreview();
        this.persistDriverProfile();
      });
    }
  }

  private syncInstallButton(): void {
    const button = this.root.querySelector<HTMLButtonElement>('#install-app');
    if (button) button.hidden = !this.pwa.installAvailable;
  }

  private async installApp(): Promise<void> {
    const installed = await this.pwa.promptInstall();
    if (!installed) this.toast(t('Install prompt dismissed'), 'info', { quiet: true });
  }

  private bindTuningEvents(scope: ParentNode = this.root): void {
    // The grid's contents are redrawn on every whole-build change, so listen
    // once on the grid itself rather than on each control.
    const grid = scope.querySelector<HTMLElement>('#tuning-grid');
    if (!grid || grid.dataset.bound === 'true') return;
    grid.dataset.bound = 'true';
    const car = () => CAR_BY_ID.get(this.selectedCarId) ?? CARS[0]!;
    const decalAllowed = (decal: VisualTuning['decal']) => decal !== 'chevron' && decal !== 'circuit'
      ? true
      : unlockedReward(progressionLevel(this.progression.xp), decal);
    const commit = (focusSelector?: string) => {
      this.rerenderTuning(focusSelector);
      this.refreshPreview();
      this.persistDriverProfile();
    };
    grid.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const swatch = target.closest<HTMLButtonElement>('[data-swatch]');
      if (swatch?.dataset.swatch && swatch.dataset.colour) {
        const slot = swatch.dataset.swatch as 'paint' | 'accent' | 'rims' | 'neon';
        const tuning = this.currentTuning();
        tuning[slot] = swatch.dataset.colour;
        if (slot === 'neon') tuning.neonEnabled = true;
        commit(`[data-swatch="${slot}"][data-colour="${swatch.dataset.colour}"]`);
        return;
      }
      const livery = target.closest<HTMLButtonElement>('[data-livery]');
      if (!livery?.dataset.livery || livery.disabled) return;
      const id = livery.dataset.livery;
      const focus = livery.classList.contains('tune-reset') ? '.livery-chip[data-livery="factory"]' : `.livery-chip[data-livery="${id}"]`;
      if (id === 'factory') {
        this.tuning.set(this.selectedCarId, cloneTuning(car().defaultTuning));
      } else if (id === 'shuffle') {
        this.tuning.set(this.selectedCarId, shuffledTuning(this.currentTuning(), car().body === 'bike', decalAllowed));
      } else {
        const preset = LIVERY_PRESETS.find((entry) => entry.id === id);
        if (!preset) return;
        applyLivery(this.currentTuning(), preset, decalAllowed);
      }
      commit(focus);
    });
    grid.addEventListener('input', (event) => {
      const input = event.target as HTMLInputElement;
      const key = input.dataset?.tune as keyof VisualTuning | undefined;
      if (!key) return;
      const tuning = this.currentTuning();
      if (input.type === 'checkbox') {
        tuning.neonEnabled = input.checked;
        commit(`input[data-tune="neonEnabled"]`);
        return;
      }
      if (key === 'stance') {
        tuning.stance = Number(input.value);
        const text = stanceText(Math.round(tuning.stance * 100));
        const output = grid.querySelector('[data-stance-output]');
        if (output) output.textContent = text;
        input.setAttribute('aria-valuetext', text);
      } else {
        (tuning as unknown as Record<string, string>)[key] = input.value;
        // Live-tint the custom swatch while the native picker stays open.
        if (input.type === 'color') input.parentElement?.style.setProperty('--swatch', input.value);
      }
      this.refreshPreview();
      this.persistDriverProfile();
    });
    grid.addEventListener('change', (event) => {
      const input = event.target as HTMLInputElement;
      // Radios and finished colour picks can move the livery highlight.
      if (input.type === 'radio' && input.dataset.tune) commit(`input[name="${input.name}"][value="${input.value}"]`);
      else if (input.type === 'color') commit();
      else if (input.dataset?.tune === 'stance') commit('#tuning-stance');
    });
    grid.addEventListener('change', (event) => {
      const input = event.target as HTMLInputElement;
      if (input.name !== 'crew-module' || !input.checked || this.importedGhostChallenge) return;
      this.driverProfile = {
        ...this.driverProfile,
        crewModule: input.value as DriverProfile['crewModule'],
        updatedAt: Date.now(),
      };
      this.persistDriverProfile();
    });
  }

  private restoreDriverProfile(): void {
    const fallback = createDefaultDriverProfile(this.driverProfile.name);
    this.selectedCarId = CAR_BY_ID.has(this.driverProfile.selectedCarId)
      ? this.driverProfile.selectedCarId
      : fallback.selectedCarId;
    this.selectedMapId = this.maps.some((map) => map.id === this.driverProfile.selectedMapId)
      ? this.driverProfile.selectedMapId
      : this.maps[0]?.id ?? '';
    this.tuning = new Map(CARS.map((car) => [
      car.id,
      cloneTuning(this.driverProfile.tuning[car.id] ?? car.defaultTuning),
    ]));
  }

  private applyGhostChallengeRoute(): void {
    const encoded = new URLSearchParams(window.location.search).get('ghost');
    const challenge = encoded
      ? decodeGhostChallenge(encoded)
      : loadStoredGhostChallenge(this.selectedMapId);
    if (encoded) {
      const clean = new URL(window.location.href);
      clean.searchParams.delete('ghost');
      this.routes.replace(`${clean.pathname}${clean.search}${clean.hash}`);
    }
    if (!encoded && !challenge) return;
    if (
      !challenge
      || !this.maps.some((map) => map.id === challenge.ghost.mapId)
      || !challenge.ghost.timeOfDay
      || !challenge.ghost.weather
    ) {
      if (!encoded && challenge) clearStoredGhostChallenge(challenge.ghost.mapId);
      this.startupNotice = { message: t('This ghost challenge link is damaged or uses an unavailable track'), type: 'error' };
      return;
    }
    const persisted = encoded ? storeGhostChallenge(challenge) : true;
    this.importedGhostChallenge = challenge;
    this.selectedMapId = challenge.ghost.mapId;
    if (CAR_BY_ID.has(challenge.ghost.carId)) this.selectedCarId = challenge.ghost.carId;
    this.driverProfile = {
      ...this.driverProfile,
      selectedMapId: this.selectedMapId,
      selectedCarId: this.selectedCarId,
      crewModule: challenge.ghost.crewModule,
      timeOfDay: challenge.ghost.timeOfDay,
      weather: challenge.ghost.weather,
      ...GHOST_CHALLENGE_RACE_RULES,
      updatedAt: Date.now(),
    };
    saveDriverProfile(this.driverProfile);
    this.startupNotice = {
      message: persisted
        ? t('Ghost challenge loaded // Beat {name}', { name: challenge.challenger })
        : t('Ghost loaded temporarily // Beat {name} · Browser storage unavailable', { name: challenge.challenger }),
      type: 'info',
    };
  }

  /** Saves the driver: callsign, car, livery, crew module and lobby defaults. */
  private persistDriverProfile(name = this.driverProfile.name): void {
    const crewModule = this.root.querySelector<HTMLInputElement>('input[name="crew-module"]:checked')?.value as DriverProfile['crewModule'] | undefined;
    this.driverProfile = {
      ...this.driverProfile,
      version: 1,
      name: name.trim().slice(0, 18),
      selectedCarId: this.selectedCarId,
      selectedMapId: this.selectedMapId,
      crewModule: crewModule ?? this.driverProfile.crewModule,
      tuning: Object.fromEntries(CARS.map((car) => [
        car.id,
        cloneTuning(this.tuning.get(car.id) ?? car.defaultTuning),
      ])),
      updatedAt: Date.now(),
    };
    saveDriverProfile(this.driverProfile);
  }

  private async resumeRecentRace(): Promise<void> {
    const continuation = this.preferredContinuation();
    const button = this.root.querySelector<HTMLButtonElement>('#continue-race');
    // Report next to the save card the player just tapped, not at the far end
    // of the setup form.
    const errorOutput = this.root.querySelector<HTMLElement>('#continue-race-status') ?? mustElement('#form-error');
    if (!continuation || !button) {
      errorOutput.textContent = t('That local race is no longer available.');
      return;
    }
    if (continuation.kind === 'offline') {
      await this.resumeOfflineRace(continuation.race, button, errorOutput);
      return;
    }
    const saved = loadRecentRaceSession();
    if (!saved) {
      errorOutput.textContent = t('That local race is no longer available.');
      return;
    }
    const action = this.beginGarageAction('resume');
    button.disabled = true;
    button.textContent = t('CHECKING…');
    errorOutput.textContent = '';
    let confirmedFinished = false;
    try {
      const room = await getRoom(saved.roomId, saved.inviteToken);
      if (!this.isGarageActionCurrent(action)) return;
      if (room.phase === 'finished') {
        confirmedFinished = true;
        throw new Error('That race has already finished. Create a fresh one below.');
      }
      this.room = room;
      this.player = saved.player;
      this.inviteToken = saved.inviteToken;
      this.selectedMapId = room.settings.mapId;
      const updated = updateRaceSession(room.id, {
        mapId: room.settings.mapId,
        mapName: this.maps.find((map) => map.id === room.settings.mapId)?.name ?? saved.mapName,
        phase: room.phase,
        resumeWindowMs: resumeWindowFromSessionTtl(room.sessionTtlMs) ?? saved.resumeWindowMs,
      });
      this.recentRace = updated ?? saved;
      this.routes.pushRace(room.id, saved.inviteToken);
      this.connect(room.id, saved.player);
    } catch (error) {
      if (!this.isGarageActionCurrent(action)) return;
      const unavailable = confirmedFinished || isUnavailableRaceError(error);
      if (unavailable) {
        forgetRaceSession(saved.roomId);
        this.recentRace = null;
        // Surfaces an offline checkpoint that the dead online pass outranked.
        this.refreshGarageContinuation();
      } else {
        button.disabled = false;
        button.innerHTML = `${t('CONTINUE')} <b>↗</b>`;
      }
      errorOutput.textContent = confirmedFinished && error instanceof Error
        ? t(error.message)
        : unavailable
          ? t('That race has ended or expired. Create a fresh one below.')
          : t('Race control could not be reached. Your local save is safe—try again.');
      this.finishGarageAction(action);
    }
  }

  private async resumeOfflineRace(
    saved: StoredOfflineRace,
    button: HTMLButtonElement,
    errorOutput: HTMLElement,
  ): Promise<void> {
    const action = this.beginGarageAction('resume');
    button.disabled = true;
    button.textContent = t('RESTORING…');
    errorOutput.textContent = '';
    try {
      const generation = this.runtimeGeneration;
      const { restoreLocalRaceSession } = await this.loadLocalRaceModule();
      if (!this.isGarageActionCurrent(action) || generation !== this.runtimeGeneration) return;
      // A checkpoint belongs to the exact geometry it was simulated against.
      // Keep that embedded map for this resumed run; freshly started races use
      // the newly downloaded map catalogue after the update check above.
      const savedMap = saved.setup.map;
      const session = restoreLocalRaceSession(
        saved.setup,
        saved.checkpoint,
        this.transportCallbacks('offline', true),
        this.localRacePerformanceOptions(),
      );
      this.activateOfflineSession(session, savedMap);
      this.toast(t('Offline race restored from this device'), 'info', { quiet: true });
    } catch (error) {
      if (!this.isGarageActionCurrent(action)) return;
      void this.clearOfflineCheckpoint(false, saved.setup.roomId);
      this.removeContinuationUi();
      errorOutput.textContent = error instanceof Error
        ? t('Saved offline race could not be restored: {reason}', { reason: t(error.message) })
        : t('Saved offline race could not be restored.');
      this.finishGarageAction(action);
    }
  }

  private beginGarageAction(kind: GarageAction): number {
    return this.garageActions.begin(kind);
  }

  private isGarageActionCurrent(generation: number): boolean {
    return this.garageActions.isCurrent(generation);
  }

  private finishGarageAction(generation: number): void {
    this.garageActions.finish(generation);
  }

  private cancelGarageAction(): void {
    this.garageActions.cancel();
  }

  private setGarageActionButtonsDisabled(disabled: boolean): void {
    for (const selector of ['.launch-button', '#continue-race', '#watch-race', '#start-night-tour', '#start-nemesis-race', '#surprise-me', '#start-daily-challenge', '[data-tour-id]', '[data-watch-lobby]', '[data-join-lobby]']) {
      for (const button of this.root.querySelectorAll<HTMLButtonElement>(selector)) {
        button.disabled = disabled;
      }
    }
  }

  private previewFrame: number | undefined;
  private refreshPreview(): void {
    if (this.previewFrame !== undefined) cancelAnimationFrame(this.previewFrame);
    this.previewFrame = requestAnimationFrame(() => {
      this.garage?.show(this.selectedCarId, this.currentTuning());
      this.previewFrame = undefined;
    });
  }

  private currentTuning(): VisualTuning {
    const value = this.tuning.get(this.selectedCarId);
    if (value) return value;
    const fallback = cloneTuning(CAR_BY_ID.get(this.selectedCarId)?.defaultTuning ?? CARS[0]!.defaultTuning);
    this.tuning.set(this.selectedCarId, fallback);
    return fallback;
  }

  private startSurpriseEventFromGarage(): void {
    const seed = `${new Date().toISOString().slice(0, 10)}:${this.progression.races}:${this.quickEventSequence++}:${this.selectedMapId}`;
    const event = quickEventFor(this.maps, seed, this.selectedMapId, this.resolvedNpcDifficulty());
    if (!event) {
      this.showMenuError(t('No quick-event district is available.'));
      return;
    }
    const nemesis = currentNemesis(this.progression);
    this.openOfflineEventLobby({
      name: this.driverProfile.name,
      mapId: event.map.id,
      npcCount: event.npcCount,
      npcDifficulty: event.npcDifficulty,
      antiCheat: false,
      collisions: true,
      traffic: event.traffic,
      timeOfDay: event.timeOfDay,
      weather: event.weather,
      timeTrial: false,
      eventMode: event.eventMode,
      crewModule: this.driverProfile.crewModule,
      featuredRival: nemesis?.record.name,
      carId: this.selectedCarId,
      tuning: cloneTuning(this.currentTuning()),
    }, { kicker: t('QUICK EVENT'), title: t('SURPRISE ME') });
  }

  private startDailyChallenge(): void {
    const challenge = this.currentDailyChallenge();
    const map = challenge ? this.maps.find((candidate) => candidate.id === challenge.mapId) : null;
    const car = challenge ? CAR_BY_ID.get(challenge.carId) : null;
    if (!challenge || !map || !car) {
      this.showMenuError(t('Today’s challenge is not available in this cache.'));
      return;
    }
    this.openOfflineEventLobby({
      name: this.driverProfile.name,
      mapId: map.id,
      npcCount: 0,
      npcDifficulty: 'sport',
      antiCheat: false,
      collisions: false,
      traffic: false,
      timeOfDay: challenge.timeOfDay,
      weather: challenge.weather,
      timeTrial: true,
      eventMode: 'time-trial',
      arcadePreset: 'standard',
      crewModule: this.driverProfile.crewModule,
      dailyChallengeId: challenge.id,
      signatureActive: false,
      carId: car.id,
      tuning: cloneTuning(this.tuning.get(car.id) ?? car.defaultTuning),
    }, { kicker: t('DAILY // {id}', { id: challenge.id }), title: t('{car} TIME ATTACK', { car: car.name.toUpperCase() }) }, { forcedCarId: car.id, locked: true });
  }

  private startGhostChallenge(): void {
    const challenge = this.importedGhostChallenge;
    const ghost = challenge?.ghost;
    const map = ghost ? this.maps.find((candidate) => candidate.id === ghost.mapId) : null;
    const car = ghost ? CAR_BY_ID.get(ghost.carId) : null;
    if (!challenge || !ghost || !map || !car || !ghost.timeOfDay || !ghost.weather) {
      this.showMenuError(t('This ghost challenge is not available on this build.'));
      return;
    }
    this.openOfflineEventLobby({
      name: this.driverProfile.name,
      mapId: map.id,
      npcCount: 0,
      npcDifficulty: 'sport',
      antiCheat: GHOST_CHALLENGE_RACE_RULES.antiCheat,
      collisions: GHOST_CHALLENGE_RACE_RULES.collisions,
      traffic: GHOST_CHALLENGE_RACE_RULES.traffic,
      timeOfDay: ghost.timeOfDay,
      weather: ghost.weather,
      timeTrial: true,
      eventMode: GHOST_CHALLENGE_RACE_RULES.eventMode,
      arcadePreset: GHOST_CHALLENGE_RACE_RULES.arcadePreset,
      crewModule: ghost.crewModule,
      dailyChallengeId: ghost.dailyChallengeId,
      signatureActive: false,
      carId: car.id,
      tuning: cloneTuning(this.tuning.get(car.id) ?? car.defaultTuning),
    }, { kicker: t('GHOST CHALLENGE'), title: t('BEAT {name}', { name: challenge.challenger.toUpperCase() }) }, { forcedCarId: car.id, locked: true });
  }

  private startNemesisRaceFromGarage(): void {
    const nemesis = currentNemesis(this.progression);
    const map = this.preferredLobbyMap();
    if (!nemesis || !map) return;
    const preference = this.driverProfile.npcDifficulty;
    const difficulty = preference === 'adaptive'
      ? adaptiveNpcDifficulty(this.progression)
      : preference === 'rookie' ? 'sport' : preference;
    this.openOfflineEventLobby({
      name: this.driverProfile.name,
      mapId: map.id,
      npcCount: 5,
      npcDifficulty: difficulty,
      antiCheat: false,
      collisions: true,
      traffic: this.driverProfile.traffic,
      timeOfDay: this.driverProfile.timeOfDay === 'map' ? map.timeOfDay : this.driverProfile.timeOfDay,
      weather: this.driverProfile.weather === 'map' ? map.weather : this.driverProfile.weather,
      timeTrial: false,
      eventMode: 'sprint',
      crewModule: this.driverProfile.crewModule,
      featuredRival: nemesis.record.name,
      carId: this.selectedCarId,
      tuning: cloneTuning(this.currentTuning()),
    }, { kicker: t('NEMESIS REVENGE'), title: t('{name} IS WAITING', { name: nemesis.record.name.toUpperCase() }) });
  }

  private startNightTourFromGarage(): void {
    const selectedTourId = this.nightTour.tourId;
    const durableTour = loadNightTour();
    const previousTour = durableTour.tourId === selectedTourId
      ? durableTour
      : selectNightTour(durableTour, selectedTourId);
    const pendingTour = nightTourStateForStart(previousTour);
    const event = currentNightTourEvent(pendingTour);
    const map = event ? this.maps.find((candidate) => candidate.id === event.mapId) : null;
    if (!event || !map) {
      this.showMenuError(t('The next NIGHT TOUR district is unavailable in this build.'));
      return;
    }
    const definition = nightTourDefinition(pendingTour.tourId);
    this.openOfflineEventLobby({
      name: this.driverProfile.name,
      mapId: map.id,
      npcCount: 5,
      npcDifficulty: event.npcDifficulty,
      antiCheat: false,
      collisions: true,
      traffic: event.traffic,
      timeOfDay: event.timeOfDay,
      weather: event.weather,
      timeTrial: false,
      eventMode: event.eventMode,
      crewModule: this.driverProfile.crewModule,
      tourId: pendingTour.tourId,
      tourStage: pendingTour.eventIndex,
      featuredRival: event.featuredRival,
      carId: this.selectedCarId,
      tuning: cloneTuning(this.currentTuning()),
    }, {
      kicker: t('NIGHT TOUR // {tour} · STAGE {stage}', { tour: definition.name, stage: pendingTour.eventIndex + 1 }),
      title: t(event.title),
    }, { pendingTour });
  }

  private connect(roomId: string, player: PlayerIdentity): void {
    this.prepareRaceRuntime(player, false, roomId, this.room?.raceAttempt ?? 1);
    this.connectionRoomId = roomId;
    this.connection = new RaceConnection({
      roomId,
      player,
      ...this.transportCallbacks(roomId, false),
    });
  }

  private async startOfflineRace(payload: CreateRoomRequest, isCurrent?: () => boolean): Promise<boolean> {
    const map = this.maps.find((candidate) => candidate.id === payload.mapId);
    if (!map) throw new Error('The selected map is not available in the offline cache.');
    const generation = this.runtimeGeneration;
    const { createLocalRaceSession } = await this.loadLocalRaceModule();
    if (generation !== this.runtimeGeneration || (isCurrent && !isCurrent())) return false;
    this.connection = null;
    await this.clearOfflineCheckpoint(true);
    if (generation !== this.runtimeGeneration || (isCurrent && !isCurrent())) return false;
    const session = createLocalRaceSession(payload, map, this.transportCallbacks('offline', true), {
      ...this.localRacePerformanceOptions(),
    });
    try {
      this.activateOfflineSession(session, map);
      return true;
    } catch (error) {
      session.transport.close();
      throw error;
    }
  }

  private localRacePerformanceOptions(): {
    snapshotIntervalMs: number;
    onCheckpoint: (setup: StoredOfflineRace['setup'], checkpoint: StoredOfflineRace['checkpoint'], force?: boolean) => void;
  } {
    return {
      snapshotIntervalMs: shouldStartWithConservativeFrameRate(this.gameSettings, this.deviceCapabilities)
        ? 1_000 / 12
        : 50,
      onCheckpoint: (setup, checkpoint, force) => {
        if (!this.benchmarkRequest) this.queueOfflineCheckpoint(setup, checkpoint, force === true);
      },
    };
  }

  private activateOfflineSession(
    session: LocalRaceSession,
    map: MapDefinition,
  ): void {
    this.room = session.room;
    this.connectionRoomId = 'offline';
    this.inviteToken = '';
    this.inviteUnavailableMessage = '';
    this.selectedMapId = map.id;
    this.selectedCarId = session.player.carId;
    this.routes.pushHome();
    this.prepareRaceRuntime(
      session.player,
      true,
      session.room.id,
      session.room.raceAttempt ?? 1,
    );
    this.connection = session.transport;
  }

  private async startMobileBenchmark(): Promise<void> {
    const request = this.benchmarkRequest;
    if (!request) return;
    const map = this.maps.find((candidate) => candidate.id === request.mapId)
      ?? this.maps.find((candidate) => candidate.timeOfDay === 'night' && candidate.weather === 'rain')
      ?? this.maps[0];
    const car = CAR_BY_ID.get(this.selectedCarId) ?? CARS[0];
    if (!map || !car) throw new Error('Benchmark route or vehicle is unavailable.');
    this.benchmarkOriginalSettings = { ...this.gameSettings };
    this.gameSettings = {
      ...this.gameSettings,
      graphicsQuality: 'low',
      frameRateTarget: 'smooth',
      adaptivePerformance: true,
      bloom: false,
      shadows: false,
    };
    this.selectedMapId = map.id;
    this.selectedCarId = car.id;
    this.benchmarkRecorder = new MobileBenchmarkRecorder(request, navigator.userAgent);
    this.benchmarkAutoStarted = false;
    await this.startOfflineRace({
      name: 'BENCHMARK',
      mapId: map.id,
      npcCount: request.npcCount,
      npcDifficulty: 'sport',
      antiCheat: false,
      collisions: true,
      traffic: true,
      timeOfDay: request.timeOfDay,
      weather: request.weather,
      timeTrial: false,
      carId: car.id,
      tuning: cloneTuning(this.tuning.get(car.id) ?? car.defaultTuning),
    });
  }

  private transportCallbacks(
    roomId: string,
    local: boolean,
  ): Omit<RaceConnectionOptions, 'roomId' | 'player'> {
    return {
      onMessage: (message) => this.onServerMessage(message),
      onStatus: (status) => this.setConnectionStatus(status),
      onTerminalClose: (code, reason) => {
        if (local) return;
        forgetRaceSession(roomId);
        this.recentRace = null;
        if (code === SOCKET_CLOSE_KICKED || code === SOCKET_CLOSE_BANNED) {
          this.startupNotice = { message: reason ? t(reason) : t('The host removed you from the lobby'), type: 'error' };
          this.goHome();
          return;
        }
        this.renderFatal(reason ? t(reason) : t('The saved race has expired or its reconnect pass is no longer valid.'));
      },
      onLatency: (latencyMs) => {
        // Smooth the 2 s probe so the HUD reads a steady figure, not noise.
        this.latencyMs = this.latencyMs === null ? latencyMs : this.latencyMs * 0.55 + latencyMs * 0.45;
        this.renderConnectionStatus();
      },
      onProtocolMismatch: () => {
        this.renderUpdating();
        void this.pwa.recoverProtocolMismatch();
      },
      onClientError: (error) => {
        this.renderFatal(local
          ? t('The offline simulation stopped: {reason}', { reason: error.message })
          : t('The race client could not process live data: {reason}', { reason: error.message }));
      },
      diagnostics: this.diagnostics,
    };
  }

  private prepareRaceRuntime(
    player: PlayerIdentity,
    local: boolean,
    roomId: string,
    attempt = 1,
  ): void {
    this.closeInviteShare(false);
    this.stopInvitePolling();
    this.stopLobbyBrowser();
    this.disposeMenuListeners();
    this.lobbyPanel?.destroy();
    this.lobbyPanel = null;
    this.lobbyGarageOpen = false;
    this.lobbyRacers = [];
    this.memberPings.clear();
    this.engineSignature = '';
    this.returningToLobby = false;
    this.discardedOfflineRoomId = '';
    this.detachRaceUiListeners();
    this.stopRaceEngine();
    window.removeEventListener('online', this.onConnectivityChange);
    window.removeEventListener('offline', this.onConnectivityChange);
    this.connection = null;
    this.raceRuntime.begin(roomId, local ? 'offline' : 'online', attempt);
    this.runtimeGeneration += 1;
    this.garageActions.invalidate();
    this.garage?.destroy();
    this.garage = null;
    this.player = player;
    this.inputSequence = 0;
    this.lastPhase = '';
    this.lastRaceSaveAt = 0;
    this.lastPersistedRacePhase = null;
    this.lastStyleEventId = -1;
    this.compactCueGate.reset();
    this.latestSnapshot = null;
    this.lastLocalRacer = null;
    this.currentGhostStatus = null;
    this.raceRun = createRaceRunStats();
    this.progressionUpdate = null;
    this.vehicleMasteryUpdate = null;
    this.careerCommitGeneration += 1;
    this.careerBankPromise = null;
    this.raceUiBound = false;
    this.raceDirector = null;
    this.activeRaceMap = null;
    this.raceEngineReady = false;
    this.playerReady = false;
    this.root.innerHTML = raceShell({ diagnostics: this.diagnosticsEnabled });
    document.documentElement.classList.add('race-mounted');
    document.body.classList.add('race-mounted');
    this.raceUiElements = captureRaceUiElements();
    this.lastTelemetryUiAt = 0;
    this.lastMinimapUiAt = 0;
    this.lastLeaderboardSignature = '';
    this.lastGhostUiAt = 0;
    this.lastGhostSectorsSignature = '';
    this.lastGhostPersistence = 'idle';
    const stage = mustElement('#race-stage');
    stage.classList.add('engine-loading');
    stage.classList.toggle('touch-device', this.deviceCapabilities.touchDevice);
    stage.classList.toggle('phone-device', this.deviceCapabilities.phone);
    stage.classList.toggle('tablet-device', this.deviceCapabilities.tablet);
    stage.classList.toggle('motion-capable', this.deviceCapabilities.motionSteering);
    stage.classList.toggle('local-race', local);
    stage.classList.toggle('benchmark-race', this.benchmarkRequest !== null);
    const benchmarkPanel = this.root.querySelector<HTMLElement>('#benchmark-panel');
    if (benchmarkPanel && this.benchmarkRequest) {
      benchmarkPanel.hidden = false;
      setText(mustElement('#benchmark-scenario'), `${this.benchmarkRequest.npcCount} NPC · NIGHT + RAIN · ${Math.round(this.benchmarkRequest.durationMs / 1_000)} S`);
    }
    this.connectionState = local ? 'local' : 'connecting';
    this.renderConnectionStatus();
  }

  private onServerMessage(message: ServerMessage): void {
    if (message.type === 'latency') {
      this.memberPings.clear();
      for (const [playerId, ping] of Object.entries(message.pings)) this.memberPings.set(playerId, ping);
      this.syncLobbyUi();
      return;
    }
    if (message.type === 'error') {
      if (this.restartPending) this.setRestartPending(false);
      this.toast(t(message.message), 'error');
      return;
    }
    if (message.type === 'connection-replaced') {
      this.renderTakenOver();
      return;
    }
    if (message.type === 'welcome') {
      const previousMapId = this.room?.settings.mapId;
      const attempt = resolveRaceAttempt(this.raceAttempt, message.room.raceAttempt, this.restartPending);
      this.raceRuntime.lifecycle.observeAuthoritative(message.room.phase, attempt.attempt);
      this.room = message.room;
      this.activeRaceMap = message.map;
      if (attempt.restartAccepted) this.acceptAuthoritativeRestart();
      else if (attempt.clearPending) this.setRestartPending(false);
      this.isHost = message.isHost;
      this.playerReady = message.room.members?.find((member) => member.id === message.playerId)?.ready ?? false;
      this.lobbyRacers = message.racers;
      const localRacer = message.racers.find((racer) => racer.id === message.playerId);
      // A restored offline checkpoint keeps the sequence it had acknowledged;
      // a fresh page counts from zero and every input would be dropped as
      // stale until it caught up, leaving the car parked on the grid.
      if (localRacer && localRacer.lastProcessedInput > this.inputSequence) {
        this.inputSequence = localRacer.lastProcessedInput;
      }
      if (this.player && localRacer) {
        this.player = { ...this.player, carId: localRacer.carId, tuning: cloneTuning(localRacer.tuning) };
      }
      if (previousMapId !== message.map.id) this.createRaceDirector(message.map);
      this.persistCurrentRace(message.room.phase, true, message.map.name);
      const signature = engineSignatureFor(message);
      const canResyncLive = Boolean(
        this.game
        && this.raceUiBound
        && this.game.map.id === message.map.id
        && this.game.playerId === message.playerId
        && signature === this.engineSignature,
      );
      const localResume = message.room.phase === 'racing'
        && message.room.settings.antiCheat !== true
        && this.lastLocalRacer?.id === message.playerId
        ? this.lastLocalRacer
        : null;
      if (localResume) {
        this.connection?.send({
          type: 'resume',
          state: resumeStateFromRacer(localResume, this.inputSequence),
        });
      }
      if (this.raceUiBound) {
        this.syncRaceUi(message.map, message.racers);
      } else {
        this.bindRaceUi(message.map, message.racers);
        this.raceUiBound = true;
      }
      if (canResyncLive) {
        this.game?.refreshRacers(message.racers);
        this.game?.setNetworkAvailable(true);
      } else {
        // A lobby edit (track, weather, laps, own car) rebuilds the track
        // behind the lobby. Rapid host clicks coalesce into one rebuild.
        const rebuildingLobby = this.game !== null && message.room.phase === 'lobby';
        this.game = null;
        this.raceEngineReady = false;
        this.engineSignature = signature;
        if (this.engineRemountTimer !== undefined) window.clearTimeout(this.engineRemountTimer);
        this.engineRemountTimer = undefined;
        const generation = this.runtimeGeneration;
        if (rebuildingLobby) {
          this.engineRemountTimer = window.setTimeout(() => {
            this.engineRemountTimer = undefined;
            if (generation === this.runtimeGeneration) void this.mountRaceEngine(message, generation);
          }, 450);
        } else {
          void this.mountRaceEngine(message, generation);
        }
      }
      this.syncLobbyUi();
      return;
    }
    if (message.type === 'room-state') {
      if (message.room.phase === 'lobby') {
        const wasReady = new Set((this.room?.members ?? []).filter((member) => member.ready).map((member) => member.id));
        const newlyReady = (message.room.members ?? []).some((member) => (
          member.ready && member.id !== this.player?.id && !wasReady.has(member.id)
        ));
        if (newlyReady && this.room?.phase === 'lobby') this.uiSounds.playerReady();
      }
      const attempt = resolveRaceAttempt(this.raceAttempt, message.room.raceAttempt, this.restartPending);
      this.raceRuntime.lifecycle.observeAuthoritative(message.room.phase, attempt.attempt);
      this.room = message.room;
      if (attempt.restartAccepted) this.acceptAuthoritativeRestart();
      else if (attempt.clearPending) this.setRestartPending(false);
      this.isHost = message.room.hostPlayerId === this.player?.id;
      this.playerReady = message.room.members?.find((member) => member.id === this.player?.id)?.ready ?? false;
      this.syncLobbyUi();
      return;
    }
    if (message.type === 'snapshot') {
      this.raceRuntime.lifecycle.observeAuthoritative(message.phase, this.raceAttempt);
      this.latestSnapshot = message;
      if (this.room && this.room.phase !== message.phase) this.room = { ...this.room, phase: message.phase };
      this.persistCurrentRace(message.phase);
      // Ghost status is synchronous and must observe the authoritative final
      // lap before career medals and best-lap progress are banked.
      this.game?.setCarCollisions(this.room?.settings.collisions !== false);
      this.game?.updateSnapshot(message);
      this.watchRaceEnginePreparation();
      if (message.phase === 'finished' && this.lastPhase !== 'finished') {
        const finalRacer = message.racers.find((racer) => racer.id === this.player?.id);
        if (finalRacer) this.raceRun = observeRaceRun(this.raceRun, finalRacer);
        this.bankFinishedRace(message);
      }
      this.updateRaceUi(message);
      // Final ghost processing and banking happen synchronously above before
      // the checkpoint is removed or a waiting service worker may reload.
      if (this.localRace && message.phase === 'finished') {
        void this.clearOfflineCheckpoint(false, this.room?.id, this.raceAttempt);
      }
      return;
    }
    if (message.type === 'post-race-vote') return;
    if (message.type === 'party-transfer') {
      this.acceptPartyTransfer(message.room, message.inviteToken);
      return;
    }
    if (message.type === 'host-changed') {
      if (this.room) this.room = { ...this.room, hostPlayerId: message.hostPlayerId };
      this.isHost = message.hostPlayerId === this.player?.id;
      this.syncHostUi();
      this.toast(this.isHost ? t('You are now the host — the lobby controls are yours') : t('The host role moved to another driver'), 'info');
      this.syncLobbyUi();
    } else if (message.type === 'player-joined') {
      this.toast(t('{name} joined the lobby', { name: message.racer.name }), 'info');
      if (message.racer.id !== this.player?.id) this.uiSounds.playerJoined();
    } else if (message.type === 'player-left') {
      this.memberPings.delete(message.playerId);
      const phase = this.latestSnapshot?.phase ?? this.room?.phase;
      if (phase === 'lobby') this.uiSounds.playerLeft();
      const name = this.room?.members?.find((member) => member.id === message.playerId)?.name;
      this.toast(phase === 'lobby'
        ? (name ? t('{name} left the lobby', { name }) : t('A driver left the lobby'))
        : (name
          ? t('{name} lost connection — their car is held for a reconnect', { name })
          : t('A driver lost connection — their car is held for a reconnect')), 'info');
    }
  }

  private async mountRaceEngine(
    message: Extract<ServerMessage, { type: 'welcome' }>,
    generation: number,
  ): Promise<void> {
    // Serialise concurrent mounts: a second welcome arriving while the engine
    // chunk is still loading must not construct a second RaceGame on top.
    const mountId = ++this.mountSequence;
    try {
      const { RaceGame: RaceGameClass } = await this.loadRaceGameModule();
      const stage = this.root.querySelector<HTMLElement>('#race-stage');
      if (!stage || generation !== this.runtimeGeneration || mountId !== this.mountSequence) return;
      const game = new RaceGameClass({
        container: stage,
        map: message.map,
        playerId: message.playerId,
        initialRacers: message.racers,
        localCrewModule: this.player?.crewModule ?? this.driverProfile.crewModule,
        initialSettings: this.gameSettings,
        capabilities: this.deviceCapabilities,
        ambientTraffic: message.room.settings.traffic === true,
        onLocalUpdate: (racer) => {
          this.lastLocalRacer = racer;
          this.updateTelemetryUi(racer);
        },
        onMotionStatus: (status) => this.updateMotionStatus(status),
        onPerformanceStatus: (status) => this.updatePerformanceStatus(status),
        onGamepadStatus: (status) => this.updateGamepadStatus(status),
        onGamepadMenu: () => this.toggleSettings(),
        onGhostStatus: (status) => this.updateGhostStatus(status),
        onMotionReading: (reading) => this.updateMotionReading(reading),
        onRendererRecovery: (status) => this.updateRendererRecovery(status),
        onCameraModeChange: (mode) => this.syncCameraMode(mode),
        raceLaps: raceLapCount(message.room.settings, message.map.laps),
        ghostChallenge: this.importedGhostChallenge
          && normaliseArcadePreset(message.room.settings.arcadePreset) === 'standard'
          && ghostMatchesRaceSetup(
            this.importedGhostChallenge.ghost,
            message.map.id,
            {
              carId: this.player?.carId ?? '',
              crewModule: this.player?.crewModule ?? this.driverProfile.crewModule,
              timeOfDay: message.map.timeOfDay,
              weather: message.map.weather,
              dailyChallengeId: message.room.settings.dailyChallengeId,
            },
          )
          ? this.importedGhostChallenge
          : null,
        arcadePreset: normaliseArcadePreset(message.room.settings.arcadePreset),
        recordLapGhost: lapGhostRecordingEligible(message.room.settings),
        dailyChallengeId: message.room.settings.dailyChallengeId,
      });
      this.game = game;
      this.syncCameraMode(game.cameraModeLabel);
      if (this.latestSnapshot) game.updateSnapshot(this.latestSnapshot);
      game.setNetworkAvailable(this.connectionState === 'open' || this.connectionState === 'local');
      this.raceEngineReady = false;
      this.syncHostUi();
      await game.prepare();
      if (generation !== this.runtimeGeneration || this.game !== game || mountId !== this.mountSequence) return;
      this.raceEngineReady = true;
      this.syncHostUi();
      stage.classList.remove('engine-loading');
      this.applySettings();
      void this.initialiseMotionSteering();
      this.startInputLoop();
      if (this.benchmarkRequest && !this.benchmarkAutoStarted && message.room.phase === 'lobby') {
        this.benchmarkAutoStarted = true;
        this.connection?.send({ type: 'start' });
      } else {
        this.scheduleOfflineAutoLaunch();
      }
    } catch (error) {
      if (generation !== this.runtimeGeneration || mountId !== this.mountSequence) return;
      this.renderFatal(error instanceof Error ? t(error.message) : t('Unable to initialise the 3D race engine.'));
    }
  }

  private bindRaceUi(map: MapDefinition, racers: RacerSnapshot[]): void {
    this.createRaceDirector(map);
    this.syncRaceUi(map, racers);
    mustElement<HTMLButtonElement>('#invite-share-close').addEventListener('click', () => this.closeInviteShare());
    mustElement<HTMLButtonElement>('#invite-copy-link').addEventListener('click', () => void this.copyInvite());
    mustElement<HTMLButtonElement>('#invite-native-share').addEventListener('click', () => void this.nativeShareInvite());
    mustElement<HTMLInputElement>('#invite-share-link').addEventListener('click', (event) => {
      (event.currentTarget as HTMLInputElement).select();
    });
    mustElement<HTMLElement>('#invite-share-overlay').addEventListener('click', (event) => {
      if (event.target === event.currentTarget) this.closeInviteShare();
    });
    mustElement<HTMLButtonElement>('#results-restart').addEventListener('click', () => this.restartSoloRace());
    mustElement<HTMLButtonElement>('#results-share-ghost').addEventListener('click', () => this.openGhostChallengeShare());
    mustElement<HTMLButtonElement>('#results-next').addEventListener('click', () => this.startNextEvent());
    mustElement<HTMLButtonElement>('#results-lobby').addEventListener('click', () => this.backToLobbyFromResults());
    mustElement<HTMLButtonElement>('#results-leave').addEventListener('click', () => this.leaveSession());
    mustElement<HTMLButtonElement>('#time-trial-restart').addEventListener('click', () => this.restartSoloRace());
    mustElement<HTMLButtonElement>('#exit-race').addEventListener('click', () => this.leaveSession());
    mustElement<HTMLButtonElement>('#settings-quit').addEventListener('click', () => this.leaveSession());
    mustElement<HTMLButtonElement>('#settings-restart').addEventListener('click', () => {
      if (!this.canRestartFromPause()) return;
      this.toggleSettings(false);
      this.restartSoloRace();
    });
    mustElement<HTMLButtonElement>('#sound-toggle').addEventListener('click', () => this.game?.toggleSound());
    mustElement<HTMLButtonElement>('#settings-toggle').addEventListener('click', () => this.toggleSettings(true));
    this.root.querySelector<HTMLButtonElement>('#diagnostics-download')?.addEventListener('click', () => {
      try {
        downloadDiagnosticReport(this.diagnosticReport());
      } catch {
        this.toast(t('Could not download diagnostics'), 'error');
      }
    });
    mustElement<HTMLButtonElement>('#settings-close').addEventListener('click', () => this.toggleSettings(false));
    mustElement<HTMLButtonElement>('#settings-resume').addEventListener('click', () => this.toggleSettings(false));
    mustElement<HTMLButtonElement>('#mobile-camera').addEventListener('click', () => this.game?.cycleCamera());
    mustElement<HTMLButtonElement>('#mobile-cockpit').addEventListener('click', () => this.game?.toggleCockpitView());
    mustElement<HTMLButtonElement>('#motion-steering-indicator').addEventListener('click', () => void this.toggleMotionSteering());
    mustElement<HTMLButtonElement>('#motion-toggle').addEventListener('click', () => void this.toggleMotionSteering());
    mustElement<HTMLButtonElement>('#motion-permission-enable').addEventListener('click', () => void this.enableMotionSteering());
    mustElement<HTMLButtonElement>('#motion-permission-touch').addEventListener('click', () => this.useTouchSteering());
    mustElement<HTMLButtonElement>('#motion-calibrate').addEventListener('click', () => this.startTiltSetup());
    mustElement<HTMLButtonElement>('#immersive-mode').addEventListener('click', () => void this.enterImmersiveMode());
    mustElement<HTMLButtonElement>('#landscape-fullscreen').addEventListener('click', () => void this.enterImmersiveMode());
    mustElement<HTMLButtonElement>('#settings-reset').addEventListener('click', () => this.resetSettings());
    for (const button of this.root.querySelectorAll<HTMLButtonElement>('.settings-language [data-set-language]')) {
      button.addEventListener('click', () => this.switchRaceLanguage(button.dataset.setLanguage as Locale));
    }
    this.controlsPanel = new ControlsSettingsPanel({
      root: this.root,
      settings: () => this.gameSettings,
      applySettings: () => this.applySettings(),
      beginCapture: (kind) => this.game?.beginGamepadCapture(kind) ?? null,
      cancelCapture: () => this.game?.cancelGamepadCapture(),
      setMapping: (mapping) => this.game?.setGamepadMapping(mapping),
      resetMapping: () => this.game?.resetGamepadMapping(),
      previewRumble: (strength) => this.game?.previewRumble(strength),
      inputFamily: () => this.gamepadUi.family,
      toast: (message) => this.toast(message, 'info'),
      onBindingsChanged: () => this.refreshInputGlyphs(),
    });
    this.controlsPanel.bind();
    this.controlsPanel.updateStatus(this.gamepadStatus);
    this.refreshInputGlyphs();
    mustElement<HTMLSelectElement>('#graphics-quality').addEventListener('change', (event) => {
      this.setGraphicsQuality((event.currentTarget as HTMLSelectElement).value as GraphicsQuality);
    });
    mustElement<HTMLInputElement>('#graphics-screen-space').addEventListener('change', (event) => {
      this.gameSettings.screenSpaceLighting = (event.currentTarget as HTMLInputElement).checked;
      this.applySettings();
    });
    mustElement<HTMLInputElement>('#graphics-car-reflections').addEventListener('change', (event) => {
      this.gameSettings.carReflections = (event.currentTarget as HTMLInputElement).checked;
      this.applySettings();
    });
    mustElement<HTMLInputElement>('#graphics-bloom').addEventListener('change', (event) => {
      this.gameSettings.bloom = (event.currentTarget as HTMLInputElement).checked;
      this.applySettings();
    });
    mustElement<HTMLInputElement>('#graphics-shadows').addEventListener('change', (event) => {
      this.gameSettings.shadows = (event.currentTarget as HTMLInputElement).checked;
      this.applySettings();
    });
    mustElement<HTMLInputElement>('#graphics-adaptive').addEventListener('change', (event) => {
      this.gameSettings.adaptivePerformance = (event.currentTarget as HTMLInputElement).checked;
      this.applySettings();
    });
    mustElement<HTMLInputElement>('#personal-ghost').addEventListener('change', (event) => {
      this.gameSettings.personalGhost = (event.currentTarget as HTMLInputElement).checked;
      this.applySettings();
    });
    mustElement<HTMLInputElement>('#camera-shake').addEventListener('input', (event) => {
      this.gameSettings.cameraShake = Number((event.currentTarget as HTMLInputElement).value);
      this.updateCameraShakeLabel();
      this.applySettings();
    });
    mustElement<HTMLInputElement>('#soft-flashes').addEventListener('change', (event) => {
      this.gameSettings.softFlashes = (event.currentTarget as HTMLInputElement).checked;
      this.applySettings();
    });
    mustElement<HTMLInputElement>('#screen-distortion').addEventListener('change', (event) => {
      this.gameSettings.screenDistortion = (event.currentTarget as HTMLInputElement).checked;
      this.applySettings();
    });
    mustElement<HTMLSelectElement>('#frame-rate-target').addEventListener('change', (event) => {
      this.gameSettings.frameRateTarget = (event.currentTarget as HTMLSelectElement).value as GameSettings['frameRateTarget'];
      this.applySettings();
    });
    mustElement<HTMLInputElement>('#motion-sensitivity').addEventListener('input', (event) => {
      this.gameSettings.motionSensitivity = Number((event.currentTarget as HTMLInputElement).value);
      this.updateMotionSensitivityLabel();
      this.applySettings();
    });
    mustElement<HTMLInputElement>('#motion-dead-zone').addEventListener('input', (event) => {
      this.gameSettings.motionDeadZone = Number((event.currentTarget as HTMLInputElement).value);
      this.updateMotionSensitivityLabel();
      this.applySettings();
    });
    mustElement<HTMLSelectElement>('#motion-response').addEventListener('change', (event) => {
      this.gameSettings.motionResponseCurve = (event.currentTarget as HTMLSelectElement).value as GameSettings['motionResponseCurve'];
      this.applySettings();
    });
    mustElement<HTMLSelectElement>('#mobile-layout').addEventListener('change', (event) => {
      this.gameSettings.mobileControlLayout = (event.currentTarget as HTMLSelectElement).value as GameSettings['mobileControlLayout'];
      this.applySettings();
    });
    mustElement<HTMLInputElement>('#mobile-control-size').addEventListener('input', (event) => {
      this.gameSettings.mobileControlSize = Number((event.currentTarget as HTMLInputElement).value);
      this.updateMobileSettingsLabels();
      this.applySettings();
    });
    mustElement<HTMLInputElement>('#mobile-control-opacity').addEventListener('input', (event) => {
      this.gameSettings.mobileControlOpacity = Number((event.currentTarget as HTMLInputElement).value);
      this.updateMobileSettingsLabels();
      this.applySettings();
    });
    mustElement<HTMLInputElement>('#mobile-haptics').addEventListener('change', (event) => {
      this.gameSettings.mobileHaptics = (event.currentTarget as HTMLInputElement).checked;
      this.applySettings();
    });
    mustElement<HTMLSelectElement>('#mobile-steering-assist').addEventListener('change', (event) => {
      this.gameSettings.steeringAssistMode = (event.currentTarget as HTMLSelectElement).value as GameSettings['steeringAssistMode'];
      this.gameSettings.mobileSteeringAssist = this.gameSettings.steeringAssistMode !== 'pro';
      this.applySettings();
    });
    mustElement<HTMLInputElement>('#mobile-auto-throttle').addEventListener('change', (event) => {
      this.gameSettings.mobileAutoThrottle = (event.currentTarget as HTMLInputElement).checked;
      this.applySettings();
    });
    mustElement<HTMLInputElement>('#driving-coach').addEventListener('change', (event) => {
      this.gameSettings.drivingCoach = (event.currentTarget as HTMLInputElement).checked;
      this.applySettings();
    });
    const runtimeScope = this.raceRuntime.scope;
    if (!runtimeScope) throw new Error('Race UI requires an active runtime scope');
    runtimeScope.listen(window, 'keydown', this.onSettingsKey);
    runtimeScope.listen(window, 'keydown', this.onQuickRestartKey);
    runtimeScope.listen(window, 'keydown', this.onLobbyShortcut);
    runtimeScope.listen(window, 'resize', this.onViewportModeChange);
    runtimeScope.listen(document, 'fullscreenchange', this.onViewportModeChange);
    if (screen.orientation) {
      runtimeScope.listen(screen.orientation, 'change', this.onViewportModeChange);
    }
    this.syncSettingsForm();
    this.updateMotionStatus(this.deviceCapabilities.motionSteering ? 'off' : 'unavailable');
    this.applySettings();
    this.syncLandscapeFullscreenPrompt();
  }

  private syncRaceUi(map: MapDefinition, racers: RacerSnapshot[]): void {
    const localCar = racers.find((racer) => racer.id === this.player?.id);
    const ui = this.raceUiElements;
    if (!ui) return;
    const stage = ui.raceStage;
    const mode = this.room ? raceEventMode(this.room.settings) : 'circuit';
    const timeTrial = mode === 'time-trial';
    stage.classList.toggle('tank-driver', CAR_BY_ID.get(localCar?.carId ?? '')?.body === 'tank');
    stage.classList.toggle('time-trial-event', timeTrial);
    stage.classList.toggle('style-rush-event', mode === 'style-rush');
    stage.classList.toggle('elimination-event', mode === 'elimination');
    stage.classList.toggle('heat-run-event', mode === 'heat-run');
    stage.classList.toggle('road-rage-event', mode === 'road-rage');
    stage.classList.toggle('sprint-event', mode === 'sprint');
    setText(ui.eventMode, t(eventModeLabel(mode)));
    ui.eventMode.dataset.mode = mode;
    mustElement<HTMLButtonElement>('#time-trial-restart').hidden = !timeTrial;
    const bankButton = this.root.querySelector<HTMLButtonElement>('[data-drive="bank"]');
    if (bankButton) bankButton.hidden = mode !== 'heat-run';
    const recover = this.root.querySelector<HTMLButtonElement>('[data-drive="reset"]');
    if (recover) {
      const soloRecovery = this.room?.playerCount === 1;
      recover.title = soloRecovery ? t('Rewind to your last safe line (2 charges, costs 25 nitro)') : t('Reset to track');
      recover.setAttribute('aria-label', soloRecovery ? t('Rewind about three seconds to a safe line. Two charges per race.') : t('Reset to track'));
    }
    this.lobbyRacers = racers;
    this.syncLobbyUi();
    drawMinimap(ui.minimap, map, racers, this.player?.id ?? '');
    this.lastMinimapUiAt = performance.now();
  }

  /** An offline grid was already confirmed in the lobby, so it releases as
   * soon as the track is built; settings or a tilt prompt hold it. */
  private scheduleOfflineAutoLaunch(): void {
    this.cancelOfflineAutoLaunch();
    if (!this.localRace || !this.isHost || this.settingsOpen) return;
    if ((this.latestSnapshot?.phase ?? this.room?.phase) !== 'lobby') return;
    const generation = this.runtimeGeneration;
    this.offlineAutoLaunchTimer = window.setTimeout(() => {
      this.offlineAutoLaunchTimer = undefined;
      if (generation !== this.runtimeGeneration || !this.raceEngineReady || this.settingsOpen) return;
      if (this.root.querySelector('#race-stage.motion-permission-required')) return;
      if ((this.latestSnapshot?.phase ?? this.room?.phase) !== 'lobby') return;
      this.connection?.send({ type: 'ready', ready: true });
    }, OFFLINE_AUTO_LAUNCH_MS);
  }

  private cancelOfflineAutoLaunch(): void {
    if (this.offlineAutoLaunchTimer === undefined) return;
    window.clearTimeout(this.offlineAutoLaunchTimer);
    this.offlineAutoLaunchTimer = undefined;
  }

  private syncHostUi(): void {
    this.syncLobbyUi();
  }

  private currentRaceMap(): MapDefinition | undefined {
    return resolveRuntimeRaceMap(
      this.room?.settings.mapId,
      this.activeRaceMap,
      this.maps,
    );
  }

  private presentLocalFinish(player: RacerSnapshot, racers: readonly RacerSnapshot[], mode: RaceEventMode): void {
    const fieldSize = racers.length;
    const banner = this.root.querySelector<HTMLElement>('#finish-banner');
    if (!banner || mode === 'time-trial') return;
    const label = eventResultLabel(mode, player.place, player.finishedAt);
    const winner = label === 'WIN' || (label === 'FINISHED' && player.place === 1);
    const kicker = label === 'OUT' ? t('KNOCKED OUT') : label === 'SCORED' ? t('RUN COMPLETE') : t('CHECKERED FLAG');
    const headline = winner ? t('VICTORY') : ordinal(player.place);
    const leader = racers.find((racer) => racer.place === 1 && racer.finishedAt !== null);
    const detail = winner
      ? t('FIRST OF {total}', { total: fieldSize })
      : label === 'FINISHED' && leader && player.finishedAt !== null
        ? t('+{gap} TO {name}', { gap: formatGap(player.finishedAt - leader.finishedAt!), name: leader.name })
        : t('OF {total} DRIVERS', { total: fieldSize });
    banner.dataset.tone = winner ? 'win' : label === 'OUT' ? 'out' : player.place <= 3 ? 'podium' : 'finish';
    banner.innerHTML = `<small>${escapeHtml(kicker)}</small><b>${escapeHtml(headline)}</b><span>${escapeHtml(detail)}</span>`;
    banner.classList.remove('visible');
    void banner.offsetWidth;
    banner.classList.add('visible');
  }

  private updateRaceUi(snapshot: RaceSnapshot): void {
    const ui = this.raceUiElements;
    if (!ui) return;
    const player = this.game?.localRacer ?? snapshot.racers.find((racer) => racer.id === this.player?.id);
    if (!player || !this.room) return;
    const phaseChanged = snapshot.phase !== this.lastPhase;
    this.raceRun = observeRaceRun(this.raceRun, player);
    this.updateTelemetryUi(player);
    const map = this.currentRaceMap();
    const laps = map ? raceLapCount(this.room.settings, map.laps) : null;
    const eventMode = raceEventMode(this.room.settings);
    setText(ui.lapValue, eventMode === 'heat-run'
      ? player.completedLaps > 0
        ? t('HEAT {heat} / {total} · BANK READY', { heat: Math.max(0, player.completedLaps), total: laps ?? '-' })
        : t('HEAT {heat} / {total}', { heat: Math.max(0, player.completedLaps), total: laps ?? '-' })
      : `${player.lap} / ${laps ?? '-'}`);
    const bankButton = this.root.querySelector<HTMLButtonElement>('[data-drive="bank"]');
    if (bankButton) {
      bankButton.disabled = player.completedLaps < 1 || player.finishedAt !== null;
      bankButton.textContent = player.completedLaps < 1 ? t('BANK LOCKED') : t('BANK H{heat}', { heat: player.completedLaps });
    }
    setText(ui.placeValue, ordinal(player.place));
    if (snapshot.phase === 'racing' && this.lastHudPlace > 0 && player.place !== this.lastHudPlace) {
      ui.placeValue.animate(
        [
          { transform: 'scale(1.32)', color: player.place < this.lastHudPlace ? '#b6ff3d' : '#ff8b84' },
          { transform: 'scale(1)' },
        ],
        { duration: 420, easing: 'cubic-bezier(.2,.8,.2,1)' },
      );
    }
    this.lastHudPlace = snapshot.phase === 'racing' ? player.place : 0;
    const classifiedRacers = snapshot.racers.filter((racer) => !racer.pursuit);
    setText(ui.positionTotal, `/ ${classifiedRacers.length}`);
    const eventClock = snapshot.raceTimeRemainingMs;
    const wager = raceWagerDefinition(player.wagerKind);
    const wagerLabel = wager && player.wagerStatus !== 'inactive'
      ? ` · ${player.wagerStatus === 'complete' ? t('WAGER WON') : player.wagerStatus === 'failed' ? t('WAGER LOST') : `${Math.min(player.wagerProgress ?? 0, player.wagerTarget ?? wager.target)}/${player.wagerTarget ?? wager.target}`}`
      : '';
    setText(
      ui.eventMode,
      `${t(eventModeLabel(eventMode))}${eventClock !== null && eventClock !== undefined && snapshot.phase === 'racing'
        ? ` · ${formatEventClock(eventClock)}`
        : ''}${wagerLabel}`,
    );
    const directorCue = this.raceDirector?.observe(snapshot, player.id);
    if (directorCue && (!this.deviceCapabilities.touchDevice || this.compactCueGate.allow(directorCue, performance.now()))) {
      this.showRaceDirectorCue(directorCue);
    }
    let leaderboardSignature = '';
    const onlineRace = !this.localRace;
    for (const racer of classifiedRacers) {
      const ping = onlineRace && !racer.npc ? displayPing(racer.id === this.player?.id ? this.latencyMs : this.memberPings.get(racer.id)) : null;
      leaderboardSignature += `${racer.id}:${racer.place}:${Number(racer.connected)}:${connectionQuality(ping, racer.connected)}:${racer.rivalStyle ?? ''}:${racer.tuning.paint}:${eventMode === 'style-rush' || eventMode === 'heat-run' ? `${Math.round(racer.styleScore)}:${racer.completedLaps}` : ''}:${Number(Boolean(racer.finishedAt))}|`;
    }
    if (leaderboardSignature !== this.lastLeaderboardSignature) {
      this.lastLeaderboardSignature = leaderboardSignature;
      const leaderboard = standingsWindow(classifiedRacers, this.player?.id);
      ui.leaderboard.innerHTML = leaderboard.map((racer) => racer === null ? '<li class="standings-gap" aria-hidden="true"></li>' : `
        <li class="${racer.id === this.player?.id ? 'you' : ''}" data-road-rage-target="${racer.roadRageTarget === true}"><b>${String(racer.place).padStart(2, '0')}</b><i style="--driver-colour:${safeColour(racer.tuning.paint)}"></i><span>${escapeHtml(racer.roadRageTarget ? t('MARK // {name}', { name: racer.name }) : racer.name)}</span><small>${eventMode === 'style-rush'
          ? Math.round(racer.styleScore).toString().padStart(5, '0')
          : eventMode === 'heat-run' ? `H${racer.completedLaps} · ${heatRunDisplayScore(racer).toString().padStart(5, '0')}`
          : eventMode === 'road-rage' ? t('{ko} KO · {points} PTS', { ko: racer.takedownCount ?? 0, points: racer.roadRageScore ?? 0 })
          : eventMode === 'elimination' && racer.finishedAt ? snapshot.phase === 'finished' && racer.place === 1 ? t('WIN') : t('OUT')
            : racer.id === this.player?.id ? t('YOU')
            : racer.npc ? t((racer.rivalStyle ?? 'AI').toUpperCase())
            : signalBarsHtml(onlineRace ? this.memberPings.get(racer.id) : null, racer.connected, false)}</small></li>`).join('');
    }
    const now = performance.now();
    const minimapIntervalMs = this.deviceCapabilities.touchDevice ? 200 : 100;
    if (map && (phaseChanged || now - this.lastMinimapUiAt >= minimapIntervalMs)) {
      drawMinimap(ui.minimap, map, snapshot.racers, this.player?.id ?? '');
      this.lastMinimapUiAt = now;
    }

    const countdown = ui.countdown;
    if (phaseChanged) {
      if (snapshot.phase !== 'lobby') this.closeInviteShare(false);
      ui.raceStage.classList.toggle('race-active', snapshot.phase === 'countdown' || snapshot.phase === 'racing');
      ui.lobby.classList.toggle('hidden', snapshot.phase !== 'lobby');
      ui.hud.classList.toggle('hidden', snapshot.phase === 'lobby');
    }
    // Race control drives the cool-down lap: the pedals step aside and a
    // badge says why the car no longer answers.
    const localFinished = snapshot.phase === 'racing'
      && snapshot.racers.some((racer) => racer.id === this.player?.id && racer.finishedAt !== null);
    ui.raceStage.classList.toggle('cooldown-lap', localFinished);
    // Crossing the line is the moment of the race: give it a full-screen
    // beat. A resumed cool-down lap (no racing snapshot before it) stays quiet.
    if (localFinished && !this.lastLocalFinished && this.lastPhase === 'racing') {
      this.presentLocalFinish(player, snapshot.racers, eventMode);
    }
    this.lastLocalFinished = localFinished;
    if (snapshot.phase === 'lobby') {
      this.lobbyRacers = snapshot.racers;
      this.syncLobbyUi();
    } else if (phaseChanged && this.lobbyGarageOpen) {
      this.closeLobbyGarage(false);
    }
    if (snapshot.phase === 'countdown') {
      if (phaseChanged) {
        this.resultsCareer.reset();
        countdown.classList.add('visible');
      }
      renderStartLights(countdown, Math.max(1, Math.ceil(snapshot.countdownMs / 1_000)));
    } else if (this.lastPhase === 'countdown' && snapshot.phase === 'racing') {
      countdown.classList.add('visible', 'go');
      renderStartLights(countdown, 'go');
      if (this.goTimer !== undefined) this.raceRuntime.scope?.cancelTimeout(this.goTimer);
      this.goTimer = this.raceRuntime.scope?.timeout(
        () => countdown.classList.remove('visible', 'go'),
        800,
      );
    } else if (snapshot.phase !== 'racing') {
      countdown.classList.remove('visible', 'go');
    }
    if (snapshot.phase === 'finished' && this.lastPhase !== 'finished') {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const careerBank = this.careerBankPromise;
      const runtimeGeneration = this.runtimeGeneration;
      const raceId = this.raceProgressionId();
      this.resultsCareer.schedule({
        delayMs: reducedMotion ? 0 : 2_850,
        settle: careerBank,
        isCurrent: () => (
          runtimeGeneration === this.runtimeGeneration
          && raceId === this.raceProgressionId()
          && this.latestSnapshot?.phase === 'finished'
        ),
        present: () => this.showResults(snapshot),
      });
    }
    if (phaseChanged) {
      const drivingActive = snapshot.phase === 'countdown' || snapshot.phase === 'racing';
      // Keep a waiting build parked while the finish cinematic/results are on
      // screen. Runtime teardown releases the session lease once the player
      // returns to a safe menu; wake lock still ends as soon as driving ends.
      const raceSessionActive = racePhaseHoldsUpdateLease(snapshot.phase);
      this.pwa.setRaceActive(raceSessionActive);
      this.pwa.setWakeLockActive(drivingActive && !this.settingsOpen);
    }
    this.lastPhase = snapshot.phase;
  }

  private rememberRace(
    room: PublicRoom,
    player: PlayerIdentity,
    inviteToken: string,
    previousRoomId = '',
  ): boolean {
    const session: StoredRaceSession = {
      version: 1,
      roomId: room.id,
      player,
      inviteToken,
      mapId: room.settings.mapId,
      mapName: this.maps.find((map) => map.id === room.settings.mapId)?.name ?? '',
      phase: room.phase,
      savedAt: Date.now(),
    };
    const resumeWindowMs = resumeWindowFromSessionTtl(room.sessionTtlMs);
    if (resumeWindowMs !== undefined) session.resumeWindowMs = resumeWindowMs;
    const replacing = previousRoomId.length > 0 && previousRoomId !== room.id;
    const persisted = replacing
      ? replaceRaceSession(previousRoomId, session)
      : saveRaceSession(session);
    this.recentRace = session;
    if (persisted) {
      this.lastRaceSaveAt = session.savedAt;
      this.lastPersistedRacePhase = session.phase;
      this.racePersistenceRetryAt = 0;
      this.pendingRaceSessionPredecessorId = '';
    } else {
      this.racePersistenceRetryAt = Date.now() + 5_000;
      this.pendingRaceSessionPredecessorId = replacing ? previousRoomId : '';
    }
    return persisted;
  }

  private persistCurrentRace(phase: RacePhase, force = false, mapName = ''): void {
    if (!this.room || !this.player || !this.inviteToken) return;
    const now = Date.now();
    if (!force && now < this.racePersistenceRetryAt) return;
    if (!force && phase === this.lastPersistedRacePhase && now - this.lastRaceSaveAt < 30_000) return;
    const existing = loadRaceSession(this.room.id);
    if (this.pendingRaceSessionPredecessorId || !existing) {
      this.rememberRace(
        this.room,
        this.player,
        this.inviteToken,
        this.pendingRaceSessionPredecessorId,
      );
    } else {
      const updated = updateRaceSession(this.room.id, {
        mapId: this.room.settings.mapId,
        mapName: mapName || this.currentRaceMap()?.name || existing.mapName,
        phase,
        resumeWindowMs: resumeWindowFromSessionTtl(this.room.sessionTtlMs) ?? existing.resumeWindowMs,
      });
      if (updated) {
        this.recentRace = updated;
        this.lastRaceSaveAt = updated.savedAt;
        this.lastPersistedRacePhase = phase;
        this.racePersistenceRetryAt = 0;
      } else {
        this.recentRace = existing;
        this.racePersistenceRetryAt = now + 5_000;
      }
    }
  }

  private queueOfflineCheckpoint(
    setup: StoredOfflineRace['setup'],
    checkpoint: StoredOfflineRace['checkpoint'],
    force = false,
  ): void {
    if (setup.roomId === this.discardedOfflineRoomId) return;
    // A grid that never left its lobby is not a race worth continuing.
    if (checkpoint.phase === 'lobby') return;
    if (checkpoint.phase === 'finished') {
      void this.clearOfflineCheckpoint(false, setup.roomId, checkpoint.raceAttempt ?? 1);
      return;
    }
    this.offlineCheckpointPublisher.queue(setup, checkpoint, force);
  }

  private async clearOfflineCheckpoint(
    waitForDelete = false,
    expectedRoomId = this.offlineRace?.setup.roomId,
    finishedAttempt?: number,
  ): Promise<void> {
    this.offlineCheckpointPublisher.reset();
    this.offlineRaceLoadGeneration += 1;
    this.offlineRace = null;
    // Starting a race can beat the asynchronous startup read on slow tablets.
    // Never let an unknown owner turn into a blind cross-tab IndexedDB delete;
    // the first checkpoint of the new race atomically replaces older data.
    const deletion = expectedRoomId
      ? deleteOfflineRaceCheckpoint(expectedRoomId, finishedAttempt)
      : Promise.resolve(false);
    if (waitForDelete) await deletion;
  }

  /** Lights the HUD car silhouette on the sides that have taken hits, so a
   * player can see at a glance where the car is hurt. */
  /**
   * Hit feedback on the integrity bar: the chunk just lost stays lit behind
   * the fill for a beat and then drains, the widget flashes, and the loss
   * pops up as a number. Hits in quick succession (a grind, a pile-up) merge
   * into one chunk and one running total instead of flickering.
   */
  private presentIntegrityHit(ui: RaceUiElements, racerId: string, health: number, now: number): void {
    const previous = this.hudIntegrityHealth;
    this.hudIntegrityHealth = health;
    if (previous === null || health > previous + 0.5 || this.hudIntegrityRacerId !== racerId) {
      // New race, reset or repair: no drain, no flash.
      this.hudIntegrityRacerId = racerId;
      this.integrityHitTotal = 0;
      this.integrityLossFrom = health;
      ui.integrityLoss.getAnimations().forEach((animation) => animation.cancel());
      setStyle(ui.integrityLoss, 'width', `${health}%`);
      return;
    }
    const lost = previous - health;
    if (lost < 0.5) return;
    const merging = now - this.integrityHitAt < 900;
    this.integrityHitAt = now;
    this.integrityHitTotal = merging ? this.integrityHitTotal + lost : lost;
    this.integrityLossFrom = merging ? Math.max(this.integrityLossFrom, previous) : previous;
    setStyle(ui.integrityLoss, 'width', `${health}%`);
    if (typeof ui.integrityLoss.animate !== 'function') return;
    ui.integrityLoss.getAnimations().forEach((animation) => animation.cancel());
    ui.integrityLoss.animate(
      [
        { width: `${this.integrityLossFrom}%`, opacity: 1 },
        { width: `${this.integrityLossFrom}%`, opacity: 1, offset: 0.42 },
        { width: `${health}%`, opacity: 0.7 },
      ],
      { duration: 900, easing: 'cubic-bezier(0.3, 0, 0.2, 1)' },
    );
    const heavy = this.integrityHitTotal >= 8;
    ui.integrity.animate(
      [{ filter: `drop-shadow(0 2px 8px #000) brightness(${heavy ? 2.1 : 1.6})` }, { filter: 'drop-shadow(0 2px 8px #000)' }],
      { duration: heavy ? 420 : 260, easing: 'ease-out' },
    );
    if (this.integrityHitTotal < 1) return;
    setText(ui.integrityHit, `\u2212${Math.round(this.integrityHitTotal)}`);
    const rise = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : heavy ? -15 : -11;
    ui.integrityHit.getAnimations().forEach((animation) => animation.cancel());
    ui.integrityHit.animate(
      [
        { opacity: 0, transform: `translate(14px, 0) scale(${heavy ? 1.5 : 1.2})` },
        { opacity: 1, transform: 'translate(14px, -3px) scale(1)', offset: 0.14 },
        { opacity: 1, transform: `translate(14px, ${rise * 0.6}px) scale(1)`, offset: 0.7 },
        { opacity: 0, transform: `translate(14px, ${rise}px) scale(0.96)` },
      ],
      { duration: 1_100, easing: 'ease-out' },
    );
  }

  private updateDamageMap(map: SVGSVGElement): void {
    const zones = this.game?.localDamageZones();
    if (!zones) return;
    const sides: (keyof typeof zones)[] = ['front', 'rear', 'left', 'right'];
    const hurt: string[] = [];
    for (const side of sides) {
      const amount = Math.round(Math.max(0, Math.min(1, zones[side])) * 20) / 20;
      const value = String(amount);
      const previous = Number(map.style.getPropertyValue(`--${side}`) || 0);
      if (amount >= previous + 0.05) {
        // The struck side flashes white-hot before settling to its wear colour.
        const zone = map.querySelector<SVGElement>(`[data-zone="${side}"]`);
        zone?.animate?.([{ opacity: 1, fill: '#ffffff' }], { duration: 520, easing: 'ease-out' });
      }
      if (map.style.getPropertyValue(`--${side}`) !== value) map.style.setProperty(`--${side}`, value);
      if (amount >= 0.15) hurt.push(`${t(side)} ${Math.round(amount * 100)}%`);
    }
    const label = hurt.length > 0
      ? t('Damage by side: {sides}', { sides: hurt.join(', ') })
      : t('Damage by side: none');
    if (map.getAttribute('aria-label') !== label) map.setAttribute('aria-label', label);
  }

  private updateTelemetryUi(player: RacerSnapshot): void {
    const ui = this.raceUiElements;
    if (!ui) return;
    const now = performance.now();
    // Ten HUD updates per second still read as continuous on a 20/30 FPS
    // tablet, while halving style, accessibility-tree and text invalidations.
    const telemetryIntervalMs = this.deviceCapabilities.touchDevice ? 100 : 50;
    if (now - this.lastTelemetryUiAt < telemetryIntervalMs) return;
    this.lastTelemetryUiAt = now;
    setText(ui.speedValue, String(Math.max(0, Math.round(player.speed * 3.6))).padStart(3, '0'));
    const car = CAR_BY_ID.get(player.carId);
    const rpm = Math.round(player.engineRpm / 50) * 50;
    const rpmRange = Math.max(1, (car?.drivetrain.redlineRpm ?? 8_000) - (car?.drivetrain.idleRpm ?? 800));
    const normalizedRpm = Math.max(0, Math.min(1, (player.engineRpm - (car?.drivetrain.idleRpm ?? 800)) / rpmRange));
    ui.powertrain.classList.toggle('redline', normalizedRpm > 0.91);
    ui.powertrain.classList.toggle('slipping', player.wheelSlip > 0.32);
    setText(ui.gearValue, player.gear < 0 ? 'R' : player.gear === 0 ? 'N' : String(player.gear));
    setText(ui.rpmValue, `${String(rpm).padStart(4, '0')} RPM`);
    setStyle(ui.rpmFill, 'width', `${Math.max(4, normalizedRpm * 100)}%`);
    // The tachometer sweep spans 75 of its 100 path units (270°).
    if (ui.tachoFill) setStyle(ui.tachoFill, 'stroke-dashoffset', (75 * (1 - Math.max(0.04, normalizedRpm))).toFixed(2));
    ui.cluster?.classList.toggle('shift-high', normalizedRpm > 0.78 && normalizedRpm <= 0.91);
    ui.cluster?.classList.toggle('redline', normalizedRpm > 0.91);
    const health = Math.max(0, Math.min(100, player.health));
    ui.integrity.classList.toggle('damaged', health < 55);
    ui.integrity.classList.toggle('critical', health < 25);
    setText(ui.integrityValue, String(Math.round(health)).padStart(3, '0'));
    setStyle(ui.integrityFill, 'width', `${health}%`);
    this.presentIntegrityHit(ui, player.id, health, now);
    this.updateDamageMap(ui.damageMap);
    setStyle(ui.damageVignette, 'opacity', String(Math.max(0, (58 - health) / 105)));
    const nitroCharge = Math.max(0, Math.min(100, player.nitroCharge));
    setStyle(ui.nitroFill, 'width', `${nitroCharge}%`);
    if (ui.tachoNitro) setStyle(ui.tachoNitro, 'stroke-dashoffset', (75 * (1 - nitroCharge / 100)).toFixed(2));
    setText(ui.nitroValue, String(Math.round(nitroCharge)).padStart(3, '0'));
    const nitroButton = ui.nitroButton;
    nitroButton?.style.setProperty('--nitro-charge', `${nitroCharge}%`);
    nitroButton?.classList.toggle('active', player.nitroActive);
    nitroButton?.setAttribute('aria-label', t('Nitro boost, {percent} percent available', { percent: Math.round(nitroCharge) }));
    const driftButton = ui.driftButton;
    const driftCharge = Math.min(100, player.drift * 100);
    driftButton?.style.setProperty('--drift-charge', `${driftCharge.toFixed(1)}%`);
    driftButton?.classList.toggle('active', player.drift > 0.2);
    driftButton?.setAttribute('aria-label', player.drift > 0.2
      ? t('Drift active, {percent} percent rotation', { percent: Math.round(driftCharge) })
      : t('Tap to rotate, hold to drift and earn exit nitro'));
    const recoverButton = this.root.querySelector<HTMLButtonElement>('[data-drive="reset"]');
    if (recoverButton && this.localRace) {
      const rewinds = player.rewindsRemaining ?? 0;
      recoverButton.textContent = rewinds > 0 ? `↺ ${rewinds}` : '↺';
      recoverButton.title = rewinds > 0
        ? tn(rewinds, '{n} safe rewind left · costs 25 nitro', '{n} safe rewinds left · costs 25 nitro')
        : t('Safe rewinds spent · normal reset remains available');
    }
    ui.raceStage.classList.toggle('nitro-active', player.nitroActive);
    const draftCharge = Math.max(0, Math.min(1, player.draftCharge));
    ui.draftHud.classList.toggle('active', draftCharge > 0.03 || player.draftBoostActive);
    ui.draftHud.classList.toggle('boost', player.draftBoostActive);
    setStyle(ui.draftFill, 'width', `${(draftCharge * 100).toFixed(1)}%`);
    setText(ui.draftValue, player.draftBoostActive ? t('SLINGSHOT') : `${Math.round(draftCharge * 100)}%`);
    const launchCharge = Math.max(0, Math.min(1, player.launchCharge));
    ui.launchHud.classList.toggle('visible', this.latestSnapshot?.phase === 'countdown');
    ui.launchHud.classList.toggle('sweet', launchCharge >= 0.54 && launchCharge <= 0.86);
    ui.launchHud.classList.toggle('over', launchCharge >= 0.93);
    setStyle(ui.launchNeedle, 'left', `${(launchCharge * 100).toFixed(1)}%`);
    setText(
      ui.launchValue,
      launchCharge >= 0.93 ? t('TOO HOT') : launchCharge >= 0.54 && launchCharge <= 0.86 ? t('HOLD') : t('BUILD'),
    );
    const combo = Math.max(0, player.styleCombo);
    ui.styleHud.classList.toggle('active', combo > 0);
    ui.styleHud.classList.toggle('hot', player.styleMultiplier >= 2.5);
    setText(ui.styleScore, String(Math.max(0, Math.round(player.styleScore))).padStart(6, '0'));
    setText(ui.styleAction, player.styleAction && player.styleAction !== 'CHAIN LOST'
      ? t(player.styleAction)
      : combo > 0 ? t('STYLE CHAIN') : t('BUILD THE CHAIN'));
    setText(ui.styleMultiplier, `×${formatNumber(player.styleMultiplier, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`);
    setStyle(ui.styleComboFill, 'width', `${Math.min(100, combo * 8.5)}%`);
    const gripStress = Math.max(player.wheelSlip, player.drift * 0.82);
    const gripRemaining = Math.max(0, Math.min(1, 1 - gripStress));
    // Understeer feedback: the front tyres are saturated, so extra steering
    // input buys nothing. The driver sees the marker turn amber instead of
    // wondering why the car stopped answering.
    const understeer = (this.game?.localSteeringAuthority ?? 1) < 0.98 && Math.abs(player.steer) > 0.15;
    ui.gripHud.classList.toggle('warning', gripRemaining < 0.55);
    ui.gripHud.classList.toggle('sliding', gripRemaining < 0.3);
    ui.gripHud.classList.toggle('understeer', understeer);
    // Full grip is the normal state; the meter steps back until it matters,
    // and stays up briefly after a slide so the row doesn't flicker.
    const gripNow = performance.now();
    if (gripRemaining <= 0.86 || understeer) this.gripHudVisibleUntil = gripNow + 1_400;
    ui.gripHud.classList.toggle('calm', gripNow > this.gripHudVisibleUntil);
    setStyle(ui.gripFill, 'width', `${(gripRemaining * 100).toFixed(1)}%`);
    setStyle(ui.gripSteer, 'transform', `translateX(${(Math.max(-1, Math.min(1, player.steer)) * 49).toFixed(1)}px)`);
    setText(ui.gripValue, `${Math.round(gripRemaining * 100)}%`);
    const challenge = liveChallengeDefinition(player.liveChallenge);
    const challengeStatus = player.liveChallengeStatus ?? 'inactive';
    const challengeVisible = challenge !== null && challengeStatus !== 'inactive';
    ui.liveChallengeHud.classList.toggle('visible', challengeVisible);
    const directorActive = ui.raceStage.classList.contains('director-active');
    ui.liveChallengeHud.setAttribute('aria-hidden', String(!challengeVisible || directorActive));
    ui.sectorHud.setAttribute('aria-hidden', String(directorActive));
    ui.liveChallengeHud.dataset.status = challengeStatus;
    if (challenge) {
      const challengeProgress = Math.max(0, player.liveChallengeProgress ?? 0);
      const challengeTarget = Math.max(1, player.liveChallengeTarget ?? 1);
      setText(ui.liveChallengeTitle, challengeStatus === 'complete'
        ? t('CONTRACT COMPLETE')
        : challengeStatus === 'failed' ? t('CONTRACT MISSED') : t(challenge.name));
      setText(ui.liveChallengeDetail, challengeStatus === 'active'
        ? t(challenge.description)
        : challengeStatus === 'complete' ? t(challenge.completion) : t('Race control is finding another offer.'));
      setStyle(ui.liveChallengeFill, 'width', `${Math.min(100, challengeProgress / challengeTarget * 100).toFixed(1)}%`);
      setText(
        ui.liveChallengeTime,
        challengeStatus === 'active'
          ? `${Math.max(0, Math.ceil((player.liveChallengeTimeRemainingMs ?? 0) / 1_000))}S`
          : challengeStatus === 'complete' ? t('DONE') : t('MISS'),
      );
    }
    const sectorIndex = Math.max(0, Math.min(2, player.sectorIndex ?? 0));
    ui.sectorCells.forEach((cell, index) => {
      cell.classList.toggle('active', index === sectorIndex);
      cell.classList.toggle('complete', index < sectorIndex);
    });
    const sectorDelta = player.sectorDeltaMs ?? 0;
    ui.sectorHud.classList.toggle('ahead', sectorDelta < 0);
    ui.sectorHud.classList.toggle('behind', sectorDelta > 0);
    // Until the first sector split exists the chip is only a hint; it steps
    // back so it doesn't compete with the road ahead.
    ui.sectorHud.classList.toggle('idle', (player.sectorEventId ?? 0) <= 0);
    setText(ui.sectorDelta, (player.sectorEventId ?? 0) > 0
      ? `${sectorDelta <= 0 ? '−' : '+'}${formatNumber(Math.abs(sectorDelta) / 1_000, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`
      : t('SECTOR DUEL'));
    const comebackCharge = Math.max(0, Math.min(100, player.comebackCharge ?? 0));
    const comebackActive = player.comebackActive === true;
    ui.comebackHud.classList.toggle('visible', comebackCharge > 0.5 || comebackActive);
    ui.comebackHud.setAttribute('aria-hidden', String(comebackCharge <= 0.5 && !comebackActive));
    ui.comebackHud.classList.toggle('active', comebackActive);
    setStyle(ui.comebackFill, 'width', `${comebackActive ? 100 : comebackCharge}%`);
    setText(ui.comebackValue, comebackActive ? t('ATTACK') : comebackCharge >= 99.5 ? t('NITRO TO DEPLOY') : `${Math.round(comebackCharge)}%`);
    const heatLevel = Math.max(0, player.heatLevel ?? 0);
    const activePursuitUnits = this.latestSnapshot?.racers.filter((racer) => racer.pursuit && racer.pursuitActive).length ?? 0;
    const heatRun = this.room ? raceEventMode(this.room.settings) === 'heat-run' : heatLevel > 0;
    ui.heatHud.classList.toggle('visible', heatRun);
    ui.heatHud.setAttribute('aria-hidden', String(!heatRun));
    ui.heatHud.classList.toggle('active', activePursuitUnits > 0);
    setText(ui.heatLevel, t('HEAT {level}', { level: heatLevel }));
    setText(ui.heatUnits, activePursuitUnits > 0
      ? tn(activePursuitUnits, '{n} INTERCEPTOR', '{n} INTERCEPTORS')
      : heatLevel > 0 ? t('PURSUIT SEARCHING') : t('LOW PROFILE'));
    if (player.styleEventId !== this.lastStyleEventId && player.styleAction) {
      this.lastStyleEventId = player.styleEventId;
      if (this.deviceCapabilities.touchDevice && !compactStyleCalloutAllowed(player.styleAction, player.styleMultiplier)) return;
      const callout = ui.styleCallout;
      const lost = player.styleAction === 'CHAIN LOST';
      const wheelspin = player.styleAction === 'WHEELSPIN';
      callout.classList.toggle('lost', lost || wheelspin);
      callout.innerHTML = lost
        ? `<b>${t('CHAIN LOST')}</b><small>${t('KEEP IT CLEAN')}</small>`
        : wheelspin
          ? `<b>${t('WHEELSPIN')}</b><small>${t('EASE INTO THE THROTTLE')}</small>`
          : `<b>${escapeHtml(t(player.styleAction))}</b><small>${t('STYLE ×{multiplier} · NITRO EARNED', { multiplier: formatNumber(player.styleMultiplier, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) })}</small>`;
      for (const animation of callout.getAnimations()) animation.cancel();
      callout.animate(
        [
          { opacity: 0, transform: 'translate(-50%, 12px) scale(.88)' },
          { opacity: 1, transform: 'translate(-50%, 0) scale(1)', offset: 0.18 },
          { opacity: 1, transform: 'translate(-50%, 0) scale(1)', offset: 0.72 },
          { opacity: 0, transform: 'translate(-50%, -12px) scale(1.04)' },
        ],
        { duration: lost || wheelspin ? 1_050 : 1_450, easing: 'cubic-bezier(.2,.8,.2,1)' },
      );
    }
  }

  private resultsVisible(): boolean {
    return this.resultsCareer.visible;
  }

  private toggleSettings(force?: boolean): void {
    const overlay = this.root.querySelector<HTMLElement>('#settings-overlay');
    if (!overlay) return;
    const opening = force ?? !this.settingsOpen;
    // The final-order overlay owns the screen once a race ends; stacking the
    // settings dialog on top of it also dangles a RESUME RACE action into a
    // finished race.
    if (opening && this.resultsVisible()) return;
    this.settingsOpen = opening;
    if (opening) {
      this.cancelOfflineAutoLaunch();
      // Offline the dialog freezes the simulation, so it is a real pause menu.
      const live = this.lastPhase === 'countdown' || this.lastPhase === 'racing';
      const eyebrow = this.root.querySelector<HTMLElement>('#settings-eyebrow');
      if (eyebrow) eyebrow.textContent = this.localRace && live ? t('RACE PAUSED // OFFLINE') : t('RACE SYSTEMS');
      const quit = this.root.querySelector<HTMLButtonElement>('#settings-quit');
      if (quit) quit.hidden = !live;
      const restart = this.root.querySelector<HTMLButtonElement>('#settings-restart');
      if (restart) restart.hidden = !this.canRestartFromPause();
      this.renderPauseStatus(live);
    }
    if (!this.settingsOpen && overlay.contains(document.activeElement)) {
      this.root.querySelector<HTMLButtonElement>('#settings-toggle')?.focus();
    }
    overlay.inert = !this.settingsOpen;
    overlay.classList.toggle('hidden', !this.settingsOpen);
    overlay.setAttribute('aria-hidden', String(!this.settingsOpen));
    mustElement('#race-stage').classList.toggle('settings-open', this.settingsOpen);
    this.game?.setControlsEnabled(!this.settingsOpen);
    this.game?.setGamepadDiagnosticsEnabled(this.settingsOpen);
    this.connection?.setPaused?.(this.localRace && this.settingsOpen);
    this.pwa.setWakeLockActive(!this.settingsOpen && (this.lastPhase === 'countdown' || this.lastPhase === 'racing'));
    if (this.settingsOpen) {
      this.syncSettingsForm();
      this.controlsPanel?.open();
      window.setTimeout(() => {
        const target = this.controlsPanel?.defaultFocus() ?? this.root.querySelector<HTMLButtonElement>('#settings-close');
        target?.focus({ preventScroll: true });
      }, 0);
    } else {
      this.controlsPanel?.close();
      this.scheduleOfflineAutoLaunch();
    }
    audioHub.setPaused(this.settingsOpen);
    if (this.settingsOpen) this.audioMeter.start();
    else this.audioMeter.stop();
    this.gamepadUi.refresh();
  }

  /** Race-at-a-glance strip under the pause header, read from the live HUD. */
  private renderPauseStatus(live: boolean): void {
    const strip = this.root.querySelector<HTMLElement>('#pause-status');
    if (!strip) return;
    strip.hidden = !live;
    const heading = this.root.querySelector<HTMLElement>('#settings-title');
    if (heading) heading.textContent = !live ? t('SETTINGS') : this.localRace ? t('PAUSED') : t('RACE MENU');
    if (!live) return;
    const hud = (selector: string): string => this.root.querySelector(selector)?.textContent?.trim() ?? '';
    const map = this.currentRaceMap();
    const cells: Array<[string, string]> = [
      [t('POSITION'), `${hud('#place-value')} ${hud('#race-position-total')}`.trim()],
      [t('LAP'), hud('#lap-value')],
      [t('INTEGRITY'), hud('#integrity-value') ? `${Number(hud('#integrity-value')) || 0}%` : ''],
    ];
    strip.replaceChildren();
    const event = document.createElement('div');
    event.className = 'pause-status-event';
    const title = document.createElement('b');
    title.textContent = map?.name ?? '';
    const mode = document.createElement('small');
    mode.textContent = [hud('#hud-event-mode'), map ? t(map.location) : ''].filter(Boolean).join(' · ');
    event.append(title, mode);
    strip.append(event);
    for (const [label, value] of cells) {
      if (!value) continue;
      const cell = document.createElement('div');
      const small = document.createElement('small');
      small.textContent = label;
      const strong = document.createElement('strong');
      strong.textContent = value;
      cell.append(small, strong);
      strip.append(cell);
    }
  }

  private applySettings(): void {
    if (!this.benchmarkRequest) saveGameSettings(this.gameSettings);
    this.syncHudEffects();
    this.applyAudioMix();
    const summary = this.game?.applyGraphicsSettings(this.gameSettings);
    if (summary) mustElement('#graphics-summary').textContent = summary;
    this.watchRaceEnginePreparation();
  }

  private watchRaceEnginePreparation(): void {
    const game = this.game;
    if (!game?.isPreparing || this.preparationWatchGame === game) return;
    const generation = this.runtimeGeneration;
    this.preparationWatchGame = game;
    this.raceEngineReady = false;
    this.syncHostUi();
    void game.prepare().then(() => {
      if (generation !== this.runtimeGeneration || this.game !== game) return;
      this.preparationWatchGame = null;
      this.raceEngineReady = true;
      this.syncHostUi();
      this.root.querySelector<HTMLElement>('#race-stage')?.classList.remove('engine-loading');
    }).catch((error: unknown) => {
      if (generation !== this.runtimeGeneration || this.game !== game) return;
      this.preparationWatchGame = null;
      this.raceEngineReady = false;
      this.syncHostUi();
      this.toast(error instanceof Error ? t(error.message) : t('Graphics preparation failed'), 'error');
    });
  }

  private updateCameraShakeLabel(): void {
    const label = this.root.querySelector<HTMLElement>('#camera-shake-value');
    if (label) label.textContent = this.gameSettings.cameraShake <= 0 ? t('OFF') : `${Math.round(this.gameSettings.cameraShake * 100)}%`;
  }

  private diagnosticReport(): ReturnType<typeof createDiagnosticReport> {
    return createDiagnosticReport(this.diagnostics, this.connection?.exportReplay?.() ?? []);
  }

  private syncSettingsForm(): void {
    const quality = this.root.querySelector<HTMLSelectElement>('#graphics-quality');
    if (!quality) return;
    quality.value = this.gameSettings.graphicsQuality;
    mustElement<HTMLInputElement>('#graphics-bloom').checked = this.gameSettings.bloom;
    mustElement<HTMLInputElement>('#graphics-shadows').checked = this.gameSettings.shadows;
    mustElement<HTMLInputElement>('#graphics-screen-space').checked = this.gameSettings.screenSpaceLighting;
    mustElement<HTMLInputElement>('#graphics-car-reflections').checked = this.gameSettings.carReflections;
    this.refreshGraphicsPanels();
    mustElement<HTMLInputElement>('#graphics-adaptive').checked = this.gameSettings.adaptivePerformance;
    mustElement<HTMLInputElement>('#personal-ghost').checked = this.gameSettings.personalGhost;
    mustElement<HTMLInputElement>('#camera-shake').value = String(this.gameSettings.cameraShake);
    mustElement<HTMLInputElement>('#soft-flashes').checked = this.gameSettings.softFlashes;
    mustElement<HTMLInputElement>('#screen-distortion').checked = this.gameSettings.screenDistortion;
    this.updateCameraShakeLabel();
    mustElement<HTMLSelectElement>('#frame-rate-target').value = this.gameSettings.frameRateTarget;
    mustElement<HTMLInputElement>('#motion-sensitivity').value = String(this.gameSettings.motionSensitivity);
    mustElement<HTMLInputElement>('#motion-dead-zone').value = String(this.gameSettings.motionDeadZone);
    mustElement<HTMLSelectElement>('#motion-response').value = this.gameSettings.motionResponseCurve;
    mustElement<HTMLSelectElement>('#mobile-layout').value = this.gameSettings.mobileControlLayout;
    mustElement<HTMLInputElement>('#mobile-control-size').value = String(this.gameSettings.mobileControlSize);
    mustElement<HTMLInputElement>('#mobile-control-opacity').value = String(this.gameSettings.mobileControlOpacity);
    mustElement<HTMLInputElement>('#mobile-haptics').checked = this.gameSettings.mobileHaptics;
    mustElement<HTMLSelectElement>('#mobile-steering-assist').value = this.gameSettings.steeringAssistMode;
    mustElement<HTMLInputElement>('#mobile-auto-throttle').checked = this.gameSettings.mobileAutoThrottle;
    mustElement<HTMLInputElement>('#driving-coach').checked = this.gameSettings.drivingCoach;
    this.updateMotionSensitivityLabel();
    this.updateMobileSettingsLabels();
    this.syncAudioControls();
  }

  private applyAudioMix(): void {
    audioHub.setMix({
      master: this.gameSettings.masterVolume,
      music: this.gameSettings.musicVolume,
      effects: this.gameSettings.effectsVolume,
      ui: this.gameSettings.uiVolume,
      dynamics: this.gameSettings.audioDynamics,
    });
  }

  private syncAudioControls(): void {
    syncAudioMixers(this.root, this.gameSettings, audioHub.isMuted);
    const raceToggle = this.root.querySelector<HTMLButtonElement>('#sound-toggle');
    if (raceToggle) {
      raceToggle.textContent = audioHub.isMuted ? t('SOUND OFF') : t('SOUND ON');
      raceToggle.setAttribute('aria-pressed', String(!audioHub.isMuted));
    }
  }

  /** One delegated handler drives every mixer (settings sheet + menu panel). */
  private bindAudioControls(): void {
    this.root.addEventListener('input', (event) => {
      const input = (event.target as HTMLElement | null)?.closest?.<HTMLInputElement>('[data-audio-mix]');
      if (!input) return;
      const channel = input.dataset.audioMix as AudioMixChannel;
      const value = Number(input.value);
      this.gameSettings[audioChannelSettingKey(channel)] = value;
      this.applySettings();
      this.syncAudioControls();
      if (channel === 'effects') this.uiSounds.previewEffects();
      else this.uiSounds.slider(value);
    });
    this.root.addEventListener('click', (event) => {
      const target = (event.target as HTMLElement | null)?.closest?.<HTMLElement>('[data-audio-mute],[data-audio-dynamics],[data-audio-test]');
      if (!target) return;
      if (target.hasAttribute('data-audio-mute')) {
        const muted = audioHub.toggleMuted();
        if (!muted) this.uiSounds.toggle(true);
      } else if (target.dataset.audioDynamics) {
        this.gameSettings.audioDynamics = target.dataset.audioDynamics as GameSettings['audioDynamics'];
        this.applySettings();
        this.syncAudioControls();
        this.uiSounds.click();
      } else {
        this.uiSounds.previewEffects();
        window.setTimeout(() => this.uiSounds.confirm(), 260);
      }
    });
    // The quick panel closes like a popover: outside press or Escape.
    document.addEventListener('pointerdown', (event) => {
      const panel = this.root.querySelector<HTMLDetailsElement>('#audio-quick');
      if (panel?.open && !panel.contains(event.target as Node)) panel.open = false;
    });
    document.addEventListener('keydown', (event) => {
      const panel = this.root.querySelector<HTMLDetailsElement>('#audio-quick');
      if (event.key !== 'Escape' || !panel?.open) return;
      panel.open = false;
      panel.querySelector('summary')?.focus();
    });
    this.root.addEventListener('toggle', (event) => {
      const details = event.target as HTMLDetailsElement;
      if (details.id !== 'audio-quick') return;
      if (details.open) {
        audioHub.ensure();
        this.syncAudioControls();
        this.audioMeter.start();
      } else if (!this.settingsOpen) {
        this.audioMeter.stop();
      }
    }, true);
  }

  private updateMotionSensitivityLabel(): void {
    const range = this.root.querySelector('#motion-sensitivity-value');
    const deadZone = this.root.querySelector('#motion-dead-zone-value');
    if (range) range.textContent = `${Math.round(24 / Math.sqrt(this.gameSettings.motionSensitivity))}°`;
    if (deadZone) deadZone.textContent = `${this.gameSettings.motionDeadZone.toFixed(1)}°`;
  }

  private updateMobileSettingsLabels(): void {
    const size = this.root.querySelector('#mobile-control-size-value');
    const opacity = this.root.querySelector('#mobile-control-opacity-value');
    if (size) size.textContent = `${Math.round(this.gameSettings.mobileControlSize * 100)}%`;
    if (opacity) opacity.textContent = `${Math.round(this.gameSettings.mobileControlOpacity * 100)}%`;
  }

  private updatePerformanceStatus(status: PerformanceStatus): void {
    this.lastPerformanceStatus = status;
    if (this.localRace) {
      const constrained = status.thermalFallback || status.budgetExceeded;
      const degraded = status.mode !== 'steady' || status.frameP95Ms > (1_000 / status.targetFps) * 1.3;
      this.connection?.setSnapshotInterval?.(
        status.targetFps <= 20 ? 125 : constrained ? 100 : degraded ? 1_000 / 12 : 50,
      );
    }
    this.updateBenchmarkPerformance(status);
    const output = this.root.querySelector<HTMLOutputElement>('#performance-live');
    if (!output) return;
    output.dataset.mode = status.mode;
    // Players get a plain verdict up front; the scene-budget alert (draw
    // calls/triangles) is a developer signal and fired at a locked 60 FPS.
    const lagging = status.fps < status.targetFps * 0.9 || status.frameP95Ms > (1_000 / status.targetFps) * 1.3;
    output.dataset.budget = lagging ? 'over' : 'ok';
    const simulation = status.cpuMs !== null ? ` · SIM ${status.cpuMs.toFixed(1)}` : '';
    const submission = status.renderSubmitMs !== null ? ` · SUBMIT ${status.renderSubmitMs.toFixed(1)}` : '';
    const gpu = status.gpuMs !== null ? ` · GPU ${status.gpuMs.toFixed(1)}` : '';
    const snapshot = status.snapshotP95Ms !== undefined && status.snapshotP95Ms !== null
      ? ` · SNAP ${status.snapshotP95Ms.toFixed(1)}`
      : '';
    const longTasks = status.longTaskCount !== undefined
      ? ` · LT ${status.longTaskCount}/${(status.longTaskTotalMs ?? 0).toFixed(0)}MS`
      : '';
    const sceneCost = status.drawCalls !== undefined ? ` · ${status.drawCalls} CALLS` : '';
    const geometryCost = status.triangles !== undefined ? ` · ${Math.round(status.triangles / 1_000)}K TRIS` : '';
    const limiter = status.limiter !== 'none' ? ` · ${status.limiter.toUpperCase()} CONTROL` : '';
    const fallback = status.thermalFallback ? ` · THERMAL ${status.targetFps}` : '';
    const budget = ` · ${status.budgetExceeded ? t('SCENE BUDGET HIGH') : t('ON BUDGET')}`;
    const verdict = lagging ? t('FRAME RATE DIPPING — TRY A LOWER PRESET') : t('RUNNING SMOOTHLY');
    output.innerHTML = '';
    const headline = document.createElement('b');
    headline.textContent = `${verdict} · ${status.fps}/${status.targetFps} FPS`;
    const detail = document.createElement('small');
    detail.textContent = `P50 ${status.frameP50Ms.toFixed(1)} · P95 ${status.frameP95Ms.toFixed(1)} · P99 ${status.frameP99Ms.toFixed(1)} MS${simulation}${submission}${gpu}${snapshot}${longTasks} · ${Math.round(status.renderScale * 100)}% SCALE${sceneCost}${geometryCost}${limiter}${fallback}${budget}`;
    // Frame-time internals are a developer readout; players only get the verdict.
    if (import.meta.env.DEV || this.diagnosticsEnabled) output.append(headline, detail);
    else output.append(headline);
  }

  private updateBenchmarkPerformance(status: PerformanceStatus): void {
    const recorder = this.benchmarkRecorder;
    if (!recorder || this.latestSnapshot?.phase !== 'racing') return;
    const now = performance.now();
    const result = recorder.record(status, now, benchmarkHeapBytes());
    const progress = recorder.progress(now);
    const fill = this.root.querySelector<HTMLElement>('#benchmark-progress');
    const live = this.root.querySelector<HTMLElement>('#benchmark-live');
    if (fill) fill.style.width = `${(progress * 100).toFixed(1)}%`;
    if (live) live.textContent = result
      ? `${result.passed ? 'PASS' : 'NEEDS WORK'} // ${result.medianFps} FPS · P95 ${result.frameP95Ms.toFixed(1)} MS`
      : `${status.fps}/${status.targetFps} FPS · P95 ${status.frameP95Ms.toFixed(1)} MS · ${status.drawCalls ?? 0} CALLS · ${Math.round((status.triangles ?? 0) / 1_000)}K TRIS`;
    if (result) this.finishBenchmarkUi(result);
  }

  private finishBenchmarkUi(result: MobileBenchmarkResult): void {
    window.__nightshiftBenchmark = result;
    const panel = this.root.querySelector<HTMLElement>('#benchmark-panel');
    const output = this.root.querySelector<HTMLElement>('#benchmark-result');
    if (panel) panel.dataset.result = result.passed ? 'pass' : 'fail';
    if (output) {
      output.hidden = false;
      output.textContent = JSON.stringify(result, null, 2);
    }
  }

  private updateRendererRecovery(status: RendererRecoveryStatus): void {
    this.diagnostics.record('presentation', 'renderer-context', { status });
    const stage = this.root.querySelector<HTMLElement>('#race-stage');
    const notice = this.root.querySelector<HTMLElement>('#graphics-recovery');
    stage?.setAttribute('data-graphics-context', status);
    if (notice) notice.hidden = status !== 'lost';
    if (status === 'restored') this.toast(t('Graphics recovered — race resumed'), 'info', { quiet: true });
  }

  private loadRaceGameModule(): Promise<typeof import('./game/race-game.js')> {
    this.raceGameModulePromise ??= import('./game/race-game.js').catch((error: unknown) => {
      // A failed chunk fetch (flaky network, mid-session deploy) must not
      // brick every later race mount in this page session.
      this.raceGameModulePromise = null;
      throw error;
    });
    return this.raceGameModulePromise;
  }

  private loadLocalRaceModule(): Promise<typeof import('./local-race.js')> {
    this.localRaceModulePromise ??= import('./local-race.js').catch((error: unknown) => {
      this.localRaceModulePromise = null;
      throw error;
    });
    return this.localRaceModulePromise;
  }

  private async preloadRaceRuntime(local: boolean): Promise<void> {
    const modules: Promise<unknown>[] = [this.loadRaceGameModule()];
    if (local) modules.push(this.loadLocalRaceModule());
    await Promise.all(modules).catch(() => undefined);
  }

  private updateGhostStatus(status: GhostStatus): void {
    this.currentGhostStatus = status;
    const persistenceChanged = status.persistence !== this.lastGhostPersistence;
    if (persistenceChanged) this.syncResultsGhostShare();
    const ui = this.raceUiElements;
    if (!ui) return;
    const now = performance.now();
    if (!status.newBest && !persistenceChanged && now - this.lastGhostUiAt < 100) return;
    this.lastGhostUiAt = now;
    const previousPersistence = this.lastGhostPersistence;
    this.lastGhostPersistence = status.persistence;
    const { ghostHud: hud, ghostBest: best, ghostDelta: delta, ghostSectors: sectors, ghostMedal: medal } = ui;
    hud.classList.toggle('available', status.active);
    // Ghosts only record in clean time trials. Elsewhere, with no stored
    // reference, the panel would show nothing but placeholder dashes.
    hud.classList.toggle('idle', !status.recording && status.referenceLapMs === null && !status.challengeName);
    hud.classList.toggle('no-reference', status.referenceLapMs === null);
    hud.classList.toggle('ahead', status.deltaMs !== null && status.deltaMs < 0);
    hud.classList.toggle('behind', status.deltaMs !== null && status.deltaMs > 0);
    hud.classList.toggle('save-pending', status.persistence === 'pending');
    hud.classList.toggle('save-failed', status.persistence === 'failed');
    const ghostLabel = hud.querySelector<HTMLElement>('small');
    if (ghostLabel) {
      setText(ghostLabel, status.challengeName
        ? t('{name} GHOST', { name: status.challengeName.toUpperCase() })
        : status.persistence === 'pending'
          ? t('PERSONAL BEST · SAVING')
          : status.persistence === 'failed' ? t('PERSONAL BEST · UNSAVED') : t('PERSONAL BEST'));
    }
    setText(best, status.referenceLapMs === null ? '--:--.---' : formatLapTime(status.referenceLapMs));
    setText(delta, status.deltaMs === null
      ? status.sectorDeltaMs !== null
        ? t('SPLIT {delta} S', { delta: `${status.sectorDeltaMs < 0 ? '−' : '+'}${formatNumber(Math.abs(status.sectorDeltaMs) / 1_000, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` })
        : status.recording
          ? status.active
            ? status.challengeName ? t('RACING {name}', { name: status.challengeName.toUpperCase() }) : t('RACING PERSONAL GHOST')
            : t('RECORDING FIRST LAP')
          : t('GHOST STANDBY')
      : `${status.deltaMs < 0 ? '−' : '+'}${formatNumber(Math.abs(status.deltaMs) / 1_000, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} S`);
    const sectorsSignature = `${status.currentSector ?? 0}:${status.referenceSectorTimesMs.join(':')}`;
    if (sectorsSignature !== this.lastGhostSectorsSignature) {
      this.lastGhostSectorsSignature = sectorsSignature;
      sectors.innerHTML = [0, 1, 2].map((index) => {
        const value = status.referenceSectorTimesMs[index];
        const active = status.currentSector === index + 1;
        return `<i class="${active ? 'active' : ''}">S${index + 1} ${value ? formatSectorTime(value) : '--.--'}</i>`;
      }).join('');
    }
    const bestMedal = medalLabel(status.bestLapMs, status.targets);
    medal.dataset.medal = bestMedal.toLowerCase();
    setText(medal, status.challengeName
      ? t('BEAT {name}', { name: status.challengeName.toUpperCase() })
      : bestMedal === 'NO MEDAL'
        ? t('GOLD {time}', { time: formatLapTime(status.targets.goldMs) })
        : t('{medal} RECORD', { medal: t(bestMedal) }));
    if (status.newBest) {
      hud.classList.add('new-best');
      window.setTimeout(() => hud.classList.remove('new-best'), 2_400);
      this.toast(
        status.persistence === 'pending'
          ? t('New personal best // {time} · saving', { time: formatLapTime(status.bestLapMs ?? 0) })
          : t('New personal best // {time}', { time: formatLapTime(status.bestLapMs ?? 0) }),
        'info',
      );
    }
    if (persistenceChanged && status.persistence === 'failed' && previousPersistence !== 'failed') {
      this.toast(t('Personal best is active in this race but could not be saved to this browser.'), 'error');
    }
  }

  private syncResultsGhostShare(): void {
    const button = this.root.querySelector<HTMLButtonElement>('#results-share-ghost');
    const results = this.root.querySelector<HTMLElement>('#results-overlay');
    if (!button || !results || results.classList.contains('hidden')) return;
    const setup = this.currentLapGhostSetup();
    const status = this.currentGhostStatus;
    const eligible = Boolean(this.room && lapGhostRecordingEligible(this.room.settings))
      && Boolean(setup)
      && status?.bestLapMs != null;
    button.hidden = !eligible;
    if (!eligible || !setup || !status) {
      button.disabled = true;
      return;
    }
    const durable = status.persistence === 'saved'
      && Boolean(loadLapGhost(this.room?.settings.mapId ?? '', setup));
    button.disabled = !durable;
    button.textContent = status.persistence === 'pending'
      ? t('SAVING GHOST…')
      : status.persistence === 'failed'
        ? t('GHOST NOT SAVED')
        : this.room?.settings.dailyChallengeId
          ? t('SHARE DAILY GHOST + QR ↗')
          : t('SHARE GHOST + QR ↗');
  }

  private updateGamepadStatus(status: GamepadStatus): void {
    const wasConnected = this.gamepadStatus.connected;
    const previousFamily = this.gamepadStatus.family;
    this.gamepadStatus = status;
    const stage = this.root.querySelector('#race-stage');
    stage?.classList.toggle('gamepad-connected', status.connected);

    const badge = this.root.querySelector<HTMLElement>('#gamepad-status');
    const badgeName = this.root.querySelector<HTMLElement>('#gamepad-name');
    if (badge) badge.hidden = !status.connected;
    if (badgeName) badgeName.textContent = status.name;

    this.controlsPanel?.updateStatus(status);
    const mappingSignature = status.mapping ? JSON.stringify(status.mapping) : '';
    if (mappingSignature !== this.lastGamepadMappingSignature || previousFamily !== status.family) {
      this.lastGamepadMappingSignature = mappingSignature;
      this.refreshInputGlyphs();
    }

    if (status.connected && !wasConnected) this.toast(t('Controller connected // {name}', { name: status.name }), 'info');
    if (!status.connected && wasConnected) this.toast(t('Controller disconnected — keyboard and touch remain active'), 'info');
  }

  private async enterImmersiveMode(): Promise<void> {
    const active = await this.pwa.enterImmersiveMode();
    this.syncLandscapeFullscreenPrompt();
    this.toast(active ? t('Fullscreen race mode enabled') : t('Fullscreen mode is unavailable in this browser'),active ? 'info' : 'error', { quiet: active });
  }

  private syncLandscapeFullscreenPrompt(): void {
    const stage = this.root.querySelector('#race-stage');
    if (!stage) return;
    const landscape = window.matchMedia('(orientation: landscape)').matches;
    const installed = window.matchMedia('(display-mode: standalone)').matches
      || window.matchMedia('(display-mode: fullscreen)').matches;
    const fullscreenAvailable = document.fullscreenEnabled
      && typeof document.documentElement.requestFullscreen === 'function';
    stage.classList.toggle(
      'landscape-fullscreen-needed',
      this.deviceCapabilities.touchDevice && landscape && fullscreenAvailable && !installed && !document.fullscreenElement,
    );
  }

  private async toggleMotionSteering(): Promise<void> {
    if (!this.game || !this.deviceCapabilities.motionSteering) return;
    const game = this.game;
    const generation = this.runtimeGeneration;
    const enabled = this.motionStatus !== 'active' && this.motionStatus !== 'waiting';
    const status = await game.configureMotionSteering(enabled, this.gameSettings.motionSensitivity);
    if (generation !== this.runtimeGeneration || this.game !== game) return;
    this.gameSettings.motionSteeringEnabled = status === 'active' || status === 'waiting';
    saveGameSettings(this.gameSettings);
    this.updateMotionStatus(status);
  }

  private async initialiseMotionSteering(): Promise<void> {
    if (!this.game || !this.deviceCapabilities.motionSteering) {
      this.updateMotionStatus('unavailable');
      return;
    }
    if (!this.gameSettings.motionSteeringEnabled) {
      this.updateMotionStatus('off');
      return;
    }
    if (motionPermissionRequiresGesture() && !motionAccessGranted()) {
      this.updateMotionStatus('off');
      this.root.querySelector('#race-stage')?.classList.add('motion-permission-required');
      window.setTimeout(() => this.root.querySelector<HTMLButtonElement>('#motion-permission-enable')?.focus(), 0);
      return;
    }
    const game = this.game;
    const generation = this.runtimeGeneration;
    const status = await game.configureMotionSteering(true, this.gameSettings.motionSensitivity);
    if (generation !== this.runtimeGeneration || this.game !== game) return;
    this.updateMotionStatus(status);
  }

  private async enableMotionSteering(): Promise<void> {
    if (!this.game || !this.deviceCapabilities.motionSteering) return;
    const game = this.game;
    const generation = this.runtimeGeneration;
    const button = this.root.querySelector<HTMLButtonElement>('#motion-permission-enable');
    if (button) button.disabled = true;
    const status = await game.configureMotionSteering(true, this.gameSettings.motionSensitivity);
    if (generation !== this.runtimeGeneration || this.game !== game) return;
    this.gameSettings.motionSteeringEnabled = status === 'active' || status === 'waiting';
    saveGameSettings(this.gameSettings);
    this.root.querySelector('#race-stage')?.classList.remove('motion-permission-required');
    if (button) button.disabled = false;
    this.updateMotionStatus(status);
    this.focusRaceUi();
  }

  private useTouchSteering(): void {
    this.gameSettings.motionSteeringEnabled = false;
    saveGameSettings(this.gameSettings);
    void this.game?.configureMotionSteering(false, this.gameSettings.motionSensitivity);
    this.root.querySelector('#race-stage')?.classList.remove('motion-permission-required');
    this.updateMotionStatus('off');
    this.focusRaceUi();
  }

  private focusRaceUi(): void {
    const ready = this.root.querySelector<HTMLButtonElement>('#lobby-overlay:not(.hidden) [data-focus-key="ready"]:not([disabled])');
    (ready ?? this.root.querySelector<HTMLButtonElement>('#settings-toggle'))?.focus();
    this.scheduleOfflineAutoLaunch();
  }

  private updateMotionStatus(status: MotionSteeringStatus): void {
    this.motionStatus = status;
    if (status !== 'active' && this.tiltSetupTimer !== undefined) {
      window.clearInterval(this.tiltSetupTimer);
      this.tiltSetupTimer = undefined;
      const setup = this.root.querySelector<HTMLElement>('#tilt-setup');
      if (setup) setup.hidden = true;
      const calibration = this.root.querySelector<HTMLButtonElement>('#motion-calibrate');
      if (calibration) {
        calibration.disabled = false;
        calibration.textContent = t('CALIBRATE TILT');
      }
    }
    const stage = this.root.querySelector('#race-stage');
    if (stage) {
      // Keep touch steering available until Android delivers the first valid
      // sensor sample. Older devices can take seconds or never report one.
      stage.classList.toggle('motion-steering-active', status === 'active');
      (stage as HTMLElement).dataset.motionStatus = status;
    }
    const output = this.root.querySelector('#motion-status');
    const toggle = this.root.querySelector<HTMLButtonElement>('#motion-toggle');
    const calibrate = this.root.querySelector<HTMLButtonElement>('#motion-calibrate');
    const indicator = this.root.querySelector<HTMLButtonElement>('#motion-steering-indicator');
    if (!output || !toggle || !calibrate || !indicator) return;
    const messages: Record<MotionSteeringStatus, string> = {
      off: t('Tilt steering is off. Enable it, hold the device naturally, then tilt to steer.'),
      waiting: t('Waiting for orientation data… move the device slightly.'),
      active: t('Tilt steering active. Disable it here to bring back the steering swiper.'),
      denied: t('Motion permission was denied. You can retry or steer with the swiper.'),
      unavailable: window.isSecureContext
        ? t('No sensor data arrived. The steering swiper is active; retry Tilt when the device sensor is available.')
        : t('Tilt steering requires HTTPS on Android. The steering swiper remains available on this HTTP connection.'),
    };
    output.textContent = messages[status];
    const permission = this.root.querySelector<HTMLElement>('#sensor-permission');
    if (permission) permission.textContent = status === 'denied'
      ? t('DENIED')
      : status === 'active'
        ? t('ACTIVE')
        : status === 'waiting'
          ? t('GRANTED / WAITING')
          : status === 'unavailable'
            ? t('NO DATA')
            : t('NOT REQUESTED');
    const runtime = detectMotionRuntime();
    const context = this.root.querySelector<HTMLElement>('#sensor-context');
    const browser = this.root.querySelector<HTMLElement>('#sensor-runtime');
    if (context) context.textContent = runtime.secureContext ? t('HTTPS / TRUSTED') : t('HTTP / SENSOR BLOCKED');
    if (browser) browser.textContent = runtime.state === 'missing'
      ? t('NO SENSOR API')
      : runtime.legacyChromium
        ? t('LEGACY CHROME · {mode}', { mode: runtime.orientationApi ? t('ORIENTATION') : t('ACCEL FALLBACK') })
        : runtime.orientationApi && runtime.accelerationApi
          ? t('ORIENTATION + ACCEL')
          : runtime.orientationApi ? t('ORIENTATION ONLY') : t('ACCEL FALLBACK');
    toggle.disabled = !this.deviceCapabilities.motionSteering;
    toggle.textContent = status === 'active' || status === 'waiting'
      ? t('DISABLE TILT')
      : status === 'unavailable' || status === 'denied'
        ? t('RETRY TILT STEERING')
        : t('ENABLE TILT STEERING');
    calibrate.hidden = status !== 'active';
    const indicatorLabel = indicator.querySelector('span');
    const indicatorHint = indicator.querySelector('small');
    const tiltEnabled = status === 'active' || status === 'waiting';
    indicator.style.setProperty('--motion-steer-offset', '0px');
    if (indicatorLabel) indicatorLabel.textContent = status === 'active'
      ? t('TILT MODE')
      : status === 'waiting'
        ? t('TILT STARTING')
        : t('TOUCH MODE');
    if (indicatorHint) indicatorHint.textContent = tiltEnabled ? t('TAP FOR SWIPER') : t('TAP FOR TILT');
    indicator.setAttribute('aria-pressed', String(tiltEnabled));
  }

  private updateMotionReading(reading: MotionSensorReading): void {
    const source = this.root.querySelector<HTMLElement>('#sensor-source');
    if (!source) return;
    setText(source, reading.source === 'orientation' ? t('DEVICE ORIENTATION') : t('GRAVITY ACCELEROMETER'));
    setText(mustElement('#sensor-axis'), `${reading.axis.toUpperCase()} @ ${reading.orientationAngle}°`);
    const raw = reading.source === 'orientation'
      ? `β ${formatSensor(reading.beta)}° · γ ${formatSensor(reading.gamma)}°`
      : `X ${formatSensor(reading.x)} · Y ${formatSensor(reading.y)} · Z ${formatSensor(reading.z)}`;
    setText(mustElement('#sensor-raw'), raw);
    setText(
      mustElement('#sensor-tilt'),
      `${reading.tilt.toFixed(1)}° / ZERO ${reading.neutral.toFixed(1)}° · DZ ${reading.deadZone.toFixed(1)}° · L/R ${reading.negativeSpan.toFixed(0)}°/${reading.positiveSpan.toFixed(0)}°`,
    );
    setText(mustElement('#sensor-steer'), `${reading.steer >= 0 ? '+' : ''}${reading.steer.toFixed(2)}`);
    setText(mustElement('#sensor-samples'), String(reading.samples));
    const setup = this.root.querySelector<HTMLElement>('#tilt-setup');
    if (setup) {
      setup.style.setProperty('--tilt-live', `${50 - reading.steer * 44}%`);
      const live = setup.querySelector<HTMLElement>('[data-tilt-live]');
      if (live) live.textContent = `${reading.steer > 0.02 ? t('LEFT') : reading.steer < -0.02 ? t('RIGHT') : t('CENTRE')} ${Math.round(Math.abs(reading.steer) * 100)}%`;
      if (this.tiltSetupTimer !== undefined) {
        this.tiltSetupLeft = Math.max(this.tiltSetupLeft, reading.steer);
        this.tiltSetupRight = Math.min(this.tiltSetupRight, reading.steer);
        setup.classList.toggle('left-ready', this.tiltSetupLeft >= 0.35);
        setup.classList.toggle('right-ready', this.tiltSetupRight <= -0.35);
      }
    }
    const indicator = this.root.querySelector<HTMLButtonElement>('#motion-steering-indicator');
    if (indicator && this.motionStatus === 'active') {
      indicator.style.setProperty('--motion-steer-offset', `${motionIndicatorOffsetPx(reading.steer).toFixed(2)}px`);
      const hint = indicator.querySelector('small');
      if (hint) {
        const percentage = Math.round(Math.abs(reading.steer) * 100);
        hint.textContent = percentage < 2
          ? t('CENTRED 0%')
          : `${reading.steer > 0 ? t('LEFT') : t('RIGHT')} ${percentage}%`;
      }
    }
  }

  private startTiltSetup(): void {
    if (!this.game || this.motionStatus !== 'active') {
      this.toast(t('Enable Tilt and wait for live sensor data before calibration'), 'error');
      return;
    }
    if (this.tiltSetupTimer !== undefined) window.clearInterval(this.tiltSetupTimer);
    this.game.calibrateMotionSteering();
    this.tiltSetupStartedAt = performance.now();
    this.tiltSetupLeft = 0;
    this.tiltSetupRight = 0;
    const setup = mustElement<HTMLElement>('#tilt-setup');
    const button = mustElement<HTMLButtonElement>('#motion-calibrate');
    setup.hidden = false;
    setup.classList.remove('left-ready', 'right-ready', 'complete');
    setup.dataset.phase = 'centre';
    setup.style.setProperty('--setup-progress', '0%');
    button.disabled = true;
    button.textContent = t('CALIBRATING…');
    const tick = (): void => {
      const elapsed = performance.now() - this.tiltSetupStartedAt;
      const progress = Math.min(1, elapsed / 10_000);
      setup.style.setProperty('--setup-progress', `${(progress * 100).toFixed(1)}%`);
      const instruction = setup.querySelector<HTMLElement>('[data-tilt-instruction]');
      if (elapsed < 2_400) {
        setup.dataset.phase = 'centre';
        if (instruction) instruction.textContent = t('Hold the device naturally and keep it centred');
      } else {
        setup.dataset.phase = 'range';
        if (instruction) instruction.textContent = t('Tilt comfortably left and right — no need to force full travel');
      }
      if (elapsed < 10_000) return;
      if (this.tiltSetupTimer !== undefined) window.clearInterval(this.tiltSetupTimer);
      this.tiltSetupTimer = undefined;
      const complete = this.tiltSetupLeft >= 0.35 && this.tiltSetupRight <= -0.35;
      setup.classList.toggle('complete', complete);
      setup.dataset.phase = 'done';
      if (instruction) instruction.textContent = complete
        ? t('Calibration saved — centre and both steering directions detected')
        : t('Neutral saved — drive normally and Tilt will keep learning your range');
      button.disabled = false;
      button.textContent = t('CALIBRATE AGAIN');
      this.toast(complete ? t('Tilt calibration complete') : t('Tilt neutral saved; range will keep learning'),'info', { quiet: true });
    };
    tick();
    this.tiltSetupTimer = window.setInterval(tick, 100);
  }

  private resetSettings(): void {
    this.gameSettings = defaultGameSettings(this.deviceCapabilities);
    this.syncSettingsForm();
    this.controlsPanel?.syncForm();
    this.applySettings();
    if (this.motionStatus === 'active' || this.motionStatus === 'waiting') {
      void this.game?.configureMotionSteering(false, this.gameSettings.motionSensitivity);
    }
    this.updateMotionStatus(this.deviceCapabilities.motionSteering ? 'off' : 'unavailable');
    void this.initialiseMotionSteering();
  }

  private readonly onSettingsKey = (event: KeyboardEvent): void => {
    if (event.code !== 'Escape' || !this.root.querySelector('#settings-overlay')) return;
    event.preventDefault();
    if (this.inviteShareOpen) {
      this.closeInviteShare();
      return;
    }
    // Esc must not stack the settings dialog over the final-order overlay.
    if (!this.settingsOpen && this.resultsVisible()) return;
    this.toggleSettings();
  };

  /** G opens the in-lobby garage while the grid is still forming. */
  private readonly onLobbyShortcut = (event: KeyboardEvent): void => {
    if (event.code !== 'KeyG' || event.ctrlKey || event.metaKey || event.altKey || this.localRace) return;
    const target = event.target as HTMLElement | null;
    if (target && (target.matches('input, textarea, select') || target.isContentEditable)) return;
    if ((this.latestSnapshot?.phase ?? this.room?.phase) !== 'lobby' || this.settingsOpen || this.inviteShareOpen) return;
    event.preventDefault();
    this.openLobbyGarage();
  };

  private readonly onQuickRestartKey = (event: KeyboardEvent): void => {
    if (
      event.code !== 'Backspace'
      || !this.room || raceEventMode(this.room.settings) !== 'time-trial'
      || this.settingsOpen
      || !this.latestSnapshot
      || !canQuickRestart('time-trial', this.latestSnapshot.phase)
    ) return;
    event.preventDefault();
    this.restartSoloRace();
  };

  private readonly onViewportModeChange = (): void => {
    this.syncLandscapeFullscreenPrompt();
  };

  private readonly onConnectivityChange = (): void => {
    const badge = this.root.querySelector<HTMLElement>('.network-badge');
    if (!badge) return;
    const online = navigator.onLine !== false;
    badge.dataset.online = String(online);
    badge.innerHTML = `<i></i> ${online ? t('REAL-TIME RACE NETWORK') : t('OFFLINE SOLO READY')}`;
    badge.title = online ? t('ONLINE') : t('OFFLINE READY');
  };

  private readonly onOfflineReconnect = (): void => window.location.reload();

  private showResults(snapshot: RaceSnapshot, animate = true, focus = true): void {
    this.resultsPresentation.render({
      snapshot,
      room: this.room,
      playerId: this.player?.id,
      currentGhostStatus: this.currentGhostStatus,
      progressionUpdate: this.progressionUpdate,
      vehicleMasteryUpdate: this.vehicleMasteryUpdate,
      activeRaceMap: this.activeRaceMap,
      maps: this.maps,
      nightTour: this.nightTour,
      localRace: this.localRace,
    }, animate);
    this.syncResultsGhostShare();
    if (focus) this.resultsPresentation.focus();
  }

  private bankFinishedRace(snapshot: RaceSnapshot): void {
    const localRacer = snapshot.racers.find((racer) => racer.id === this.player?.id);
    if (!localRacer || !this.room) return;
    const settings = this.room.settings;
    const classifiedRacers = snapshot.racers.filter((racer) => !racer.pursuit);
    const raceId = this.raceProgressionId();
    const generation = ++this.careerCommitGeneration;
    const tourId = settings.tourId;
    const tourStage = settings.tourStage;
    const commit = commitLocalCareerResultWithRetry({
      racer: localRacer,
      racers: classifiedRacers,
      raceId,
      run: this.raceRun,
      career: arcadeCareerEligible(settings.arcadePreset)
        ? {
          totalRacers: classifiedRacers.length,
          context: {
            mapId: settings.mapId,
            npcDifficulty: settings.npcDifficulty,
            timeTrial: raceEventMode(settings) === 'time-trial',
            racers: classifiedRacers,
            bestLapMs: this.currentGhostStatus?.bestLapMs ?? null,
            medal: this.currentGhostStatus?.medal ?? 'none',
            featuredRival: settings.featuredRival,
            now: Date.now(),
          },
        }
        : undefined,
      daily: settings.dailyChallengeId
        ? {
          challengeId: settings.dailyChallengeId,
          lapMs: this.currentGhostStatus?.lastLapMs ?? null,
        }
        : undefined,
      tour: tourId && isStreetTourId(tourId) && tourStage !== undefined
        ? { tourId, stage: tourStage }
        : undefined,
    }).then((result) => {
      if (generation === this.careerCommitGeneration) {
        this.progression = result.progression;
        this.progressionUpdate = result.progressionUpdate;
        this.vehicleMastery = result.vehicleMastery;
        this.vehicleMasteryUpdate = result.vehicleMasteryUpdate;
        this.dailyChallenges = result.dailyChallenges;
        this.nightTour = result.nightTour;
      } else {
        // A party transfer can replace the race runtime while this durable
        // transaction is still settling. Rehydrate the shared career base even
        // though result-specific presentation belongs to the previous race.
        this.reloadLocalCareerState();
      }
      this.syncGarageCareerState();
    }).catch((error) => {
      if (generation !== this.careerCommitGeneration) return;
      this.toast(
        error instanceof Error
          ? t('Local career could not be saved: {reason}', { reason: error.message })
          : t('Local career save could not be completed.'),
        'error',
      );
    });
    this.careerBankPromise = commit;
  }

  /** A solo grid (just the player and AI) can be reset from the pause menu. */
  private canRestartFromPause(): boolean {
    return Boolean(this.room && this.room.playerCount === 1 && !this.restartPending
      && this.latestSnapshot?.phase === 'racing'
      && canQuickRestart(raceEventMode(this.room.settings), 'racing'));
  }

  private restartSoloRace(): void {
    if (
      !this.room
      || this.room.playerCount !== 1
      || !this.latestSnapshot
      || !canQuickRestart(raceEventMode(this.room.settings), this.latestSnapshot.phase)
      || this.restartPending
    ) return;
    this.setRestartPending(true, this.latestSnapshot.phase === 'racing');
    this.connection?.send({ type: 'restart', raceAttempt: this.raceAttempt });
  }

  private acceptPartyTransfer(room: PublicRoom, inviteToken: string): void {
    if (!this.player || this.localRace) return;
    const player = this.player;
    const previousRoomId = this.connectionRoomId || this.room?.id || '';
    const nextInviteToken = inviteToken || this.inviteToken;
    if (!nextInviteToken) {
      this.toast(t('Party moved, but no valid invite credential is available'), 'error');
      return;
    }
    // A successor is a new race runtime, not an in-place room mutation. Tear
    // down the old renderer, director, listeners and transport so the incoming
    // welcome cannot inherit stale phase/UI state from the finished event.
    this.prepareRaceRuntime(player, false, room.id, room.raceAttempt ?? 1);
    this.room = room;
    this.connectionRoomId = room.id;
    this.inviteToken = nextInviteToken;
    this.isHost = room.hostPlayerId === player.id;
    this.routes.pushRace(room.id, nextInviteToken);
    this.rememberRace(room, player, nextInviteToken, previousRoomId);
    this.connection = new RaceConnection({
      roomId: room.id,
      player,
      ...this.transportCallbacks(room.id, false),
    });
    this.toast(t('Party moved together // New grid ready'), 'info');
  }

  private acceptAuthoritativeRestart(): void {
    // Another driver reopening the lobby must not yank this player's result
    // screen away; it stays until they choose BACK TO LOBBY themselves.
    const keepResults = this.room?.phase === 'lobby' && this.resultsCareer.visible && !this.returningToLobby;
    if (!keepResults) this.resultsCareer.reset();
    if (this.room?.phase === 'lobby') {
      this.returningToLobby = false;
      if (keepResults) this.toast(t('The lobby is open again — join when you are done here'), 'info');
    }
    this.raceRun = createRaceRunStats();
    this.progressionUpdate = null;
    this.vehicleMasteryUpdate = null;
    this.lastStyleEventId = -1;
    this.compactCueGate.reset();
    this.clearRaceDirectorCue();
    this.game?.prepareForRestart();
    const map = this.currentRaceMap();
    if (map) this.createRaceDirector(map);
    this.setRestartPending(false);
  }

  private createRaceDirector(map: MapDefinition): void {
    if (!this.room) {
      this.raceDirector = null;
      return;
    }
    const nemesisNames = Object.values(this.progression.rivals)
      .filter((rival) => rival.lossesAgainst > rival.winsAgainst || rival.streak < 0)
      .sort((a, b) => (b.lossesAgainst - b.winsAgainst) - (a.lossesAgainst - a.winsAgainst)
        || b.lastSeenAt - a.lastSeenAt)
      .slice(0, 3)
      .map((rival) => rival.name);
    if (this.room.settings.featuredRival) nemesisNames.unshift(this.room.settings.featuredRival);
    this.raceDirector = new RaceDirector(map, {
      eventMode: raceEventMode(this.room.settings),
      raceLaps: raceLapCount(this.room.settings, map.laps),
      nemesisNames,
      initialPhase: this.room.phase,
    });
  }

  private clearRaceDirectorCue(): void {
    if (this.directorUiTimer !== undefined) this.raceRuntime.scope?.cancelTimeout(this.directorUiTimer);
    this.directorUiTimer = undefined;
    const callout = this.raceUiElements?.directorCallout
      ?? this.root.querySelector<HTMLElement>('#race-director-callout');
    if (callout) {
      for (const animation of callout.getAnimations?.() ?? []) animation.cancel();
      callout.classList.remove('visible');
      callout.replaceChildren();
      delete callout.dataset.tone;
    }
    const stage = this.raceUiElements?.raceStage
      ?? this.root.querySelector<HTMLElement>('#race-stage');
    const challengeHud = this.raceUiElements?.liveChallengeHud
      ?? this.root.querySelector<HTMLElement>('#hud-live-challenge');
    const sectorHud = this.raceUiElements?.sectorHud
      ?? this.root.querySelector<HTMLElement>('#hud-sector-duel');
    stage?.classList.remove('director-active');
    challengeHud?.setAttribute('aria-hidden', String(!challengeHud.classList.contains('visible')));
    sectorHud?.setAttribute('aria-hidden', 'false');
  }

  private showRaceDirectorCue(cue: RaceDirectorCue): void {
    this.clearRaceDirectorCue();
    const callout = this.raceUiElements?.directorCallout;
    if (!callout) return;
    const stage = this.root.querySelector<HTMLElement>('#race-stage');
    const challengeHud = this.raceUiElements?.liveChallengeHud;
    const sectorHud = this.raceUiElements?.sectorHud;
    callout.dataset.tone = cue.tone;
    callout.innerHTML = `<small>${t('RACE CONTROL')}</small><b>${escapeHtml(cue.title)}</b><span>${escapeHtml(cue.detail)}</span>`;
    // Restarting one short compositor-only animation keeps rapid passes legible.
    void callout.offsetWidth;
    callout.classList.add('visible');
    stage?.classList.add('director-active');
    challengeHud?.setAttribute('aria-hidden', 'true');
    sectorHud?.setAttribute('aria-hidden', 'true');
    this.directorUiTimer = this.raceRuntime.scope?.timeout(() => {
      this.directorUiTimer = undefined;
      this.clearRaceDirectorCue();
    }, this.deviceCapabilities.touchDevice ? COMPACT_CUE_DURATION_MS : 2_550);
    this.game?.announceRaceDirector(cue.strength);
  }

  private setRestartPending(pending: boolean, midRace = false): void {
    if (pending) this.raceRuntime.lifecycle.requestRestart(midRace);
    else this.raceRuntime.lifecycle.cancelRestart();
    for (const selector of ['#results-restart', '#time-trial-restart']) {
      const button = this.root.querySelector<HTMLButtonElement>(selector);
      if (button) button.disabled = pending;
    }
  }

  /** Results → the next tour stage or a fresh quick event, via the lobby. */
  private startNextEvent(): void {
    if (!this.localRace || !this.room || !this.player || this.maps.length < 2) return;
    const tourActive = this.room.settings.tourId === this.nightTour.tourId && !this.nightTour.completed;
    const roomId = this.room.id;
    this.discardedOfflineRoomId = roomId;
    void this.clearOfflineCheckpoint(false, roomId);
    this.room = null;
    this.player = null;
    if (tourActive) {
      this.startNightTourFromGarage();
      return;
    }
    this.startSurpriseEventFromGarage();
  }

  private raceProgressionId(): string {
    if (!this.room) return '';
    return this.raceAttempt === 1 ? this.room.id : `${this.room.id}-run-${this.raceAttempt}`;
  }

  private startInputLoop(): void {
    if (this.inputTimer !== undefined) window.clearInterval(this.inputTimer);
    this.inputTimer = this.raceRuntime.scope?.interval(() => {
      if (!this.game) return;
      this.inputSequence += 1;
      this.connection?.sendInput({
        ...this.game.consumeInput(this.inputSequence),
        raceAttempt: this.raceAttempt,
      });
    }, 1_000 / 30);
  }

  private setConnectionStatus(status: ConnectionStatus): void {
    const previous = this.connectionState;
    this.connectionState = status;
    if (status !== previous) this.diagnostics.record('transport', 'status', { previous, status });
    const available = status === 'open' || status === 'local';
    this.game?.setNetworkAvailable(available);
    if (!available) this.latencyMs = null;
    this.renderConnectionStatus();
    if (status === 'reconnecting' && previous === 'open') {
      this.toast(t('Signal lost — local control stays active while race control reconnects'), 'info');
    } else if (status === 'open' && previous === 'reconnecting') {
      this.toast(t('Race control resynchronised'), 'info', { quiet: true });
    }
  }

  private renderConnectionStatus(): void {
    const element = this.root.querySelector<HTMLElement>('#connection-status');
    if (!element) return;
    element.dataset.status = this.connectionState;
    element.dataset.quality = this.connectionState === 'open' ? connectionQuality(this.latencyMs) : '';
    if (this.connectionState === 'open') {
      const ping = displayPing(this.latencyMs);
      element.innerHTML = `${signalBarsHtml(this.latencyMs, true, false)}${t('LIVE')}${ping === null ? '' : ` // ${ping} MS`}`;
      return;
    }
    const label = this.connectionState === 'local'
      ? t('OFFLINE // LOCAL SIM')
      : this.connectionState === 'reconnecting'
        ? t('RECONNECTING // LOCAL CONTROL')
        : tc('connection', this.connectionState.toUpperCase());
    element.innerHTML = `<i></i>${label}`;
  }

  private inviteLink(): string | null {
    if (!this.room || this.localRace) return null;
    return this.routes.absoluteRaceUrl(this.room.id, this.inviteToken);
  }

  private openInviteShare(): void {
    const link = this.inviteLink();
    this.shareLinkOverride = null;
    if (!link) return;
    this.openShareOverlay(link);
  }

  private openGhostChallengeShare(): void {
    if (!this.room || !lapGhostRecordingEligible(this.room.settings)) {
      this.toast(t('Only standard solo Time Trials can record or share career ghosts'), 'error');
      return;
    }
    const mapId = this.room?.settings.mapId ?? '';
    const setup = this.currentLapGhostSetup();
    const ghost = setup ? loadLapGhost(mapId, setup) : null;
    if (!ghost) {
      this.toast(t('Finish a valid flying lap before sharing a ghost'), 'error');
      return;
    }
    const link = createGhostChallengeUrl(window.location.origin, ghost, this.player?.name ?? 'DRIVER');
    const daily = this.room?.settings.dailyChallengeId;
    this.shareLinkOverride = {
      link,
      title: daily ? t('NIGHTSHIFT daily challenge {daily}', { daily }) : t('NIGHTSHIFT ghost challenge'),
      text: daily
        ? (this.player?.name
          ? t('Beat {name} daily ghost for {daily}.', { name: this.player.name, daily })
          : t('Beat my daily ghost for {daily}.', { daily }))
        : (this.player?.name
          ? t('Beat {name} on {map}.', { name: this.player.name, map: this.currentRaceMap()?.name ?? t('this route') })
          : t('Beat my on {map}.', { map: this.currentRaceMap()?.name ?? t('this route') })),
      label: daily ? 'DAILY CHALLENGE' : 'GHOST CHALLENGE',
    };
    this.openShareOverlay(link);
  }

  private currentLapGhostSetup(): LapGhostSetup | null {
    if (!this.room || !this.player) return null;
    const map = this.currentRaceMap();
    const timeOfDay = this.room.settings.timeOfDay ?? map?.timeOfDay;
    const weather = this.room.settings.weather ?? map?.weather;
    if (!timeOfDay || !weather) return null;
    return {
      carId: this.player.carId,
      crewModule: this.player.crewModule ?? this.driverProfile.crewModule,
      timeOfDay,
      weather,
      dailyChallengeId: this.room.settings.dailyChallengeId,
    };
  }

  private openShareOverlay(link: string): void {
    const overlay = this.root.querySelector<HTMLElement>('#invite-share-overlay');
    if (!overlay) return;

    this.inviteShareOpen = true;
    this.inviteShareReturnFocus = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : this.root.querySelector<HTMLButtonElement>('#copy-invite');
    overlay.inert = false;
    overlay.classList.remove('hidden');
    overlay.setAttribute('aria-hidden', 'false');
    mustElement('#race-stage').classList.add('invite-share-open');

    const ghostShare = this.shareLinkOverride !== null;
    const dailyShare = this.shareLinkOverride?.label === 'DAILY CHALLENGE';
    setText(mustElement('#invite-share-eyebrow'), ghostShare ? t(this.shareLinkOverride?.label ?? 'GHOST CHALLENGE') : t('PRIVATE GRID ACCESS'));
    setText(mustElement('#invite-share-title'), dailyShare ? t('BEAT TODAY’S GHOST.') : ghostShare ? t('BEAT MY GHOST.') : t('BRING YOUR CREW.'));
    setText(mustElement('#invite-share-description'), ghostShare
      ? dailyShare
        ? t('Scan or send today’s seeded run. The QR contains the car, track and complete non-colliding ghost.')
        : t('Scan or send this compact challenge. It includes the car, track and complete non-colliding racing line.')
      : t('Scan the QR code or send the private link. Anyone who has it can claim an open grid slot before launch.'));
    setText(mustElement('#invite-share-kicker'), dailyShare ? t('DAILY SEEDED TIME ATTACK') : ghostShare ? t('TIME TRIAL CHALLENGE') : t('ONE LINK // ONE GRID'));
    setText(mustElement('#invite-share-label'), ghostShare ? t('CHALLENGE LINK') : t('PRIVATE INVITE LINK'));
    mustElement('#invite-share-heading').innerHTML = ghostShare
      ? t('CHASE THE<br>RACING LINE.')
      : t('JOIN THE<br>NIGHT SHIFT.');

    const linkInput = mustElement<HTMLInputElement>('#invite-share-link');
    linkInput.value = link;
    const nativeShare = mustElement<HTMLButtonElement>('#invite-native-share');
    nativeShare.hidden = typeof navigator.share !== 'function';
    const qr = mustElement<HTMLElement>('#invite-qr');
    qr.setAttribute('aria-label', ghostShare
      ? dailyShare
        ? t('QR code containing the daily ghost challenge link')
        : t('QR code containing the Time Trial ghost challenge link')
      : t('QR code containing the private race invite link'));
    qr.dataset.status = 'loading';
    qr.innerHTML = `<span class="invite-qr-loader" aria-hidden="true"></span><small>${t('GENERATING SECURE QR…')}</small>`;
    window.setTimeout(() => mustElement<HTMLButtonElement>('#invite-share-close').focus(), 0);

    const renderId = ++this.inviteShareRenderId;
    this.inviteQrAbort?.abort();
    const qrAbort = new AbortController();
    this.inviteQrAbort = qrAbort;
    void import('./invite-qr.js')
      .then(({ createInviteQrSvgAsync }) => createInviteQrSvgAsync(link, { signal: qrAbort.signal }))
      .then((svg) => {
        if (!this.inviteShareOpen || renderId !== this.inviteShareRenderId) return;
        qr.innerHTML = svg;
        qr.dataset.status = 'ready';
      })
      .catch(() => {
        if (!this.inviteShareOpen || renderId !== this.inviteShareRenderId) return;
        qr.dataset.status = 'error';
        qr.innerHTML = `<b>${t('QR UNAVAILABLE')}</b><small>${t('Copy or share the link instead.')}</small>`;
        this.toast(t('QR code could not be generated'), 'error');
      })
      .finally(() => {
        if (this.inviteQrAbort === qrAbort) this.inviteQrAbort = null;
      });
  }

  private closeInviteShare(restoreFocus = true): void {
    const overlay = this.root.querySelector<HTMLElement>('#invite-share-overlay');
    this.inviteShareOpen = false;
    this.inviteShareRenderId += 1;
    this.inviteQrAbort?.abort();
    this.inviteQrAbort = null;
    if (overlay) {
      overlay.inert = true;
      overlay.classList.add('hidden');
      overlay.setAttribute('aria-hidden', 'true');
    }
    this.root.querySelector('#race-stage')?.classList.remove('invite-share-open');
    if (restoreFocus && this.inviteShareReturnFocus?.isConnected) {
      this.inviteShareReturnFocus.focus({ preventScroll: true });
    }
    this.inviteShareReturnFocus = null;
    this.shareLinkOverride = null;
  }

  private async copyInvite(): Promise<void> {
    const share = this.shareLinkOverride;
    const link = share?.link ?? this.inviteLink();
    if (!link) return;
    const copiedMessage = share ? t('Ghost challenge copied') : t('Invite link copied');
    const promptMessage = share ? t('Copy this ghost challenge link:') : t('Copy this private race link:');
    const renderId = this.inviteShareRenderId;
    try {
      await navigator.clipboard.writeText(link);
      this.toast(copiedMessage, 'info');
    } catch {
      if (!this.inviteShareOpen || renderId !== this.inviteShareRenderId) return;
      const input = this.root.querySelector<HTMLInputElement>('#invite-share-link');
      if (input) {
        input.focus();
        input.select();
        input.setSelectionRange(0, link.length);
      }
      try {
        if (document.execCommand('copy')) {
          this.toast(copiedMessage, 'info');
          return;
        }
      } catch {
        // Older browsers can expose execCommand while refusing clipboard access.
      }
      window.prompt(promptMessage, link);
    }
  }

  private async nativeShareInvite(): Promise<void> {
    const link = this.shareLinkOverride?.link ?? this.inviteLink();
    if (!link || typeof navigator.share !== 'function') return;
    try {
      await navigator.share({
        title: this.shareLinkOverride?.title ?? t('NIGHTSHIFT private race'),
        text: this.shareLinkOverride?.text ?? t('Take a grid slot in my NIGHTSHIFT race.'),
        url: link,
      });
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        this.toast(t('Could not open the system share menu'), 'error');
      }
    }
  }

  private startSpectating(
    room = this.pendingInvite?.room ?? this.room,
    inviteToken = this.pendingInvite?.inviteToken ?? this.inviteToken,
  ): void {
    if (!room || (!inviteToken && room.settings.visibility !== 'public')) return;
    const roomId = room.id;
    this.destroyRuntime();
    this.room = room;
    this.player = null;
    this.inviteToken = inviteToken;
    this.pendingInvite = null;
    this.raceRuntime.begin(roomId, 'spectator', room.raceAttempt ?? 1);
    const spectatorMap = this.maps.find((map) => map.id === room.settings.mapId) ?? null;
    this.spectator.begin(room, spectatorMap);
    this.root.innerHTML = spectatorShell(spectatorMap, room);
    this.connectionState = 'connecting';
    this.latencyMs = null;
    this.renderConnectionStatus();
    mustElement<HTMLButtonElement>('#spectator-home').addEventListener('click', () => this.goHome());
    for (const id of ['#spectator-return', '#spectator-join']) {
      mustElement<HTMLButtonElement>(id).addEventListener('click', () => {
        this.pendingInvite = { roomId, inviteToken, room: this.spectator.state.room ?? room, error: '' };
        this.renderMenu(this.entryStep());
      });
    }
    this.connection = new SpectatorConnection({
      roomId,
      inviteToken,
      onMessage: (message) => this.onSpectatorMessage(message),
      onStatus: (status) => this.setConnectionStatus(status),
      onLatency: (latencyMs) => {
        // Smooth the 2 s probe so the HUD reads a steady figure, not noise.
        this.latencyMs = this.latencyMs === null ? latencyMs : this.latencyMs * 0.55 + latencyMs * 0.45;
        this.renderConnectionStatus();
      },
      onTerminalClose: (_code, reason) => this.renderInviteUnavailable(reason ? t(reason) : t('This live session is no longer available.')),
      onProtocolMismatch: () => {
        this.renderUpdating();
        void this.pwa.recoverProtocolMismatch();
      },
      onClientError: (error) => {
        this.renderFatal(t('The spectator view could not process live data: {reason}', { reason: error.message }));
      },
      diagnostics: this.diagnostics,
    });
  }

  private onSpectatorMessage(message: ServerMessage): void {
    if (message.type === 'spectator-welcome') {
      this.raceRuntime.lifecycle.observeAuthoritative(
        message.room.phase,
        message.room.raceAttempt ?? 1,
      );
    } else if (message.type === 'room-state') {
      this.raceRuntime.lifecycle.observeAuthoritative(
        message.room.phase,
        message.room.raceAttempt ?? this.raceAttempt,
      );
    } else if (message.type === 'snapshot') {
      this.raceRuntime.lifecycle.observeAuthoritative(message.phase, this.raceAttempt);
    }
    const actions = this.spectator.reduce(message, Date.now());
    const state = this.spectator.state;
    this.room = state.room;
    this.latestSnapshot = state.snapshot;
    for (const action of actions) {
      if (action.type === 'render') {
        this.spectatorUiScheduler.request(action.immediate);
      } else if (action.type === 'toast') {
        this.toast(t(action.message), action.tone);
      } else if (action.type === 'error') {
        this.toast(t(action.message), 'error');
      } else {
        const nextInviteToken = action.inviteToken || this.inviteToken;
        if (!nextInviteToken) {
          this.toast(t('The party moved, but this spectator link has no reusable invite'), 'error');
          continue;
        }
        this.room = action.room;
        this.inviteToken = nextInviteToken;
        this.latestSnapshot = null;
        this.routes.replaceRace(action.room.id, nextInviteToken);
        // Reconnect immediately with the authoritative successor route instead
        // of waiting for the predecessor socket's exponential reconnect path.
        this.startSpectating();
        this.toast(t('Pit wall moved to the next grid'), 'info');
      }
    }
  }

  private renderSpectatorUi(): void {
    const { room, map, snapshot } = this.spectator.state;
    if (!room || !map || !this.root.querySelector('#spectator-stage')) return;
    setText(mustElement('#spectator-map'), map.name);
    setText(mustElement('#spectator-location'), t(map.location));
    const phase = snapshot?.phase ?? room.phase;
    const countdown = snapshot?.countdownMs ?? 0;
    const laps = raceLapCount(room.settings, map.laps);
    const phaseLabel = phase === 'lobby' ? t('GRID FORMING')
      : phase === 'countdown' ? t('START IN {seconds}', { seconds: Math.max(1, Math.ceil(countdown / 1_000)) })
        : phase === 'racing' ? t('RACE LIVE') : t('FINAL CLASSIFICATION');
    const phaseElement = mustElement('#spectator-phase');
    phaseElement.dataset.phase = phase;
    setText(phaseElement, phaseLabel);
    setText(mustElement('#spectator-meta'), `${t('{n} DRIVERS', { n: room.playerCount })} · ${t('{n} WATCHING', { n: room.spectatorCount ?? 1 })} · ${tn(laps, '{n} LAP', '{n} LAPS')}`);
    const join = mustElement<HTMLButtonElement>('#spectator-join');
    join.hidden = phase !== 'lobby' || room.playerCount >= room.settings.maxPlayers;
    const racers = snapshot?.racers ?? [];
    const orderedRacers = racers.filter((racer) => !racer.pursuit).sort((a, b) => a.place - b.place);
    const leaderboardSignature = orderedRacers.map((racer) => (
      `${racer.id}:${racer.place}:${racer.lap}:${Math.round(racer.speed * 3.6)}:${Math.round(racer.health)}:${racer.carId}:${racer.tuning.paint}`
    )).join('|');
    if (leaderboardSignature !== this.lastSpectatorLeaderboardSignature) {
      this.lastSpectatorLeaderboardSignature = leaderboardSignature;
      mustElement<HTMLOListElement>('#spectator-leaderboard').innerHTML = orderedRacers
        .map((racer) => `
          <li><b>${String(racer.place).padStart(2, '0')}</b><i style="--driver-colour:${safeColour(racer.tuning.paint)}"></i><span>${escapeHtml(racer.name)}<small>${escapeHtml(CAR_BY_ID.get(racer.carId)?.name ?? racer.carId)} · ${t('LAP {lap}/{total}', { lap: racer.lap, total: laps })}</small></span><strong>${Math.max(0, Math.round(racer.speed * 3.6))}<small>KM/H</small></strong><em style="--health:${Math.max(0, racer.health)}%"></em></li>`)
        .join('');
    }
    const canvas = mustElement<HTMLCanvasElement>('#spectator-minimap');
    if (racers.length > 0) drawMinimap(canvas, map, racers, '');
  }

  /** Camera buttons and the remembered start view follow every camera change. */
  private syncCameraMode(mode: CameraModeLabel): void {
    const camera = this.root.querySelector<HTMLButtonElement>('#mobile-camera');
    if (camera) {
      const modeLabel = camera.querySelector<HTMLElement>('[data-camera-mode-label]');
      if (modeLabel) modeLabel.textContent = tc('camera', mode);
      camera.dataset.cameraMode = mode.toLowerCase();
      camera.title = t('Current camera: {mode}', { mode: tc('camera', mode.toLowerCase()) });
      camera.setAttribute('aria-label', t('Change camera. Current view: {mode}', { mode: tc('camera', mode.toLowerCase()) }));
    }
    const cockpit = mode === 'COCKPIT';
    const cockpitButton = this.root.querySelector<HTMLButtonElement>('#mobile-cockpit');
    cockpitButton?.setAttribute('aria-pressed', String(cockpit));
    // The dashboard carries speed, gear, nitro and integrity in cockpit view.
    this.root.querySelector('#race-hud')?.classList.toggle('cockpit-camera', cockpit);
    if (this.gameSettings.cockpitView !== cockpit) {
      this.gameSettings.cockpitView = cockpit;
      saveGameSettings(this.gameSettings);
    }
  }

  private toast(message: string, type: 'info' | 'error', options: ToastOptions = {}): void {
    this.notificationFeed.unshift({ text: message, type, at: Date.now() });
    if (this.notificationFeed.length > 20) this.notificationFeed.length = 20;
    this.renderNotificationFeed();
    const stack = this.root.querySelector('#toast-stack');
    if (!stack) return;
    const phase = this.latestSnapshot?.phase;
    const racing = Boolean(this.root.querySelector('#race-stage'))
      && (phase === 'racing' || phase === 'countdown')
      && !this.resultsVisible();
    const touch = this.deviceCapabilities.touchDevice;
    const decision = toastDecision(type, touch, racing, options);
    if (!decision.show) return;
    if (touch && !this.toastRepeatGate.allow(message, performance.now())) return;
    if (decision.replace) stack.replaceChildren();
    const toast = document.createElement('div');
    toast.className = `toast ${type}`;
    toast.textContent = message;
    stack.append(toast);
    const dismiss = (): void => {
      if (!toast.isConnected || toast.classList.contains('leaving')) return;
      toast.classList.add('leaving');
      window.setTimeout(() => toast.remove(), 220);
    };
    // On glass a pill that covers something can be flicked away with a tap.
    if (touch) toast.addEventListener('pointerdown', dismiss, { once: true });
    window.setTimeout(dismiss, decision.durationMs);
  }

  private renderNotificationFeed(): void {
    const list = this.root.querySelector<HTMLElement>('#notif-list');
    if (!list) return;
    const count = this.root.querySelector<HTMLElement>('#notif-count');
    if (count) {
      count.hidden = this.notificationFeed.length === 0;
      count.textContent = String(this.notificationFeed.length);
    }
    if (this.notificationFeed.length === 0) {
      list.innerHTML = `<p class="notif-empty">${t('No race control messages yet.')}</p>`;
      return;
    }
    list.innerHTML = this.notificationFeed.map((entry) => `
      <p class="notif-entry" data-type="${entry.type}"><time>${new Date(entry.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</time><span>${escapeHtml(entry.text)}</span></p>`).join('');
  }

  private renderFatal(message: string): void {
    this.diagnostics.record('runtime', 'fatal-screen', { message });
    this.destroyRuntime();
    this.root.innerHTML = `
      <main class="fatal-screen"><div class="fatal-code">404 //</div><p class="eyebrow">${t('RACE CONTROL')}</p><h1>${t('SESSION<br>UNAVAILABLE')}</h1><p>${escapeHtml(message)}</p><button id="fatal-home">${t('RETURN TO GARAGE')}</button></main>`;
    mustElement<HTMLButtonElement>('#fatal-home').addEventListener('click', () => this.goHome());
  }

  private renderTakenOver(): void {
    // The saved race pass deliberately stays in local storage: the newer tab
    // races on it now, and a reload here is the deliberate way to take it back.
    this.destroyRuntime();
    this.root.innerHTML = `
      <main class="fatal-screen taken-over-screen"><div class="fatal-code">${t('CONTROL MOVED //')}</div><p class="eyebrow">${t('RACE CONTROL')}</p><h1>${t('ANOTHER TAB<br>TOOK OVER')}</h1><p>${t('This race was opened in a newer tab or window, which now owns the controls. This tab has been disconnected so both cannot steer the same car.')}</p><button id="taken-over-reload">${t('TAKE OVER HERE ↻')}</button><button id="fatal-home">${t('RETURN TO GARAGE')}</button></main>`;
    mustElement<HTMLButtonElement>('#taken-over-reload').addEventListener('click', () => window.location.reload());
    mustElement<HTMLButtonElement>('#fatal-home').addEventListener('click', () => this.goHome());
  }

  private renderInviteUnavailable(message: string): void {
    this.destroyRuntime();
    this.root.innerHTML = `
      <main class="fatal-screen invite-unavailable-screen"><div class="fatal-code">${t('GRID CLOSED //')}</div><p class="eyebrow">${t('PRIVATE INVITATION')}</p><h1>${t('SESSION<br>UNAVAILABLE')}</h1><p>${escapeHtml(message)}</p><small>${t('The link may have expired, filled up, or belonged to an older server build.')}</small><button id="fatal-home">${t('RETURN TO GARAGE')}</button></main>`;
    mustElement<HTMLButtonElement>('#fatal-home').addEventListener('click', () => this.goHome());
  }

  private renderUpdating(): void {
    this.destroyRuntime();
    this.root.innerHTML = `
      <main class="fatal-screen update-screen"><div class="fatal-code">${t('UPDATE //')}</div><p class="eyebrow">${t('RACE CONTROL HANDSHAKE')}</p><h1>${t('GETTING<br>NEW BUILD')}</h1><p>${t('Your saved driver and race pass stay on this device. NIGHTSHIFT is activating the compatible client now…')}</p><div class="loader"><i></i></div></main>`;
  }

  private renderOffline(): void {
    this.destroyRuntime();
    this.root.innerHTML = `
      <main class="fatal-screen offline-screen">
        <div class="fatal-code">${t('OFFLINE //')}</div>
        <p class="eyebrow">${t('LOCAL GARAGE SAFE')}</p>
        <h1>${t('RADIO<br>SILENCE')}</h1>
        <p>${t('Your driver profile, settings and street-career progress are safe on this device. Reconnect to reach race control and get back on the grid.')}</p>
        <button id="offline-retry">${t('RETRY CONNECTION ↻')}</button>
      </main>`;
    mustElement<HTMLButtonElement>('#offline-retry').addEventListener('click', () => window.location.reload());
    window.addEventListener('online', this.onOfflineReconnect, { once: true });
  }

  private goHome(updateHistory = true): void {
    this.destroyRuntime();
    if (updateHistory) this.routes.pushHome();
    if (this.benchmarkOriginalSettings) this.gameSettings = this.benchmarkOriginalSettings;
    this.benchmarkOriginalSettings = null;
    this.benchmarkRequest = null;
    this.benchmarkRecorder = null;
    this.benchmarkAutoStarted = false;
    this.room = null;
    this.inviteToken = '';
    this.player = null;
    this.lastPhase = '';
    this.restoreDriverProfile();
    this.reconcileImportedGhostChallenge();
    this.renderMenu(this.entryStep());
  }

  private readonly onHistoryNavigation = (route: RaceRoute | null): void => {
    this.historyNavigationGeneration += 1;
    if (!route) {
      if (
        this.garageMetadataReady
        || this.room
        || this.player
        || this.connection
        || this.game
        || this.localRace
      ) this.goHome(false);
      return;
    }
    if (this.room?.id === route.roomId && (!route.inviteToken || route.inviteToken === this.inviteToken)) return;
    // A route transition can require a different saved identity, room payload,
    // and transport. Reloading is the only atomic way to enter that state.
    window.location.reload();
  };

  private detachRaceUiListeners(): void {
    window.removeEventListener('keydown', this.onSettingsKey);
    window.removeEventListener('keydown', this.onQuickRestartKey);
    window.removeEventListener('resize', this.onViewportModeChange);
    document.removeEventListener('fullscreenchange', this.onViewportModeChange);
    screen.orientation?.removeEventListener('change', this.onViewportModeChange);
    if (this.tiltSetupTimer !== undefined) window.clearInterval(this.tiltSetupTimer);
    this.tiltSetupTimer = undefined;
  }

  private stopRaceEngine(): void {
    this.game = null;
    this.preparationWatchGame = null;
    if (this.inputTimer !== undefined) window.clearInterval(this.inputTimer);
    this.inputTimer = undefined;
    if (this.goTimer !== undefined) this.raceRuntime.scope?.cancelTimeout(this.goTimer);
    this.goTimer = undefined;
    this.resultsCareer.reset();
    this.clearRaceDirectorCue();
  }

  private destroyRuntime(): void {
    this.runtimeGeneration += 1;
    this.garageActions.invalidate();
    this.closeInviteShare(false);
    this.stopInvitePolling();
    document.documentElement.classList.remove('race-mounted');
    document.body.classList.remove('race-mounted');
    this.garageGeneration += 1;
    this.pwa.setRaceActive(false);
    this.detachRaceUiListeners();
    window.removeEventListener('online', this.onConnectivityChange);
    window.removeEventListener('offline', this.onConnectivityChange);
    window.removeEventListener('online', this.onOfflineReconnect);
    this.stopLobbyBrowser();
    this.disposeMenuListeners();
    this.lobbyPanel?.destroy();
    this.lobbyPanel = null;
    this.lobbyGarageOpen = false;
    this.lobbyRacers = [];
    this.engineSignature = '';
    if (this.engineRemountTimer !== undefined) window.clearTimeout(this.engineRemountTimer);
    this.engineRemountTimer = undefined;
    this.returningToLobby = false;
    this.settingsOpen = false;
    this.motionStatus = 'off';
    this.gamepadStatus = { connected: false, index: null, name: '', standardMapping: false };
    this.lastGamepadMappingSignature = '';
    this.controlsPanel?.cancelCapture();
    this.controlsPanel = null;
    this.raceUiBound = false;
    this.raceDirector = null;
    this.activeRaceMap = null;
    this.raceEngineReady = false;
    this.playerReady = false;
    this.spectatorUiScheduler.cancel();
    this.spectator.reset();
    this.lastSpectatorLeaderboardSignature = '';
    this.raceUiElements = null;
    this.lastTelemetryUiAt = 0;
    this.lastMinimapUiAt = 0;
    this.lastLeaderboardSignature = '';
    this.lastGhostUiAt = 0;
    this.lastGhostSectorsSignature = '';
    // Drop the finished race's snapshot so a following spectator view never
    // renders the previous session's classification onto the new room.
    this.latestSnapshot = null;
    this.garage?.destroy();
    this.garage = null;
    this.raceRuntime.dispose();
    this.connectionRoomId = '';
    this.stopRaceEngine();
  }
}

function waitForGaragePreviewOpportunity(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => resolve(), { timeout: 750 });
      } else {
        window.setTimeout(resolve, 0);
      }
    });
  });
}

function resumeStateFromRacer(racer: RacerSnapshot, inputSequence: number): ResumeState {
  return {
    x: racer.x,
    y: racer.y,
    groundY: racer.groundY ?? racer.y,
    z: racer.z,
    yaw: racer.yaw,
    travelYaw: racer.travelYaw,
    turretYaw: racer.turretYaw,
    velocityX: racer.velocityX,
    velocityZ: racer.velocityZ,
    verticalSpeed: racer.verticalSpeed,
    steer: racer.steer,
    drift: racer.drift,
    engineRpm: racer.engineRpm,
    gear: racer.gear,
    wheelSlip: racer.wheelSlip,
    longitudinalAcceleration: racer.longitudinalAcceleration,
    lateralAcceleration: racer.lateralAcceleration,
    chassisPitch: racer.chassisPitch,
    chassisRoll: racer.chassisRoll,
    pitchVelocity: racer.pitchVelocity ?? 0,
    rollVelocity: racer.rollVelocity ?? 0,
    angularVelocity: racer.angularVelocity ?? 0,
    trackSegment: racer.trackSegment ?? -1,
    inputSequence,
  };
}

function formatSensor(value: number | null): string {
  return value === null || !Number.isFinite(value) ? '—' : value.toFixed(2);
}

function isConnectionError(error: unknown): boolean {
  if (!navigator.onLine) return true;
  if (error instanceof ApiTimeoutError) return true;
  return error instanceof TypeError && /failed to fetch|network error|load failed/i.test(error.message);
}

function motionPermissionRequiresGesture(): boolean {
  const sensorWindow = window as Window & {
    DeviceOrientationEvent?: typeof DeviceOrientationEvent & {
      requestPermission?: () => Promise<'granted' | 'denied'>;
    };
    DeviceMotionEvent?: typeof DeviceMotionEvent & {
      requestPermission?: () => Promise<'granted' | 'denied'>;
    };
  };
  return typeof sensorWindow.DeviceOrientationEvent?.requestPermission === 'function'
    || typeof sensorWindow.DeviceMotionEvent?.requestPermission === 'function';
}

function captureRaceUiElements(): RaceUiElements {
  return {
    raceStage: mustElement('#race-stage'),
    speedValue: mustElement('#speed-value'),
    powertrain: mustElement('#hud-powertrain'),
    gearValue: mustElement('#gear-value'),
    rpmValue: mustElement('#rpm-value'),
    rpmFill: mustElement('#rpm-fill'),
    cluster: document.querySelector<HTMLElement>('#hud-cluster'),
    tachoFill: document.querySelector<SVGElement>('#tacho-fill'),
    tachoNitro: document.querySelector<SVGElement>('#tacho-nitro'),
    integrity: mustElement('#hud-integrity'),
    integrityValue: mustElement('#integrity-value'),
    integrityFill: mustElement('#integrity-fill'),
    integrityLoss: mustElement('#integrity-loss'),
    integrityHit: mustElement('#integrity-hit'),
    damageMap: mustElement<SVGSVGElement>('#damage-map'),
    damageVignette: mustElement('#damage-vignette'),
    nitroFill: mustElement('#nitro-fill'),
    nitroValue: mustElement('#nitro-value'),
    draftHud: mustElement('#hud-draft'),
    draftFill: mustElement('#draft-fill'),
    draftValue: mustElement('#draft-value'),
    launchHud: mustElement('#hud-launch'),
    launchNeedle: mustElement('#launch-needle'),
    launchValue: mustElement('#launch-value'),
    nitroButton: document.querySelector<HTMLElement>('#nitro-button'),
    driftButton: document.querySelector<HTMLElement>('[data-drive="handbrake"]'),
    styleHud: mustElement('#hud-style'),
    styleScore: mustElement('#style-score'),
    styleAction: mustElement('#style-action'),
    styleMultiplier: mustElement('#style-multiplier'),
    styleComboFill: mustElement('#style-combo-fill'),
    styleCallout: mustElement('#style-callout'),
    directorCallout: mustElement('#race-director-callout'),
    gripHud: mustElement('#hud-grip'),
    gripFill: mustElement('#grip-fill'),
    gripSteer: mustElement('#grip-steer'),
    gripValue: mustElement('#grip-value'),
    lapValue: mustElement('#lap-value'),
    placeValue: mustElement('#place-value'),
    positionTotal: mustElement('#race-position-total'),
    eventMode: mustElement('#hud-event-mode'),
    leaderboard: mustElement<HTMLOListElement>('#race-leaderboard'),
    minimap: mustElement<HTMLCanvasElement>('#minimap'),
    lobby: mustElement('#lobby-overlay'),
    hud: mustElement('#race-hud'),
    countdown: mustElement('#countdown'),
    results: mustElement('#results-overlay'),
    ghostHud: mustElement('#hud-ghost'),
    ghostBest: mustElement('#ghost-best'),
    ghostDelta: mustElement('#ghost-delta'),
    ghostSectors: mustElement('#ghost-sectors'),
    ghostMedal: mustElement('#ghost-medal'),
    liveChallengeHud: mustElement('#hud-live-challenge'),
    liveChallengeTitle: mustElement('#live-challenge-title'),
    liveChallengeDetail: mustElement('#live-challenge-detail'),
    liveChallengeFill: mustElement('#live-challenge-fill'),
    liveChallengeTime: mustElement('#live-challenge-time'),
    sectorHud: mustElement('#hud-sector-duel'),
    sectorDelta: mustElement('#sector-delta'),
    sectorCells: [
      mustElement('#sector-cell-1'),
      mustElement('#sector-cell-2'),
      mustElement('#sector-cell-3'),
    ],
    comebackHud: mustElement('#hud-comeback'),
    comebackFill: mustElement('#comeback-fill'),
    comebackValue: mustElement('#comeback-value'),
    heatHud: mustElement('#hud-heat'),
    heatLevel: mustElement('#heat-level'),
    heatUnits: mustElement('#heat-units'),
  };
}

function setText(element: HTMLElement, value: string): void {
  if (element.textContent !== value) element.textContent = value;
}

function setStyle(element: HTMLElement | SVGElement, property: string, value: string): void {
  if (element.style.getPropertyValue(property) !== value) element.style.setProperty(property, value);
}

interface MinimapCache {
  mapId: string;
  width: number;
  height: number;
  minX: number;
  minZ: number;
  centerX: number;
  centerZ: number;
  scale: number;
  background: HTMLCanvasElement;
}

const MINIMAP_CACHE = new WeakMap<HTMLCanvasElement, MinimapCache>();

function drawMinimap(canvas: HTMLCanvasElement, map: MapDefinition, racers: RacerSnapshot[], playerId: string): void {
  const context = canvas.getContext('2d');
  if (!context) return;
  const width = canvas.width;
  const height = canvas.height;
  let cache = MINIMAP_CACHE.get(canvas);
  if (!cache || cache.mapId !== map.id || cache.width !== width || cache.height !== height) {
    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let minZ = Number.POSITIVE_INFINITY;
    let maxZ = Number.NEGATIVE_INFINITY;
    const layoutPoints = [
      ...map.track,
      ...(map.features.alternateRoutes ?? []).flatMap((route) => route.points),
      ...(map.features.lakes ?? []).flatMap((lake) => [
        { x: lake.x - lake.radiusX, z: lake.z - lake.radiusZ },
        { x: lake.x + lake.radiusX, z: lake.z + lake.radiusZ },
      ]),
    ];
    for (const point of layoutPoints) {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minZ = Math.min(minZ, point.z);
      maxZ = Math.max(maxZ, point.z);
    }
    const scale = Math.min((width - 38) / (maxX - minX || 1), (height - 38) / (maxZ - minZ || 1));
    const centerX = (minX + maxX) * 0.5;
    const centerZ = (minZ + maxZ) * 0.5;
    const background = document.createElement('canvas');
    background.width = width;
    background.height = height;
    const backgroundContext = background.getContext('2d');
    if (!backgroundContext) return;
    backgroundContext.lineCap = 'round';
    backgroundContext.lineJoin = 'round';
    const mapX = (value: number): number => width * 0.5 + (value - centerX) * scale;
    const mapZ = (value: number): number => height * 0.5 + (value - centerZ) * scale;

    for (const lake of map.features.lakes ?? []) {
      backgroundContext.beginPath();
      backgroundContext.ellipse(
        mapX(lake.x),
        mapZ(lake.z),
        lake.radiusX * scale,
        lake.radiusZ * scale,
        lake.rotation ?? 0,
        0,
        Math.PI * 2,
      );
      backgroundContext.fillStyle = `${lake.colour ?? '#287a92'}66`;
      backgroundContext.fill();
      backgroundContext.strokeStyle = `${map.palette.accent}88`;
      backgroundContext.lineWidth = 1;
      backgroundContext.stroke();
    }

    const geometry = getTrackGeometry(map);
    const mainSamples = 240;
    backgroundContext.beginPath();
    for (let index = 0; index <= mainSamples; index += 1) {
      const point = sampleBakedRoad(geometry.main, index / mainSamples);
      const x = mapX(point.x);
      const y = mapZ(point.z);
      if (index === 0) backgroundContext.moveTo(x, y); else backgroundContext.lineTo(x, y);
    }
    backgroundContext.closePath();
    backgroundContext.strokeStyle = 'rgba(5,10,15,.78)';
    backgroundContext.lineWidth = 8;
    backgroundContext.stroke();

    for (let index = 0; index < mainSamples; index += 1) {
      const fromProgress = index / mainSamples;
      const toProgress = (index + 1) / mainSamples;
      const from = sampleBakedRoad(geometry.main, fromProgress);
      const to = sampleBakedRoad(geometry.main, toProgress);
      const section = roadSectionAtProgress(map, (fromProgress + toProgress) * 0.5);
      const profile = roadVisualProfile(section?.style, section?.accent ?? map.palette.accent);
      backgroundContext.beginPath();
      backgroundContext.moveTo(mapX(from.x), mapZ(from.z));
      backgroundContext.lineTo(mapX(to.x), mapZ(to.z));
      backgroundContext.strokeStyle = profile.edgeColour;
      backgroundContext.lineWidth = 2.25;
      backgroundContext.stroke();
    }

    backgroundContext.setLineDash([5, 4]);
    for (const route of map.features.alternateRoutes ?? []) {
      const road = geometry.alternateRoutes.get(route.id);
      if (!road) continue;
      backgroundContext.beginPath();
      const samples = Math.max(18, Math.round(road.length / 7));
      for (let index = 0; index <= samples; index += 1) {
        const point = sampleBakedRoad(road, index / samples);
        if (index === 0) backgroundContext.moveTo(mapX(point.x), mapZ(point.z));
        else backgroundContext.lineTo(mapX(point.x), mapZ(point.z));
      }
      backgroundContext.strokeStyle = route.risk === 'extreme'
        ? '#ff704d'
        : route.risk === 'technical' ? '#ffd45d' : '#75edff';
      backgroundContext.lineWidth = 2;
      backgroundContext.stroke();
    }
    backgroundContext.setLineDash([]);

    for (const landmark of map.features.landmarks ?? []) {
      const point = trackPoseAtIndex(map, landmark.trackIndex);
      const x = mapX(point.x);
      const y = mapZ(point.z);
      backgroundContext.save();
      backgroundContext.translate(x, y);
      backgroundContext.rotate(Math.PI * 0.25);
      backgroundContext.fillStyle = landmark.colour ?? map.palette.accent;
      backgroundContext.shadowColor = backgroundContext.fillStyle;
      backgroundContext.shadowBlur = 5;
      backgroundContext.fillRect(-2.7, -2.7, 5.4, 5.4);
      backgroundContext.restore();
    }
    cache = { mapId: map.id, width, height, minX, minZ, centerX, centerZ, scale, background };
    MINIMAP_CACHE.set(canvas, cache);
  }
  context.clearRect(0, 0, width, height);
  context.drawImage(cache.background, 0, 0);
  for (const racer of racers) {
    if (racer.pursuit && !racer.pursuitActive) continue;
    const x = width * 0.5 + (racer.x - cache.centerX) * cache.scale;
    const y = height * 0.5 + (racer.z - cache.centerZ) * cache.scale;
    context.beginPath();
    context.arc(x, y, racer.id === playerId ? 5 : 3, 0, Math.PI * 2);
    context.fillStyle = racer.id === playerId
      ? '#ffffff'
      : racer.pursuit ? (Math.floor(performance.now() / 180) % 2 === 0 ? '#ff3652' : '#2979ff')
        : safeColour(racer.tuning.paint);
    context.shadowColor = context.fillStyle;
    context.shadowBlur = racer.id === playerId ? 9 : 3;
    context.fill();
  }
  context.shadowBlur = 0;
}

const LAUNCH_TIPS = [
  'Tap the handbrake to flick into a drift, then feather the throttle to hold it.',
  'Tuck in behind a rival: the slipstream builds your top speed.',
  'Stuck against a wall? Reset puts you back on the racing line.',
  'Lift before the corner, not in it: the coach callouts tell you when.',
  'Alternative routes can be faster. Watch the minimap for the split.',
  'Hits wear down your integrity. A wrecked car loses precious seconds.',
  'Switch the camera at any time to find the view that suits you.',
] as const;

/** One stable tip per track and session, so the launch card never flickers. */
function launchTip(mapId: string): string {
  let hash = launchTipSeed;
  for (const character of mapId) hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  return LAUNCH_TIPS[hash % LAUNCH_TIPS.length]!;
}
const launchTipSeed = Math.floor(Math.random() * 1_000);

function settlePromise<T>(promise: Promise<T>): Promise<PromiseSettledResult<T>> {
  return promise.then(
    (value): PromiseFulfilledResult<T> => ({ status: 'fulfilled', value }),
    (reason: unknown): PromiseRejectedResult => ({ status: 'rejected', reason }),
  );
}

function mustElement<T extends Element = HTMLElement>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Expected element ${selector}`);
  return element;
}

const root = document.querySelector<HTMLElement>('#app');
if (!root) throw new Error('Application root is missing');
installDevTouchEmulation();
void new NightshiftApp(root).start();

/** A first-run callsign so a new player can race straight away; editable. */
function mustElementIn<T extends Element = HTMLElement>(scope: ParentNode, selector: string): T {
  const element = scope.querySelector<T>(selector);
  if (!element) throw new Error(`Missing UI element: ${selector}`);
  return element;
}

function engineSignatureFor(message: Extract<ServerMessage, { type: 'welcome' }>): string {
  const local = message.racers.find((racer) => racer.id === message.playerId);
  return JSON.stringify([
    message.map.id,
    message.map.timeOfDay,
    message.map.weather,
    message.room.settings.traffic === true,
    raceLapCount(message.room.settings, message.map.laps),
    lapGhostRecordingEligible(message.room.settings),
    normaliseArcadePreset(message.room.settings.arcadePreset),
    local?.carId ?? '',
    local?.tuning ?? null,
  ]);
}

function suggestedCallsign(): string {
  const names = ['NOVA', 'GHOST', 'RAVEN', 'VOLT', 'APEX', 'DRIFT', 'COMET', 'NEON', 'ROGUE', 'BLAZE'];
  const name = names[Math.floor(Math.random() * names.length)]!;
  return `${name}-${String(Math.floor(Math.random() * 90) + 10)}`;
}
